import {SavedTeamSettings} from '../../../services/payroll-workforce/workforce-team-settings.state';
import { MonthlyTopupScheduleComponent } from '../../monthly-topup-schedule/monthly-topup-schedule.component';
import {WorkforceTeamCreationState} from '../../../services/payroll-workforce/workforce-team-creation.state';
import {WorkforceBrowseState} from '../../../services/payroll-workforce/workforce-browse.state';
import { Component, ElementRef, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { UsersService } from '../../../services/users/users.service';
import {
  MonthlyAllowanceFundingMode,
  PreviewMonthlyAllowanceFundingResponseData,
  Team,
  TeamsService,
} from '../../../services/teams/teams.service';
import { SessionService } from '../../../services/session/session.service';
import { UiAlertService } from '../../../services/ui-alert/ui-alert.service';
import { SpinnerService } from '../../../services/spinner/spinner.service';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import {
  AllowanceFundingAmountDialogComponent,
  AllowanceFundingAmountDialogResult,
} from '../../modal/allowancefundingamountdialog/allowance-funding-amount-dialog.component';
import {
  EditTeamDialogComponent,
  EditTeamDialogData,
  EditTeamDialogResult,
} from '../edit-team-dialog/edit-team-dialog.component';
import {
  EditBenefitsDialogComponent,
  EditBenefitsDialogData,
  EditBenefitsDialogResult,
} from '../edit-benefits-dialog/edit-benefits-dialog.component';
import {
  EditTeamSnacksDialogComponent,
  EditTeamSnacksDialogData,
  EditTeamSnacksDialogResult,
} from '../edit-team-snacks-dialog/edit-team-snacks-dialog.component';
import {
  SendInvitesDialogComponent,
  SendInvitesDialogData,
  SendInvitesDialogResult,
} from '../send-invites-dialog/send-invites-dialog.component';
import {
  EmployeeDetailsDialogComponent,
  EmployeeDetailsDialogData,
  EmployeeDetailsDialogResult,
} from '../employee-details-dialog/employee-details-dialog.component';
import {
  PayrollCensusService,
  PayrollCensusSession,
} from '../../../services/payroll-census/payroll-census.service';
import {
  PayrollConnectionHealth,
  PayrollProviderCapabilities,
  PayrollProviderConnectionResponse,
  PayrollService,
  UKG_READY_PROVIDER_SLUG,
} from '../../../services/payroll/payroll.service';
import { ManagedFeatureFlagsService } from '../../../services/managed-feature-flags/managed-feature-flags.service';
import { Subscription } from 'rxjs';
import { TeamprogramsService } from '../../../services/teamprograms/teamprograms.service';
import { PayrollBenefitsAssignmentService } from '../../../services/payroll-benefits-assignment/payroll-benefits-assignment.service';
import {
  PayrollSnackRewardItem,
  PayrollSnacksAssignmentService,
} from '../../../services/payroll-snacks-assignment/payroll-snacks-assignment.service';
import { SnackBarComponent } from '../../snackbar/snackbar.component';
import { TransactionsService } from '../../../services/transactions/transactions.service';
import { Plan, StripeService } from '../../../services/stripe/stripe.service';
import {
  CompanyGroupProductOnboardingRow,
  TeamCompaniesService,
} from '../../../services/teamcompanies/teamcompanies.service';
import {
  PAYROLL_ADMIN_COMPANY_SNACK_REWARD_SELECTION_FLAG,
  PAYROLL_ADMIN_UKG_DEDUCTION_CANCELLATION_WRITES_FLAG,
  PAYROLL_ADMIN_UKG_EMPLOYEE_TERMINATION_PROCESSING_FLAG,
  PAYROLL_ADMIN_UKG_EMPLOYEE_TERMINATIONS_FLAG,
} from '../payroll-admin-feature-flags';
import { Sort } from '@angular/material/sort';
import { ActiveEmployerCompanyService } from '../../../services/active-employer-company/active-employer-company.service';
import { PayrollAccessService } from '../../../services/payroll-access/payroll-access.service';
import { NativeTeamContextService } from '../../../services/native-team-context/native-team-context.service';
import { TeamMemberRemovalCoordinator } from '../../../services/team-member-removal/team-member-removal-coordinator.service';
import {
  PayrollEmployeeTerminationCase,
  PayrollEmployeeTerminationListResponse,
  PayrollEmployeeTerminationsService,
  PayrollTerminationLifecycleStatus,
  PayrollTerminationProcessingReadiness,
} from '../../../services/payroll-employee-terminations/payroll-employee-terminations.service';
import {
  TerminationReviewDialogComponent,
  TerminationReviewDialogData,
  TerminationReviewDialogResult,
  TerminationReviewEmployeeIdentity,
} from '../termination-review-dialog/termination-review-dialog.component';

const PAYROLL_PROVIDER_DISPLAY_NAMES: Record<string, string> = {
  paycor: 'Paycor',
  ukg_ready: 'UKG Ready',
};
const AUTO_ADD_REQUIRES_FILTER_TEAM_REASON = 'Auto-add requires teams created from filters.';
const AUTO_ADD_REMOVAL_WARNING =
  'Removing this member will disable automatic member additions for this team so they are not added back. Auto-add will remain disabled even if the removal cannot be completed, and you can enable it again later.';
const AUTO_ADD_REMOVAL_CANCELLED_MESSAGE =
  'The member was not removed. Auto-add remains disabled for this team.';
const AUTO_ADD_REMOVAL_FAILED_MESSAGE =
  'The member removal could not be completed. Auto-add remains disabled for this team.';
type ActiveTerminationStatus = 'needs_review' | 'scheduled' | 'blocked' | 'processing';
type ResolvedTerminationStatus = 'completed' | 'dismissed';

const ACTIVE_TERMINATION_STATUSES: ActiveTerminationStatus[] = [
  'needs_review',
  'scheduled',
  'blocked',
  'processing',
];
const RESOLVED_TERMINATION_STATUSES: ResolvedTerminationStatus[] = ['completed', 'dismissed'];
const TERMINATION_PAGE_LIMIT = 100;

interface TerminationStatusPage {
  offset: number;
  hasMore: boolean;
}

interface GuidedTerminationRevealContext {
  companyId: string;
  caseId: string;
  requestVersion: number;
  teamId: string;
}

interface AllEmployeesLoadRequest {
  readonly canUseProviderCensus: boolean;
  readonly company: any;
  readonly contextKey: string;
  readonly generation: number;
  readonly providerConnection: PayrollProviderConnectionResponse | null;
  readonly teamsMap: Readonly<Record<string, any>>;
}

interface AutomatedBonusHistoryRow {
  sentTo: string;
  type: string;
  amount: number;
  sentOn: string | null;
}

interface RewardActivityHistoryRow {
  sentOn: string | null;
  amount: number;
  message: string;
  recipient: string;
}

interface TeamFilterChip {
  label: string;
  value: string;
  display: string;
}

@Component({
  selector: 'app-payroll-teams',
  templateUrl: './payroll-teams.component.html',
  styleUrls: ['./payroll-teams.component.scss'],
})
export class PayrollTeamsComponent implements OnInit, OnDestroy {
  @ViewChild(MonthlyTopupScheduleComponent) monthlyTopupSchedule?: MonthlyTopupScheduleComponent;
  @ViewChild('guidedTerminationBanner')
  private guidedTerminationBanner?: ElementRef<HTMLElement>;

  @ViewChildren('selectedTeamMemberRow')
  private selectedTeamMemberRows?: QueryList<ElementRef<HTMLElement>>;

  public filterValue = '';
  private currentCompany?: any;
  private payrollProviderConnection: PayrollProviderConnectionResponse | null = null;
  public isInitialCompanyCheckComplete = false;
  public hasCompany = false;
  public payrollConnectionHealth: PayrollConnectionHealth = 'none';
  public payrollConnectionUnsupportedReason = '';
  public isTeamsDataLoaded = false;
  public teamName = '';
  public teams: any[] = [];
  public groupProductOnboardingRows: CompanyGroupProductOnboardingRow[] = [];
  private payrollConfig?: any;
  public teamsMap: Record<string, any> = {};
  private teamBonusAmounts: Record<string, { welcome: number; birthday: number; anniversary: number }> = {};
  private teamRewardPaymentMethods: Record<string, any | null> = {};
  private readonly emptyTeamBonusAmounts = { welcome: 0, birthday: 0, anniversary: 0 };

  census: PayrollCensusSession;
  readonly workforce: WorkforceBrowseState;
  readonly teamCreation: WorkforceTeamCreationState;
  matchingSelection: import('../../../services/payroll-workforce/workforce-team-criteria.state').MatchingSelection | null = null;
  private pageLoadVersion = 0;
  private settingsSaveVersion = 0;
  private settingsCompanyVersion = 0;
  private acceptedTeamSettings = new Map<string, {version: number; name: string; amount: number}>();
  get canUseLegacyCensus(): boolean {
    return this.workforce.mode === 'legacy' && this.workforce.companyId === this.currentCompany?.id;
  }

  public selectedTabIndex = 0;
  public selectedTeamId = '';
  public isEmployeesPanelExpanded = false;
  public isAutoAddEligibilityLoading = false;
  public isAutoAddSaving = false;
  public isFundingTimingSaving = false;
  public isFundingMembersLoading = false;
  public isFundingCandidatesLoading = false;
  public isFundingMethodActionLoading = false;
  public isFundingTimingExpanded = true;
  public isRewardsActivityExpanded = true;
  public selectedTeamAutoAddEligible = false;
  public selectedTeamAutoAddReason = 'Apply and save filters for this team to enable auto-add.';
  public selectedTeamFundingCandidates: string[] = [];
  private selectedTeamAttachableFundingMethod: any | null = null;
  public automatedBonusHistory: AutomatedBonusHistoryRow[] = [];
  public oneTimeRewardsHistory: RewardActivityHistoryRow[] = [];
  public isRewardsActivityLoading = false;
  public rewardsActivityError = '';
  public isCompanySnackRewardSelectionEnabled = false;
  public isNativeCompanyManager = false;
  public isSelectedTeamSnacksLoading = false;
  public selectedTeamSnackRewards: PayrollSnackRewardItem[] = [];
  public selectedTeamSnackLoadError = '';
  private selectedTeamSnackRewardIdsInOrder: string[] = [];
  private selectedTeamSnackRewardsLoadedTeamId = '';
  private selectedTeamSnackRewardsLoadPromise?: Promise<void>;
  private companySnackRewardSelectionFeatureFlagRequest?: Promise<void>;

  // -------------------------
  // All Employees tab state
  // -------------------------
  public searchAllEmployees = '';
  public allEmployeesConnectionError = '';
  public allEmployeesCensusErrorCode = '';
  public allEmployeesRefreshError = '';
  public isRefreshingAllEmployeesFromUkg = false;

  // NOTE: These names/patterns intentionally match EditTeamDialogComponent via pass-throughs.
  get availableEmployees(): any[] {
    return this.census.availableEmployees;
  }
  get availableFilters(): any[] {
    return this.census.availableFilters;
  }
  get activeFilters(): any[] {
    return this.census.activeFilters;
  }
  get activeFiltersMap(): Record<string, any> {
    return this.census.activeFiltersMap;
  }
  get allEmployeesAvailableFilters(): any[] {
    return this.availableFilters;
  }
  get allEmployeesActiveFilters(): Array<{ key: string; value: string; label: string }> {
    return this.activeFilters;
  }
  get allEmployeesActiveFiltersMap(): Record<string, any> {
    return this.activeFiltersMap;
  }
  get selected(): Set<string> {
    return this.census.selected;
  }
  get selectedEmails(): Record<string, any> {
    return this.census.selectedEmails;
  }
  get isLoadingInitialData(): boolean {
    return this.census.isLoadingInitialData;
  }
  get isAllEmployeesResultsLoading(): boolean {
    return !!this.census.isLoadingEmployees
      && !this.census.isLoadingInitialData
      && !this.isRefreshingAllEmployeesFromUkg;
  }

  private allEmployeesLoaded = false;
  private allEmployeesLoadMode: 'provider' | 'fallback' | '' = '';
  private allEmployeesLoadPromise?: Promise<void>;
  private allEmployeesContextKey = '';
  private allEmployeesLoadedContextKey = '';
  private allEmployeesLoadContextKey = '';
  private allEmployeesLoadGeneration = 0;
  private isDestroyed = false;
  private selectedTeamDetailsLoadedTeamId = '';
  private selectedTeamDetailsLoadPromise?: Promise<void>;
  private selectedTeamDetailsLoadingTeamId = '';
  private selectedTeamDetailsRequestVersion = 0;
  private rewardsActivityRequestId = 0;
  private handledOpenCreateTeam = false;
  private meSubscription?: Subscription;
  private activeEmployerCompanySubscription?: Subscription;
  private loadedCompanyId = '';
  private initializedNativeTeamCompanyId = '';
  private isActiveCompanyReloadPending = false;
  private censusEmployeesIndexSource?: any[];
  private censusEmployeeByEmail = new Map<string, any>();
  private censusEmployeeByName = new Map<string, any | null>();
  private filteredEmployeesCacheSource?: any[];
  private filteredEmployeesCacheKey = '';
  private filteredEmployeesCache: any[] = [];
  public isTerminationFeatureEnabled = false;
  public isTerminationProcessingEnabled = false;
  public isTerminationDeductionCancellationWritesEnabled = false;
  public isTerminationReviewLoading = false;
  public isTerminationReviewLoadingMore = false;
  public isTerminationReviewListOpen = false;
  public terminationReviewLoadError = '';
  public terminationCases: PayrollEmployeeTerminationCase[] = [];
  public resolvedTerminationCases: PayrollEmployeeTerminationCase[] = [];
  public isResolvedHistoryOpen = false;
  public isResolvedHistoryLoading = false;
  public isResolvedHistoryLoadingMore = false;
  public resolvedHistoryLoadError = '';
  private terminationRequestVersion = 0;
  private resolvedHistoryRequestVersion = 0;
  private terminationCaseByUserId = new Map<string, PayrollEmployeeTerminationCase>();
  private terminationMembersByUserId = new Map<string, any>();
  private terminationReviewDialogRef?: MatDialogRef<TerminationReviewDialogComponent>;
  public guidedTerminationReadiness?: PayrollTerminationProcessingReadiness;
  private guidedTerminationCaseId = '';
  private guidedTerminationUserId = '';
  private guidedTerminationTeamIds = new Set<string>();
  private guidedTerminationRequestVersion = 0;
  private pendingGuidedTerminationReveal?: GuidedTerminationRevealContext;
  private guidedTerminationRevealTimer?: ReturnType<typeof setTimeout>;
  private terminationResetForUnavailableCompanyState = false;
  private terminationPages: Record<ActiveTerminationStatus, TerminationStatusPage> = {
    needs_review: {offset: 0, hasMore: false},
    scheduled: {offset: 0, hasMore: false},
    blocked: {offset: 0, hasMore: false},
    processing: {offset: 0, hasMore: false},
  };
  private resolvedTerminationPages: Record<ResolvedTerminationStatus, TerminationStatusPage> = {
    completed: {offset: 0, hasMore: false},
    dismissed: {offset: 0, hasMore: false},
  };

  private readonly defaultAllEmployeesColumns: string[] = [
    'select',
    'employee',
    'location',
    'division',
    'department',
    'state',
    'employmentType',
    'payType',
    'actions',
  ];
  private readonly paycorAllEmployeesColumns: string[] = [
    'select',
    'employee',
    'status',
    'jobTitle',
    'department',
    'manager',
    'payGroup',
    'workerType',
    'location',
    'actions',
  ];
  public allEmployeesSort: Sort = { active: '', direction: '' };

  get allEmployeesColumns(): string[] {
    return this.isPaycorProviderCensus
      ? this.paycorAllEmployeesColumns
      : this.defaultAllEmployeesColumns;
  }

  constructor(
    private sessionService: SessionService,
    private spinnerService: SpinnerService,
    private teamsService: TeamsService,
    private teamProgramsService: TeamprogramsService,
    private transactionsService: TransactionsService,
    private usersService: UsersService,
    private uiAlertService: UiAlertService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private stripeService: StripeService,
    private payrollCensusService: PayrollCensusService,
    private payrollService: PayrollService,
    private payrollBenefitsAssignmentService: PayrollBenefitsAssignmentService,
    private payrollSnacksAssignmentService: PayrollSnacksAssignmentService,
    private teamCompaniesService: TeamCompaniesService,
    private managedFeatureFlagsService: ManagedFeatureFlagsService,
    private payrollEmployeeTerminationsService: PayrollEmployeeTerminationsService,
    private activeEmployerCompanyService: ActiveEmployerCompanyService,
    private payrollAccessService: PayrollAccessService,
    private nativeTeamContextService: NativeTeamContextService,
    private teamMemberRemovalCoordinator: TeamMemberRemovalCoordinator,
    private route: ActivatedRoute,
    private router: Router,
  ) {
    this.census = this.payrollCensusService.createSession();
    this.workforce = new WorkforceBrowseState(this.payrollService);
    this.teamCreation = new WorkforceTeamCreationState(this.payrollService, teamId => {
      void this.onSnapshotOperationComplete(teamId);
    });
  }

  ngOnInit(): void {
    this.isNativeCompanyManager = this.payrollAccessService.workspaceContext === 'nativeCompanyManager';
    this.meSubscription = this.usersService.meSubscribe(async (me: any) => {
      if (!me) { this.workforce.destroy(); this.teamCreation.destroy(); this.invalidateAllEmployeesCensusCache(); return; }
      if (me) {
        const cachedCompanies = this.isCompanyScopedWorkspace
          ? undefined
          : this.usersService.getCachedTeamCompanies();
        if (cachedCompanies) {
          this.updateCompanyState(cachedCompanies);
          this.isInitialCompanyCheckComplete = true;
        }

        try {
          await this.loadCompanySnackRewardSelectionFeatureFlag();
          await this.loadPageData(!cachedCompanies);
        } finally {
          this.isInitialCompanyCheckComplete = true;
        }

        this.maybeOpenCreateTeamFlowFromQuery();
        this.subscribeToActiveEmployerCompany();
      }
    });
  }

  ngOnDestroy(): void {
    this.isDestroyed = true;
    ++this.pageLoadVersion;
    this.workforce.destroy(); this.teamCreation.destroy();
    this.invalidateAllEmployeesCensusCache();
    this.terminationRequestVersion++;
    this.cancelGuidedTerminationReveal();
    this.terminationReviewDialogRef?.close();
    this.meSubscription?.unsubscribe();
    this.activeEmployerCompanySubscription?.unsubscribe();
  }

  get isCompanyScopedWorkspace(): boolean {
    return this.isNativeCompanyManager
      || this.activeEmployerCompanyService.state.workspaceContext === 'superadminCompany';
  }

  get canShowNativeSnacksActions(): boolean {
    return this.isCompanyScopedWorkspace
      ? this.activeEmployerCompanyService.state.hasSnacks
      : this.isCompanySnackRewardSelectionEnabled;
  }

  get isHealthyPayrollConnection(): boolean {
    return this.payrollConnectionHealth === 'healthy';
  }

  get canUseProviderCensus(): boolean {
    return this.canUseLegacyCensus && this.isHealthyPayrollConnection && this.providerConnectionSupportsCapability('supportsCensus');
  }

  get canRefreshAllEmployeesFromUkg(): boolean {
    return this.canUseProviderCensus
      && this.getPayrollProviderSlug() === UKG_READY_PROVIDER_SLUG
      && this.allEmployeesLoaded
      && this.allEmployeesLoadedContextKey === this.getAllEmployeesContextKey()
      && !this.hasAllEmployeesConnectionError;
  }

  get isPaycorProviderCensus(): boolean {
    return this.getPayrollProviderSlug() === 'paycor';
  }

  get canUseProviderCensusFilters(): boolean {
    return this.canUseProviderCensus && this.providerConnectionSupportsCapability('supportsCensusFilters');
  }

  get canUsePaycorLocalCensusFilters(): boolean {
    return this.canUseProviderCensus && this.census.usesLocalCensusFilters();
  }

  get canUseAllEmployeesFilterControls(): boolean {
    return this.canUseProviderCensusFilters || this.canUsePaycorLocalCensusFilters;
  }

  get canUseProviderTeamWrites(): boolean {
    return this.canUseProviderCensus && this.providerConnectionSupportsCapability('supportsFilteredTeams');
  }

  get canCreateInternalTeamFromCensusSelection(): boolean {
    return this.canUseProviderCensus
      && this.providerConnectionSupportsCapability('supportsInternalTeamCreationFromCensus');
  }

  get canUseProviderAutoAdd(): boolean {
    return this.canUseProviderTeamWrites && this.providerConnectionSupportsCapability('supportsAutoAddFromSavedRules');
  }

  get canUseSelectedTeamAddOnlyAutoAdd(): boolean {
    const team: any = this.selectedTeam;
    return !!team?.id
      && this.canUseProviderCensus
      && this.getPayrollProviderSlug() === 'paycor'
      && this.isWallitManagedProviderCensusSnapshotTeam(team)
      && this.getTeamSourceProviderSlug(team) === 'paycor'
      && this.providerConnectionSupportsCapability('supportsAutoAddFromSavedRules')
      && this.providerConnectionSupportsCapability('supportsInternalTeamCreationFromCensus');
  }

  get canShowSelectedTeamAddOnlyAutoAddControls(): boolean {
    const team: any = this.selectedTeam;
    return !!team?.id
      && this.canUseProviderCensus
      && this.getPayrollProviderSlug() === 'paycor'
      && this.isWallitManagedProviderCensusTeam(team)
      && this.getTeamSourceProviderSlug(team) === 'paycor'
      && this.providerConnectionSupportsCapability('supportsAutoAddFromSavedRules')
      && this.providerConnectionSupportsCapability('supportsInternalTeamCreationFromCensus');
  }

  get canUseSelectedTeamAutoAdd(): boolean {
    return this.canUseProviderAutoAdd || this.canUseSelectedTeamAddOnlyAutoAdd;
  }

  get canShowSelectedTeamAutoAddControls(): boolean {
    if (!this.canUseLegacyCensus) return false;
    const team: any = this.selectedTeam;
    return this.canUseSelectedTeamAutoAdd
      || this.canShowSelectedTeamAddOnlyAutoAddControls
      || !!team?.autoAddNewMembers;
  }

  get isReadOnlyProviderCensus(): boolean {
    return this.canUseProviderCensus && !this.canUseProviderTeamWrites;
  }

  get isManualTeamFlow(): boolean {
    return !this.canUseProviderTeamWrites;
  }

  get canUseProviderTeamCreationFlow(): boolean {
    return this.canUseProviderTeamWrites || this.canCreateInternalTeamFromCensusSelection;
  }

  get isPayrollConnectionMissing(): boolean {
    return this.payrollConnectionHealth === 'none';
  }

  get isPayrollConnectionErrored(): boolean {
    return this.payrollConnectionHealth === 'error';
  }

  get pendingGroupProductOnboardingRows(): CompanyGroupProductOnboardingRow[] {
    return (this.groupProductOnboardingRows || [])
      .filter((row) => row?.status === 'pending')
      .sort((left, right) => `${left?.providerName || ''} ${left?.planName || ''}`
        .localeCompare(`${right?.providerName || ''} ${right?.planName || ''}`));
  }

  get hasPendingGroupProductOnboarding(): boolean {
    return this.pendingGroupProductOnboardingRows.length > 0;
  }

  get pendingGroupProductOnboardingPlanNames(): string {
    return this.pendingGroupProductOnboardingRows
      .map((row) => [row?.providerName || '', row?.planName || ''].filter(Boolean).join(' - '))
      .filter((label) => !!label)
      .join(', ');
  }

  get pendingGroupProductBannerMessage(): string {
    if (!this.hasPendingGroupProductOnboarding) {
      return '';
    }
    const planNames = this.pendingGroupProductOnboardingPlanNames;
    return planNames
      ? `Your employees will not have access to all desired group plans until Wallit completes the required enrollment paperwork for: ${planNames}.`
      : 'Your employees will not have access to all desired group plans until Wallit completes the required enrollment paperwork.';
  }

  get pendingGroupProductInviteBlockMessage(): string {
    if (!this.hasPendingGroupProductOnboarding) {
      return '';
    }
    return 'Employee invites are temporarily disabled until Wallit completes the required enrollment paperwork for your pending group plans.';
  }

  get payrollConnectionStateTitle(): string {
    if (this.payrollConnectionUnsupportedReason) {
      return 'Payroll provider unavailable';
    }
    if (this.isPayrollConnectionErrored) {
      return `${this.getPayrollProviderDisplayName()} connection needs attention`;
    }
    if (this.isPayrollConnectionMissing) {
      return 'Payroll connection not set up';
    }
    return `${this.getPayrollProviderDisplayName()} connection active`;
  }

  get payrollConnectionStateMessage(): string {
    if (this.payrollConnectionUnsupportedReason) {
      return `${this.payrollConnectionUnsupportedReason} Company and team management is still available, and you can keep working with people already added to company teams.`;
    }
    if (this.isPayrollConnectionErrored) {
      return `Company and team management is still available, and you can keep working with people already added to company teams. ${this.getPayrollProviderDisplayName()} import and filter-based tools stay disabled until the connection is fixed.`;
    }
    if (this.isPayrollConnectionMissing) {
      if (this.isCompanyScopedWorkspace) {
        return 'Company and team management is available. Payroll census import, filters, and auto-add are unavailable because this company does not have a configured payroll provider.';
      }
      return 'Company and team management is available now, and you can work with people already added to company teams. Connect a payroll provider later if you want census import, filters, and auto-add.';
    }
    if (this.isReadOnlyProviderCensus) {
      if (this.canCreateInternalTeamFromCensusSelection) {
        return `${this.getPayrollProviderDisplayName()} census is read-only for provider writes. Browse employees in All Employees and create internal Wallit teams from selected employees or saved filters; payroll-provider team writes stay disabled.`;
      }
      return `${this.getPayrollProviderDisplayName()} census is read-only for now. Browse employees in All Employees; filters, auto-add, and payroll-provider team writes stay disabled.`;
    }
    return `Use ${this.getPayrollProviderDisplayName()} import, filters, and auto-add to manage payroll teams.`;
  }

  get allEmployeesConnectionErrorTitle(): string {
    if (this.isAllEmployeesCapacityError) {
      return 'Census exceeds interactive limit';
    }
    if (this.allEmployeesCensusErrorCode === 'PAYCOR_ENDPOINT_UNSUPPORTED') {
      return 'Payroll census unavailable';
    }
    if (this.allEmployeesCensusErrorCode) {
      return 'Payroll census could not be loaded';
    }
    return this.payrollConnectionUnsupportedReason
      ? 'Payroll provider unavailable'
      : 'Payroll connection error';
  }

  get allEmployeesConnectionErrorMessage(): string {
    if (this.allEmployeesConnectionError) {
      return this.allEmployeesConnectionError;
    }
    if (this.payrollConnectionUnsupportedReason) {
      return this.payrollConnectionUnsupportedReason;
    }
    if (this.isCompanyScopedWorkspace) {
      return `The ${this.getPayrollProviderDisplayName('payroll provider')} connection is unavailable. Existing company teams and members remain available.`;
    }
    return `Fix the ${this.getPayrollProviderDisplayName('payroll provider')} connection to browse imported employees.`;
  }

  get isAllEmployeesCapacityError(): boolean {
    return [
      'PAYCOR_CENSUS_CAPACITY_EXCEEDED',
      'PAYCOR_PAGINATION_CAPACITY_EXCEEDED',
      'PAYCOR_PAGINATION_LIMIT_EXCEEDED',
    ].includes(this.allEmployeesCensusErrorCode);
  }

  get shouldShowAllEmployeesConnectionAction(): boolean {
    return !this.isCompanyScopedWorkspace
      && !this.allEmployeesCensusErrorCode
      && this.isPayrollConnectionErrored;
  }

  get allEmployeesCensusWarningMessage(): string {
    const completeness = this.census?.completeness;
    if (!completeness || completeness.complete !== false) {
      return '';
    }
    const missingEmailCount = Number(completeness.missingEmailCount || 0);
    if (missingEmailCount > 0) {
      const employeeLabel = missingEmailCount === 1 ? 'employee is' : 'employees are';
      return `Census loaded with warnings: ${missingEmailCount} active ${employeeLabel} missing an email address and cannot be selected until email data is available.`;
    }
    if (completeness.employeeCollectionComplete === false) {
      return 'Census enumeration is incomplete. This employee list must not be treated as the complete company population.';
    }
    const warningMessages = (completeness.warnings || [])
      .map((warning) => `${warning?.message || ''}`.trim())
      .filter((message) => !!message);
    return warningMessages.length > 0
      ? `Census loaded with warnings: ${warningMessages.join(' ')}`
      : 'Census loaded with completeness warnings.';
  }

  get payrollImportUnavailableTitle(): string {
    return 'Payroll import unavailable';
  }

  get payrollImportUnavailableMessage(): string {
    if (this.isCompanyScopedWorkspace) {
      return 'Payroll census import and filter-based team creation are unavailable because this company does not have a configured payroll provider.';
    }
    return 'Connect a payroll provider in Company Connections to use census import and filter-based team creation.';
  }

  get allEmployeesFallbackBannerMessage(): string {
    if (this.isCompanyScopedWorkspace) {
      return 'Showing existing members already added to teams for this company. To add people manually, open a team and choose Edit team.';
    }
    if (this.payrollConnectionUnsupportedReason) {
      return `${this.payrollConnectionUnsupportedReason} Showing existing members already added to teams for this company.`;
    }
    if (this.isPayrollConnectionErrored) {
      return `Showing existing members already added to teams for this company. Fix the ${this.getPayrollProviderDisplayName('payroll provider')} connection to browse the full employee census and use filter-based team creation. To add people manually, open a team and choose Edit team.`;
    }
    return 'Showing existing members already added to teams for this company. Connect a payroll provider to browse the full employee census and use filter-based team creation. To add people manually, open a team and choose Edit team.';
  }

  get createTeamFromSelectionDisabledTooltip(): string {
    if (this.canCreateTeamFromSelection) {
      return '';
    }
    if (this.isReadOnlyProviderCensus) {
      if (this.canCreateInternalTeamFromCensusSelection) {
        return 'Select at least one employee with an email address to create an internal Wallit team.';
      }
      return `${this.getPayrollProviderDisplayName()} census is read-only for now. Create and manage teams manually.`;
    }
    if (this.payrollConnectionUnsupportedReason) {
      return this.payrollConnectionUnsupportedReason;
    }
    return this.isCompanyScopedWorkspace
      ? 'A configured supported payroll provider is required to create teams from employee selection.'
      : 'Connect a supported payroll provider to create teams from employee selection.';
  }

  get allEmployeesFilterCardTitle(): string {
    return this.canUseAllEmployeesFilterControls ? 'FILTER EMPLOYEE LIST' : 'SEARCH EMPLOYEE LIST';
  }

  get allEmployeesReadOnlyBannerMessage(): string {
    if (!this.isReadOnlyProviderCensus) {
      return '';
    }
    if (this.canCreateInternalTeamFromCensusSelection) {
      return '';
    }
    return `${this.getPayrollProviderDisplayName()} census is read-only for now. Employee rows and details are available, but filters, auto-add, saved-filter import, and provider team writes are disabled.`;
  }

  onTabChange(index: number): void {
    this.selectedTabIndex = index;
    void this.workforce.setVisible(index === 2);
    if (index === 1) {
      void this.ensureSelectedTeamDetailsLoaded();
    }
    if (index === 2) {
      // All Employees tab
      void this.ensureAllEmployeesLoaded();
    }
  }

  get terminationReviewCountLabel(): string {
    return `${this.terminationCases.length}${this.hasMoreTerminationCases ? '+' : ''}`;
  }

  get hasMoreTerminationCases(): boolean {
    return ACTIVE_TERMINATION_STATUSES.some((status) => this.terminationPages[status].hasMore);
  }

  get showTerminationReviewCard(): boolean {
    return this.isInitialCompanyCheckComplete
      && this.hasCompany
      && this.isTerminationFeatureEnabled;
  }

  toggleTerminationReviewList(): void {
    this.isTerminationReviewListOpen = !this.isTerminationReviewListOpen;
  }

  async toggleResolvedHistory(): Promise<void> {
    if (this.isResolvedHistoryOpen) {
      this.clearResolvedHistoryState();
      return;
    }
    this.isResolvedHistoryOpen = true;
    await this.loadResolvedTerminationHistory();
  }

  async retryResolvedTerminationHistory(): Promise<void> {
    if (this.isResolvedHistoryLoading) {
      return;
    }
    await this.loadResolvedTerminationHistory();
  }

  get hasMoreResolvedTerminationCases(): boolean {
    return RESOLVED_TERMINATION_STATUSES.some((status) => this.resolvedTerminationPages[status].hasMore);
  }

  resolvedTerminationLabel(terminationCase: PayrollEmployeeTerminationCase): string {
    if (terminationCase.lifecycleStatus === 'completed') {
      return 'Completed';
    }
    if (terminationCase.dismissalReason === 'initial_baseline_history') {
      return 'Baseline history';
    }
    if (terminationCase.dismissalReason === 'admin_marked_resolved') {
      return 'Marked resolved';
    }
    return 'Resolved';
  }

  resolvedTerminationDate(terminationCase: PayrollEmployeeTerminationCase): string | null {
    return terminationCase.lifecycleStatus === 'completed'
      ? terminationCase.completedAt
      : terminationCase.dismissedAt;
  }

  async loadMoreResolvedTerminationCases(): Promise<void> {
    if (this.isResolvedHistoryLoadingMore || !this.hasMoreResolvedTerminationCases) {
      return;
    }
    await this.loadResolvedTerminationHistory(true);
  }

  async retryTerminationReview(): Promise<void> {
    await this.loadTerminationReviewForCurrentCompany();
  }

  async loadMoreTerminationCases(): Promise<void> {
    if (this.isTerminationReviewLoadingMore || !this.hasMoreTerminationCases) {
      return;
    }
    const companyId = `${this.currentCompany?.id || ''}`.trim();
    const userId = `${this.sessionService.getUserId() || ''}`.trim();
    if (!companyId || !userId) {
      this.resetTerminationReviewState();
      return;
    }
    const requestVersion = this.terminationRequestVersion;
    const statuses = ACTIVE_TERMINATION_STATUSES.filter((status) => this.terminationPages[status].hasMore);
    if (!statuses.length) {
      return;
    }

    this.isTerminationReviewLoadingMore = true;
    this.terminationReviewLoadError = '';
    try {
      const results = await Promise.all(statuses.map(async (status) => ({
        status,
        response: await this.payrollEmployeeTerminationsService.listCases(companyId, {
          lifecycleStatus: status,
          limit: TERMINATION_PAGE_LIMIT,
          offset: this.terminationPages[status].offset,
        }),
      })));
      if (!this.isCurrentTerminationRequest(companyId, requestVersion, userId)) {
        return;
      }

      const incomingItems: PayrollEmployeeTerminationCase[] = [];
      results.forEach(({status, response}) => {
        incomingItems.push(...response.items);
        this.updateTerminationPage(status, response);
      });
      this.terminationCases = this.sortTerminationCases(
        this.mergeTerminationCaseSnapshots(
          this.terminationCases,
          incomingItems,
          ACTIVE_TERMINATION_STATUSES,
        ),
      );
      this.rebuildTerminationCaseLookup();
      await this.hydrateTerminationMemberDisplays(companyId, requestVersion, userId);
    } catch (error: any) {
      if (!this.isCurrentTerminationRequest(companyId, requestVersion, userId)) {
        return;
      }
      if (this.isUnavailableTerminationError(error)) {
        this.resetTerminationReviewState();
        return;
      }
      this.terminationReviewLoadError = 'Unable to load more termination reviews right now.';
    } finally {
      if (this.isCurrentTerminationRequest(companyId, requestVersion, userId)) {
        this.isTerminationReviewLoadingMore = false;
      }
    }
  }

  terminationCaseForMember(member: any): PayrollEmployeeTerminationCase | undefined {
    return member?.userId ? this.terminationCaseByUserId.get(member.userId) : undefined;
  }

  terminationReviewLabel(terminationCase: PayrollEmployeeTerminationCase): string {
    if (terminationCase.lifecycleStatus === 'scheduled') {
      return 'Termination scheduled';
    }
    if (
      terminationCase.lifecycleStatus === 'needs_review'
      && terminationCase.reviewEligible
      && terminationCase.actionable
    ) {
      return 'Termination review';
    }
    return 'Manual review';
  }

  terminationReviewMarkerClass(terminationCase: PayrollEmployeeTerminationCase): string {
    if (terminationCase.lifecycleStatus === 'scheduled') {
      return 'termination-marker--scheduled';
    }
    if (this.terminationReviewLabel(terminationCase) === 'Termination review') {
      return 'termination-marker--effective';
    }
    return 'termination-marker--manual';
  }

  terminationMarkerAriaLabel(member: any, terminationCase: PayrollEmployeeTerminationCase): string {
    const mode = this.isTerminationProcessingEnabled ? '' : 'read-only ';
    return `${this.terminationReviewLabel(terminationCase)} for ${this.memberDisplayName(member)}. Open ${mode}termination review.`;
  }

  isGuidedTerminationMember(member: any): boolean {
    return !!this.guidedTerminationUserId
      && member?.userId === this.guidedTerminationUserId
      && this.guidedTerminationTeamIds.has(`${this.selectedTeam?.id || ''}`);
  }

  get showGuidedTerminationBanner(): boolean {
    return !!this.guidedTerminationReadiness && (
      this.guidedTerminationReadiness.state === 'ready_to_complete'
      || this.guidedTerminationTeamIds.has(`${this.selectedTeam?.id || ''}`)
    );
  }

  get guidedTerminationMessage(): string {
    const readiness = this.guidedTerminationReadiness;
    if (!readiness) {
      return '';
    }
    if (readiness.state === 'awaiting_final_removal') {
      return 'Settlement must clear before Finalize Removal can complete this membership.';
    }
    if (readiness.state === 'ready_to_complete') {
      return 'All required local outcomes are verified. Open the termination review to complete it.';
    }
    return readiness.blockers[0]?.message
      || 'Use the existing Remove action for each highlighted company membership.';
  }

  stopTerminationMarkerKeyboardEvent(event: Event): void {
    event.stopPropagation();
  }

  terminationEmployeeIdentity(terminationCase: PayrollEmployeeTerminationCase): TerminationReviewEmployeeIdentity {
    if (terminationCase.matchStatus === 'ambiguous') {
      return {name: 'Ambiguous payroll employee', email: ''};
    }
    if (terminationCase.matchStatus !== 'exact' || !terminationCase.userId) {
      return {name: 'Unmatched payroll employee', email: ''};
    }

    const member = this.findExactTerminationMember(terminationCase.userId);
    if (!member) {
      return {name: 'Matched Wallit employee', email: ''};
    }
    const firstName = `${member?.firstName || ''}`.trim();
    const lastName = `${member?.lastName || ''}`.trim();
    const displayName = `${firstName} ${lastName}`.trim() || `${member?.name || ''}`.trim();
    return {
      name: displayName || 'Matched Wallit employee',
      email: this.memberEmail(member) || '',
    };
  }

  openTerminationReview(
    terminationCase: PayrollEmployeeTerminationCase,
    event?: Event,
  ): void {
    event?.stopPropagation();
    const companyId = `${this.currentCompany?.id || ''}`.trim();
    const isActiveCase = this.terminationCases.some((item) => item.id === terminationCase.id);
    const isResolvedCase = this.resolvedTerminationCases.some((item) => item.id === terminationCase.id);
    if (!companyId || (!isActiveCase && !isResolvedCase)) {
      return;
    }
    this.terminationReviewDialogRef?.close();
    this.terminationReviewDialogRef = undefined;
    this.clearGuidedTerminationState();
    const guidedRequestVersion = this.guidedTerminationRequestVersion;

    let dialogRef: MatDialogRef<TerminationReviewDialogComponent>;
    const data: TerminationReviewDialogData = {
      companyId,
      case: terminationCase,
      employeeIdentity: this.terminationEmployeeIdentity(terminationCase),
      teamNamesById: this.teams.reduce((names: Record<string, string>, team: any) => {
        if (team?.id) {
          names[team.id] = team.name || 'Company team';
        }
        return names;
      }, {}),
      processingEnabled: this.isTerminationProcessingEnabled && isActiveCase,
      deductionCancellationWritesEnabled:
        this.isTerminationDeductionCancellationWritesEnabled && isActiveCase,
      onProcessingUnavailable: () => {
        if (
          this.terminationReviewDialogRef !== dialogRef
          || `${this.currentCompany?.id || ''}`.trim() !== companyId
        ) {
          return;
        }
        this.isTerminationDeductionCancellationWritesEnabled = false;
        this.clearGuidedTerminationState();
      },
    };
    dialogRef = this.dialog.open<
      TerminationReviewDialogComponent,
      TerminationReviewDialogData,
      TerminationReviewDialogResult
    >(
      TerminationReviewDialogComponent,
      {
        width: '820px',
        maxWidth: '96vw',
        data,
      },
    );
    this.terminationReviewDialogRef = dialogRef;
    dialogRef?.afterClosed().subscribe((result) => {
      if (this.terminationReviewDialogRef === dialogRef) {
        this.terminationReviewDialogRef = undefined;
      }
      if (`${this.currentCompany?.id || ''}`.trim() !== companyId || !result) {
        return;
      }
      if (guidedRequestVersion !== this.guidedTerminationRequestVersion) {
        return;
      }
      if (result.action === 'review_removals' && result.readiness.caseId === terminationCase.id) {
        void this.focusGuidedTerminationRows(terminationCase, result.readiness);
      } else if (result.action === 'completed' && result.caseId === terminationCase.id) {
        this.handleTerminalTerminationResult(companyId, result.caseId);
      } else if (result.action === 'resolved' && result.caseId === terminationCase.id) {
        this.handleTerminalTerminationResult(companyId, result.caseId);
      }
    });
  }

  openGuidedTerminationReview(): void {
    const terminationCase = this.terminationCases.find((item) => item.id === this.guidedTerminationCaseId);
    if (terminationCase) {
      this.openTerminationReview(terminationCase);
    }
  }

  private async focusGuidedTerminationRows(
    terminationCase: PayrollEmployeeTerminationCase,
    readiness: PayrollTerminationProcessingReadiness,
  ): Promise<void> {
    if (
      terminationCase.matchStatus !== 'exact'
      || !terminationCase.userId
      || readiness.caseId !== terminationCase.id
    ) {
      return;
    }
    this.guidedTerminationCaseId = terminationCase.id;
    this.guidedTerminationUserId = terminationCase.userId;
    this.guidedTerminationReadiness = readiness;
    this.guidedTerminationTeamIds = new Set(readiness.affectedTeamIds);
    const targetTeamId = readiness.affectedTeamIds.find((teamId) => !!this.getAuthorizedTeamId(teamId));
    if (targetTeamId) {
      await this.navigateToGuidedTerminationTeam(targetTeamId, {
        companyId: `${this.currentCompany?.id || ''}`.trim(),
        caseId: terminationCase.id,
        requestVersion: this.guidedTerminationRequestVersion,
        teamId: targetTeamId,
      });
    }
  }

  private async refreshGuidedTerminationReadiness(): Promise<void> {
    const companyId = `${this.currentCompany?.id || ''}`.trim();
    const caseId = this.guidedTerminationCaseId;
    const userId = this.guidedTerminationUserId;
    if (!this.isTerminationProcessingEnabled || !companyId || !caseId || !userId) {
      this.clearGuidedTerminationState();
      return;
    }
    const requestVersion = ++this.guidedTerminationRequestVersion;
    this.cancelGuidedTerminationReveal();
    try {
      const readiness = await this.payrollEmployeeTerminationsService.getProcessingReadiness(companyId, caseId);
      if (
        requestVersion !== this.guidedTerminationRequestVersion
        || companyId !== `${this.currentCompany?.id || ''}`.trim()
        || caseId !== this.guidedTerminationCaseId
        || readiness.caseId !== caseId
      ) {
        return;
      }
      this.guidedTerminationReadiness = readiness;
      const hasRemovalTargets = ['ready_for_removal', 'awaiting_removal', 'awaiting_final_removal']
        .includes(readiness.state);
      this.guidedTerminationTeamIds = new Set(hasRemovalTargets ? readiness.affectedTeamIds : []);
      const selectedTeamStillAffected = readiness.affectedTeamIds.includes(`${this.selectedTeam?.id || ''}`);
      const nextTeamId = readiness.affectedTeamIds.find((teamId) => !!this.getAuthorizedTeamId(teamId));
      if (!selectedTeamStillAffected && nextTeamId) {
        await this.navigateToGuidedTerminationTeam(nextTeamId, {
          companyId,
          caseId,
          requestVersion,
          teamId: nextTeamId,
        });
      }
    } catch (_) {
      if (requestVersion === this.guidedTerminationRequestVersion) {
        this.clearGuidedTerminationState();
      }
    }
  }

  private clearGuidedTerminationState(): void {
    this.guidedTerminationRequestVersion++;
    this.cancelGuidedTerminationReveal();
    this.guidedTerminationReadiness = undefined;
    this.guidedTerminationCaseId = '';
    this.guidedTerminationUserId = '';
    this.guidedTerminationTeamIds.clear();
  }

  private async navigateToGuidedTerminationTeam(
    teamId: string,
    context: GuidedTerminationRevealContext,
  ): Promise<void> {
    if (!this.isCurrentGuidedTerminationReveal(context)) {
      return;
    }
    this.selectedTabIndex = 1;
    const selected = await this.onSelectedTeamChange(teamId);
    if (!selected || !this.isCurrentGuidedTerminationReveal(context)) {
      return;
    }
    const waitForPanelExpansion = !this.isEmployeesPanelExpanded;
    this.isEmployeesPanelExpanded = true;
    this.scheduleGuidedTerminationReveal(context, waitForPanelExpansion);
  }

  private scheduleGuidedTerminationReveal(
    context: GuidedTerminationRevealContext,
    waitForPanelExpansion = false,
  ): void {
    this.cancelGuidedTerminationReveal();
    this.pendingGuidedTerminationReveal = context;
    if (waitForPanelExpansion) {
      return;
    }
    this.deferGuidedTerminationReveal(context);
  }

  onEmployeesPanelAfterExpand(): void {
    const context = this.pendingGuidedTerminationReveal;
    if (context && !this.guidedTerminationRevealTimer) {
      this.deferGuidedTerminationReveal(context);
    }
  }

  private deferGuidedTerminationReveal(context: GuidedTerminationRevealContext): void {
    this.guidedTerminationRevealTimer = setTimeout(() => {
      this.guidedTerminationRevealTimer = undefined;
      this.revealGuidedTerminationTarget(context);
    }, 0);
  }

  private revealGuidedTerminationTarget(context: GuidedTerminationRevealContext): void {
    if (this.pendingGuidedTerminationReveal !== context) {
      return;
    }
    this.pendingGuidedTerminationReveal = undefined;
    if (!this.isCurrentGuidedTerminationReveal(context)) {
      return;
    }
    const target = this.guidedTerminationBanner?.nativeElement
      || this.selectedTeamMemberRows?.find((row) => (
        row.nativeElement.classList.contains('member-row--termination-guided')
      ))?.nativeElement;
    if (!target) {
      return;
    }
    target.scrollIntoView({behavior: 'smooth', block: 'center'});
    target.focus({preventScroll: true});
  }

  private isCurrentGuidedTerminationReveal(context: GuidedTerminationRevealContext): boolean {
    return !!context.companyId
      && context.companyId === `${this.currentCompany?.id || ''}`.trim()
      && context.caseId === this.guidedTerminationCaseId
      && context.caseId === this.guidedTerminationReadiness?.caseId
      && context.requestVersion === this.guidedTerminationRequestVersion
      && context.teamId === `${this.selectedTeam?.id || ''}`
      && this.guidedTerminationTeamIds.has(context.teamId)
      && !!this.getAuthorizedTeamId(context.teamId);
  }

  private cancelGuidedTerminationReveal(): void {
    if (this.guidedTerminationRevealTimer !== undefined) {
      clearTimeout(this.guidedTerminationRevealTimer);
      this.guidedTerminationRevealTimer = undefined;
    }
    this.pendingGuidedTerminationReveal = undefined;
  }

  trackByTerminationCase(_: number, terminationCase: PayrollEmployeeTerminationCase): string {
    return terminationCase.id;
  }

  private async loadTerminationReviewForCurrentCompany(options: {
    preserveOpenState?: boolean;
    reopenResolved?: boolean;
  } = {}): Promise<void> {
    const companyId = `${this.currentCompany?.id || ''}`.trim();
    const userId = `${this.sessionService.getUserId() || ''}`.trim();
    const reviewListWasOpen = options.preserveOpenState && this.isTerminationReviewListOpen;
    const resolvedHistoryWasOpen = options.reopenResolved && this.isResolvedHistoryOpen;
    this.resetTerminationReviewState();
    const requestVersion = this.terminationRequestVersion;
    this.isTerminationReviewLoading = !!companyId && !!userId;
    if (!companyId || !userId || !['legacy', 'snapshot'].includes(this.workforce.mode)) {
      this.isTerminationReviewLoading = false;
      return;
    }

    let enabled = false;
    try {
      enabled = await this.managedFeatureFlagsService.isEnabled(
        PAYROLL_ADMIN_UKG_EMPLOYEE_TERMINATIONS_FLAG,
        'team-admin',
        {
          companyId,
          userId,
        },
      );
    } catch (_) {
      if (this.isCurrentTerminationRequest(companyId, requestVersion, userId)) {
        this.isTerminationReviewLoading = false;
      }
      return;
    }

    if (!this.isCurrentTerminationRequest(companyId, requestVersion, userId) || !enabled) {
      if (this.isCurrentTerminationRequest(companyId, requestVersion, userId)) {
        this.isTerminationReviewLoading = false;
      }
      return;
    }
    this.isTerminationFeatureEnabled = true;

    let processingEnabled = false;
    try {
      processingEnabled = await this.managedFeatureFlagsService.isEnabled(
        PAYROLL_ADMIN_UKG_EMPLOYEE_TERMINATION_PROCESSING_FLAG,
        'team-admin',
        {companyId, userId},
      );
    } catch (_) {
      processingEnabled = false;
    }
    if (!this.isCurrentTerminationRequest(companyId, requestVersion, userId)) {
      return;
    }
    this.isTerminationProcessingEnabled = processingEnabled;

    let deductionCancellationWritesEnabled = false;
    if (processingEnabled) {
      try {
        deductionCancellationWritesEnabled = await this.managedFeatureFlagsService.isEnabled(
          PAYROLL_ADMIN_UKG_DEDUCTION_CANCELLATION_WRITES_FLAG,
          'team-admin',
          {companyId, userId},
        );
      } catch (_) {
        deductionCancellationWritesEnabled = false;
      }
    }
    if (!this.isCurrentTerminationRequest(companyId, requestVersion, userId)) {
      return;
    }
    this.isTerminationDeductionCancellationWritesEnabled = deductionCancellationWritesEnabled;

    try {
      const results = await Promise.all(ACTIVE_TERMINATION_STATUSES.map(async (status) => ({
        status,
        response: await this.payrollEmployeeTerminationsService.listCases(companyId, {
          lifecycleStatus: status,
          limit: TERMINATION_PAGE_LIMIT,
          offset: 0,
        }),
      })));
      if (!this.isCurrentTerminationRequest(companyId, requestVersion, userId)) {
        return;
      }

      const items: PayrollEmployeeTerminationCase[] = [];
      results.forEach(({status, response}) => {
        items.push(...response.items);
        this.updateTerminationPage(status, response);
      });
      this.terminationCases = this.sortTerminationCases(
        this.mergeTerminationCaseSnapshots([], items, ACTIVE_TERMINATION_STATUSES),
      );
      this.isTerminationReviewListOpen = !!reviewListWasOpen;
      this.rebuildTerminationCaseLookup();
      await this.hydrateTerminationMemberDisplays(companyId, requestVersion, userId);
      if (resolvedHistoryWasOpen && this.isCurrentTerminationRequest(companyId, requestVersion, userId)) {
        this.isResolvedHistoryOpen = true;
        await this.loadResolvedTerminationHistory();
      }
    } catch (error: any) {
      if (!this.isCurrentTerminationRequest(companyId, requestVersion, userId)) {
        return;
      }
      if (this.isUnavailableTerminationError(error)) {
        this.resetTerminationReviewState();
        return;
      }
      this.terminationReviewLoadError = 'Unable to load termination reviews right now.';
    } finally {
      if (this.isCurrentTerminationRequest(companyId, requestVersion, userId)) {
        this.isTerminationReviewLoading = false;
      }
    }
  }

  private async hydrateTerminationMemberDisplays(
    companyId: string,
    requestVersion: number,
    userId: string,
  ): Promise<void> {
    // Full-company display enrichment belongs only to the legacy census experience.
    // Snapshot displays use any already available Wallit member or the neutral fallback.
    if (!this.canUseLegacyCensus) {
      this.terminationMembersByUserId.clear();
      return;
    }
    if (![...this.terminationCases, ...this.resolvedTerminationCases].some((terminationCase) => (
      terminationCase.matchStatus === 'exact' && !!terminationCase.userId
    ))) {
      return;
    }
    try {
      const members = await this.teamsService.getPayrollUsersForCompany(companyId);
      if (!this.canUseLegacyCensus || !this.isCurrentTerminationRequest(companyId, requestVersion, userId)) {
        return;
      }
      this.terminationMembersByUserId.clear();
      (Array.isArray(members) ? members : []).forEach((member: any) => {
        if (member?.userId && !this.terminationMembersByUserId.has(member.userId)) {
          this.terminationMembersByUserId.set(member.userId, member);
        }
      });
    } catch (_) {
      // Display hydration is optional. Exact matches retain a neutral Wallit fallback.
    }
  }

  private findExactTerminationMember(userId: string): any | undefined {
    const hydratedMember = this.terminationMembersByUserId.get(userId);
    if (hydratedMember) {
      return hydratedMember;
    }
    for (const team of this.teams) {
      const member = (Array.isArray(team?.teamMembers) ? team.teamMembers : [])
        .find((teamMember: any) => teamMember?.userId === userId);
      if (member) {
        return member;
      }
    }
    return undefined;
  }

  private rebuildTerminationCaseLookup(): void {
    this.terminationCaseByUserId.clear();
    [...this.terminationCases]
      .filter((terminationCase) => (
        terminationCase.matchStatus === 'exact' && !!terminationCase.userId
      ))
      .sort((left, right) => this.terminationMarkerPriority(left) - this.terminationMarkerPriority(right))
      .forEach((terminationCase) => {
        if (!this.terminationCaseByUserId.has(terminationCase.userId as string)) {
          this.terminationCaseByUserId.set(terminationCase.userId as string, terminationCase);
        }
      });
  }

  private terminationMarkerPriority(terminationCase: PayrollEmployeeTerminationCase): number {
    if (this.terminationReviewLabel(terminationCase) === 'Termination review') {
      return 0;
    }
    if (this.terminationReviewLabel(terminationCase) === 'Manual review') {
      return 1;
    }
    return 2;
  }

  private mergeTerminationCaseSnapshots(
    existingItems: PayrollEmployeeTerminationCase[],
    incomingItems: PayrollEmployeeTerminationCase[],
    allowedStatuses: PayrollTerminationLifecycleStatus[] = ACTIVE_TERMINATION_STATUSES,
  ): PayrollEmployeeTerminationCase[] {
    const byId = new Map<string, PayrollEmployeeTerminationCase>();
    [...existingItems, ...incomingItems]
      .filter((terminationCase) => (
        !!terminationCase?.id
        && allowedStatuses.includes(terminationCase.lifecycleStatus)
      ))
      .forEach((terminationCase) => {
        const existing = byId.get(terminationCase.id);
        byId.set(
          terminationCase.id,
          existing ? this.preferredTerminationSnapshot(existing, terminationCase) : terminationCase,
        );
      });
    return [...byId.values()];
  }

  private preferredTerminationSnapshot(
    left: PayrollEmployeeTerminationCase,
    right: PayrollEmployeeTerminationCase,
  ): PayrollEmployeeTerminationCase {
    const leftUpdatedAt = this.validTerminationTimestamp(left.updatedAt);
    const rightUpdatedAt = this.validTerminationTimestamp(right.updatedAt);
    if (leftUpdatedAt !== null || rightUpdatedAt !== null) {
      if (leftUpdatedAt === null) {
        return right;
      }
      if (rightUpdatedAt === null) {
        return left;
      }
      if (leftUpdatedAt !== rightUpdatedAt) {
        return leftUpdatedAt > rightUpdatedAt ? left : right;
      }
    }

    const priorityDifference = this.terminationSnapshotPriority(left) - this.terminationSnapshotPriority(right);
    if (priorityDifference !== 0) {
      return priorityDifference < 0 ? left : right;
    }
    return this.terminationSnapshotDeterministicKey(left)
      .localeCompare(this.terminationSnapshotDeterministicKey(right)) <= 0
      ? left
      : right;
  }

  private validTerminationTimestamp(value: string | null): number | null {
    const timestamp = Date.parse(`${value || ''}`);
    return Number.isFinite(timestamp) ? timestamp : null;
  }

  private terminationSnapshotPriority(terminationCase: PayrollEmployeeTerminationCase): number {
    if (this.terminationReviewLabel(terminationCase) === 'Termination review') {
      return 0;
    }
    if (this.terminationReviewLabel(terminationCase) === 'Manual review') {
      return terminationCase.lifecycleStatus === 'processing' ? 3 : 1;
    }
    if (terminationCase.lifecycleStatus === 'scheduled') {
      return 2;
    }
    return 4;
  }

  private terminationSnapshotDeterministicKey(terminationCase: PayrollEmployeeTerminationCase): string {
    return [
      terminationCase.lifecycleStatus,
      terminationCase.classification || '',
      terminationCase.matchStatus,
      terminationCase.userId || '',
      terminationCase.safeErrorCode || '',
    ].join('|');
  }

  private sortTerminationCases(items: PayrollEmployeeTerminationCase[]): PayrollEmployeeTerminationCase[] {
    const statusPriority: Record<PayrollTerminationLifecycleStatus, number> = {
      needs_review: 0,
      scheduled: 1,
      blocked: 2,
      processing: 3,
      completed: 4,
      dismissed: 5,
    };
    return [...items].sort((left, right) => {
      const priorityDifference = statusPriority[left.lifecycleStatus] - statusPriority[right.lifecycleStatus];
      if (priorityDifference) {
        return priorityDifference;
      }
      return `${right.updatedAt || ''}`.localeCompare(`${left.updatedAt || ''}`);
    });
  }

  private updateTerminationPage(
    status: ActiveTerminationStatus,
    response: PayrollEmployeeTerminationListResponse,
  ): void {
    this.terminationPages[status] = {
      offset: response.pagination.offset + response.pagination.count,
      hasMore: response.pagination.hasMore,
    };
  }

  private isCurrentTerminationRequest(
    companyId: string,
    requestVersion: number,
    userId?: string,
  ): boolean {
    return requestVersion === this.terminationRequestVersion
      && companyId === `${this.currentCompany?.id || ''}`.trim()
      && (!userId || userId === `${this.sessionService.getUserId() || ''}`.trim());
  }

  private isUnavailableTerminationError(error: any): boolean {
    const status = Number(error?.status ?? error?.statusCode ?? error?.data?.statusCode ?? 0);
    return status === 403 || status === 404;
  }

  private resetTerminationPages(): void {
    this.terminationPages = {
      needs_review: {offset: 0, hasMore: false},
      scheduled: {offset: 0, hasMore: false},
      blocked: {offset: 0, hasMore: false},
      processing: {offset: 0, hasMore: false},
    };
  }

  private resetTerminationReviewState(): void {
    this.terminationRequestVersion++;
    this.terminationReviewDialogRef?.close();
    this.terminationReviewDialogRef = undefined;
    this.isTerminationFeatureEnabled = false;
    this.isTerminationProcessingEnabled = false;
    this.isTerminationDeductionCancellationWritesEnabled = false;
    this.isTerminationReviewLoading = false;
    this.isTerminationReviewLoadingMore = false;
    this.isTerminationReviewListOpen = false;
    this.terminationReviewLoadError = '';
    this.terminationCases = [];
    this.clearResolvedHistoryState();
    this.terminationCaseByUserId.clear();
    this.terminationMembersByUserId.clear();
    this.clearGuidedTerminationState();
    this.resetTerminationPages();
  }

  private handleTerminalTerminationResult(companyId: string, caseId: string): void {
    if (companyId !== `${this.currentCompany?.id || ''}`.trim()) {
      return;
    }
    const reopenResolved = this.isResolvedHistoryOpen;
    this.terminationCases = this.terminationCases.filter((item) => item.id !== caseId);
    this.rebuildTerminationCaseLookup();
    if (this.guidedTerminationCaseId === caseId) {
      this.clearGuidedTerminationState();
    }
    void this.loadTerminationReviewForCurrentCompany({preserveOpenState: true, reopenResolved});
  }

  private async loadResolvedTerminationHistory(loadMore = false): Promise<void> {
    const companyId = `${this.currentCompany?.id || ''}`.trim();
    const userId = `${this.sessionService.getUserId() || ''}`.trim();
    if (!this.isResolvedHistoryOpen || !this.isTerminationFeatureEnabled || !companyId || !userId) {
      this.clearResolvedHistoryState();
      return;
    }
    const terminationRequestVersion = this.terminationRequestVersion;
    const requestVersion = ++this.resolvedHistoryRequestVersion;
    const statuses = loadMore
      ? RESOLVED_TERMINATION_STATUSES.filter((status) => this.resolvedTerminationPages[status].hasMore)
      : RESOLVED_TERMINATION_STATUSES;
    if (!statuses.length) {
      return;
    }
    if (loadMore) {
      this.isResolvedHistoryLoadingMore = true;
    } else {
      this.isResolvedHistoryLoading = true;
      this.resolvedHistoryLoadError = '';
      this.resolvedTerminationCases = [];
      this.resetResolvedTerminationPages();
    }
    try {
      const results = await Promise.all(statuses.map(async (status) => ({
        status,
        response: await this.payrollEmployeeTerminationsService.listCases(companyId, {
          lifecycleStatus: status,
          limit: TERMINATION_PAGE_LIMIT,
          offset: loadMore ? this.resolvedTerminationPages[status].offset : 0,
        }),
      })));
      if (
        requestVersion !== this.resolvedHistoryRequestVersion
        || terminationRequestVersion !== this.terminationRequestVersion
        || !this.isCurrentTerminationRequest(companyId, terminationRequestVersion, userId)
        || !this.isResolvedHistoryOpen
      ) {
        return;
      }
      const incomingItems: PayrollEmployeeTerminationCase[] = [];
      results.forEach(({status, response}) => {
        incomingItems.push(...response.items);
        this.resolvedTerminationPages[status] = {
          offset: response.pagination.offset + response.pagination.count,
          hasMore: response.pagination.hasMore,
        };
      });
      this.resolvedTerminationCases = this.sortTerminationCases(this.mergeTerminationCaseSnapshots(
        loadMore ? this.resolvedTerminationCases : [],
        incomingItems,
        RESOLVED_TERMINATION_STATUSES,
      ));
      await this.hydrateTerminationMemberDisplays(companyId, terminationRequestVersion, userId);
    } catch (error: any) {
      if (
        requestVersion !== this.resolvedHistoryRequestVersion
        || terminationRequestVersion !== this.terminationRequestVersion
      ) {
        return;
      }
      if (this.isUnavailableTerminationError(error)) {
        this.resetTerminationReviewState();
        return;
      }
      this.resolvedHistoryLoadError = 'Unable to load resolved termination history right now.';
    } finally {
      if (requestVersion === this.resolvedHistoryRequestVersion) {
        this.isResolvedHistoryLoading = false;
        this.isResolvedHistoryLoadingMore = false;
      }
    }
  }

  private clearResolvedHistoryState(): void {
    this.resolvedHistoryRequestVersion++;
    this.isResolvedHistoryOpen = false;
    this.isResolvedHistoryLoading = false;
    this.isResolvedHistoryLoadingMore = false;
    this.resolvedHistoryLoadError = '';
    this.resolvedTerminationCases = [];
    this.resetResolvedTerminationPages();
  }

  private resetResolvedTerminationPages(): void {
    this.resolvedTerminationPages = {
      completed: {offset: 0, hasMore: false},
      dismissed: {offset: 0, hasMore: false},
    };
  }

  private async ensureAllEmployeesLoaded(): Promise<void> {
    if (!this.canUseLegacyCensus) return;
    if (this.isDestroyed || !this.currentCompany?.id) {
      return;
    }

    const contextKey = this.getAllEmployeesContextKey();
    this.activateAllEmployeesContext(contextKey);
    const desiredLoadMode = this.canUseProviderCensus ? 'provider' : 'fallback';
    if (
      this.allEmployeesLoaded
      && this.allEmployeesLoadedContextKey === contextKey
      && this.allEmployeesLoadMode === desiredLoadMode
    ) {
      return;
    }
    if (
      this.allEmployeesLoadPromise
      && this.allEmployeesLoadContextKey === contextKey
    ) {
      await this.allEmployeesLoadPromise;
      return;
    }

    const request = this.captureAllEmployeesLoadRequest(contextKey);
    this.allEmployeesLoadContextKey = contextKey;

    const loadPromise = (async () => {
      this.clearAllEmployeesConnectionError();
      if (request.canUseProviderCensus) {
        try {
          await this.census.init({
            company: request.company,
            providerConnection: request.providerConnection,
            teamsMap: request.teamsMap,
            selectedEmployeeIds: [],
          });
          if (!this.isCurrentAllEmployeesLoad(request)) {
            return;
          }
          this.allEmployeesLoadMode = 'provider';
          this.allEmployeesLoadedContextKey = request.contextKey;
          this.allEmployeesLoaded = true;
          if (this.getPayrollProviderSlug(request.providerConnection) === UKG_READY_PROVIDER_SLUG) {
            await this.loadTerminationReviewForCurrentCompany({
              preserveOpenState: true,
              reopenResolved: true,
            });
          }
        } catch (error: any) {
          if (!this.isCurrentAllEmployeesLoad(request)) {
            return;
          }
          this.allEmployeesLoadMode = '';
          this.allEmployeesLoadedContextKey = '';
          this.allEmployeesLoaded = false;
          this.setAllEmployeesConnectionError(error);
        }
        return;
      }

      try {
        const employees = await this.teamsService.getPayrollUsersForCompany(request.company.id);
        if (!this.isCurrentAllEmployeesLoad(request)) {
          return;
        }
        this.census.availableEmployees = Array.isArray(employees) ? employees : [];
        this.census.availableFilters = [];
        this.census.originalAvailableFilters = {};
        this.census.originalFilterKeys = {};
        this.census.activeFilters = [];
        this.census.activeFiltersMap = {};
        this.census.selected = new Set<string>();
        this.census.selectedEmails = {};
        this.census.isLoadingInitialData = false;
        this.allEmployeesLoadMode = 'fallback';
        this.allEmployeesLoadedContextKey = request.contextKey;
        this.allEmployeesLoaded = true;
      } catch (error: any) {
        if (!this.isCurrentAllEmployeesLoad(request)) {
          return;
        }
        this.allEmployeesLoadMode = '';
        this.allEmployeesLoadedContextKey = '';
        this.allEmployeesLoaded = false;
        this.setAllEmployeesConnectionError(error);
      }
    });

    const trackedPromise = loadPromise().finally(() => {
      if (
        this.allEmployeesLoadPromise === trackedPromise
        && this.isCurrentAllEmployeesLoad(request)
      ) {
        this.allEmployeesLoadPromise = undefined;
        this.allEmployeesLoadContextKey = '';
      }
    });
    this.allEmployeesLoadPromise = trackedPromise;

    await trackedPromise;
  }

  private invalidateSelectedTeamDetails(): void {
    this.selectedTeamDetailsRequestVersion++;
    this.selectedTeamDetailsLoadedTeamId = '';
    this.clearSelectedTeamDetailPresentation();
  }

  private clearSelectedTeamDetailPresentation(): void {
    this.rewardsActivityRequestId++;
    this.selectedTeamAutoAddEligible = false;
    this.selectedTeamAutoAddReason = 'Select a team to configure auto-add.';
    this.isAutoAddEligibilityLoading = false;
    this.selectedTeamFundingCandidates = [];
    this.isFundingCandidatesLoading = false;
    this.selectedTeamAttachableFundingMethod = null;
    this.automatedBonusHistory = [];
    this.oneTimeRewardsHistory = [];
    this.rewardsActivityError = '';
    this.isRewardsActivityLoading = false;
    this.resetSelectedTeamSnackRewards();
  }

  private isCurrentSelectedTeamRequest(teamId: string, requestVersion: number): boolean {
    return requestVersion === this.selectedTeamDetailsRequestVersion
      && teamId === `${this.selectedTeam?.id || ''}`.trim();
  }

  private async ensureSelectedTeamDetailsLoaded(force = false): Promise<void> {
    const teamId = `${this.selectedTeam?.id || ''}`.trim();
    if (!teamId) {
      this.selectedTeamDetailsLoadedTeamId = '';
      return;
    }

    if (!force && this.selectedTeamDetailsLoadedTeamId === teamId) {
      return;
    }

    if (
      this.selectedTeamDetailsLoadPromise
      && this.selectedTeamDetailsLoadingTeamId === teamId
    ) {
      await this.selectedTeamDetailsLoadPromise;
      if (!force && this.selectedTeamDetailsLoadedTeamId === teamId) {
        return;
      }
    }

    const requestVersion = this.selectedTeamDetailsRequestVersion;
    const loadPromise = this.loadSelectedTeamDetails(teamId, requestVersion)
      .finally(() => {
        if (this.selectedTeamDetailsLoadPromise !== loadPromise) {
          return;
        }
        this.selectedTeamDetailsLoadPromise = undefined;
        this.selectedTeamDetailsLoadingTeamId = '';
      });
    this.selectedTeamDetailsLoadPromise = loadPromise;
    this.selectedTeamDetailsLoadingTeamId = teamId;

    await loadPromise;
  }

  private async loadSelectedTeamDetails(teamId: string, requestVersion: number): Promise<void> {
    if (!this.canUseLegacyCensus) {
      const members = await this.teamsService.getTeamMembersWithDailyLoginInfo(teamId);
      if (!this.isCurrentSelectedTeamRequest(teamId, requestVersion)) return;
      const team = this.teams.find(candidate => candidate.id === teamId);
      if (team) { team.teamMembers = members; team.membersCount = members.length; }
    }
    await Promise.all([
      this.refreshSelectedTeamAutoAddEligibility(),
      this.refreshSelectedTeamBonusAmounts(),
      this.refreshSelectedTeamRewardPaymentMethod(),
      this.refreshSelectedTeamRewardsActivity(),
      this.refreshSelectedTeamFundingCandidates(),
      this.refreshSelectedTeamSnackRewards(true),
    ]);

    if (!this.isCurrentSelectedTeamRequest(teamId, requestVersion)) {
      return;
    }

    await this.refreshSelectedTeamAttachableFundingMethod();

    if (this.isCurrentSelectedTeamRequest(teamId, requestVersion)) {
      this.selectedTeamDetailsLoadedTeamId = teamId;
    }
  }

  // -------------------------
  // Team Details tab helpers
  // -------------------------
  get selectedTeam(): Team | undefined {
    return this.teams.find((t) => t.id === this.selectedTeamId);
  }

  /** Derived from payrollConfig.teamsMap[teamId].originalFilters.fieldsContain (same source as EditTeamDialogComponent). */
  get selectedTeamFilterChips(): TeamFilterChip[] {
    const team: any = this.selectedTeam;
    if (!team?.id) {
      return [];
    }

    const raw = this.teamsMap?.[team.id]?.originalFilters?.fieldsContain || {};
    const chips: TeamFilterChip[] = [];

    for (const [rawKey, rawValues] of Object.entries(raw)) {
      const label = this.extractFilterKey(rawKey);
      const values = Array.isArray(rawValues) ? rawValues : [rawValues];

      for (const v of values) {
        const value = `${v ?? ''}`.trim();
        if (!value) {
          continue;
        }

        // Employment/Employee Type should be shown as a standalone tag (e.g. "Full-time").
        const displayLabel = this.normalizeFilterLabel(label);
        const display = displayLabel ? `${displayLabel}: ${value}` : value;

        chips.push({ label: displayLabel || label, value, display });
      }
    }

    return chips.sort((a, b) => (a.display || '').localeCompare(b.display || ''));
  }

  get selectedTeamAutoAddTooltip(): string {
    if (!this.selectedTeamAutoAddEligible) {
      return this.selectedTeamAutoAddReason;
    }
    return 'When enabled, any new team member added in your payroll system who meets the defined filters will be automatically added to this team.';
  }

  get selectedTeamMembers(): any[] {
    const members = (this.selectedTeam as any)?.teamMembers ?? [];
    return Array.isArray(members) ? members.map((member) => this.enrichSelectedTeamMember(member)) : [];
  }

  get selectedTeamWelcomeAmount(): number {
    return this.getSelectedTeamBonusAmounts().welcome;
  }

  get selectedTeamBirthdayAmount(): number {
    return this.getSelectedTeamBonusAmounts().birthday;
  }

  get selectedTeamAnniversaryAmount(): number {
    return this.getSelectedTeamBonusAmounts().anniversary;
  }

  get shouldShowSelectedTeamFundingTiming(): boolean {
    return Number(this.selectedTeam?.amount || 0) > 0;
  }

  get selectedTeamFundingMethod(): any | null {
    const teamId = this.selectedTeam?.id;
    if (!teamId) {
      return null;
    }
    return this.normalizeSelectedTeamFundingMethod(
      this.teamRewardPaymentMethods[teamId],
      this.selectedTeam?.defaultPaymentMethod,
    );
  }

  get selectedTeamOneTimeRewardDisabledReason(): string {
    const baseReason = this.getSelectedTeamRewardBaseDisabledReason();
    if (baseReason) {
      return baseReason;
    }
    return '';
  }

  get selectedTeamPaymentRewardDisabledReason(): string {
    const baseReason = this.getSelectedTeamRewardBaseDisabledReason();
    if (baseReason) {
      return baseReason;
    }
    if (!this.hasValidSelectedTeamRewardPaymentMethod()) {
      return 'Add or verify a team funding method before using this reward action.';
    }
    return '';
  }

  get canSendOneTimeRewardsToSelectedTeam(): boolean {
    return !this.selectedTeamOneTimeRewardDisabledReason;
  }

  get canUploadRewardsToSelectedTeam(): boolean {
    return !this.selectedTeamPaymentRewardDisabledReason;
  }

  get canTopUpSelectedTeamP2p(): boolean {
    return !this.selectedTeamPaymentRewardDisabledReason;
  }

  get selectedTeamFundingTimingValue(): 'fund_now' | 'next_month' | null {
    if (!this.shouldShowSelectedTeamFundingTiming) {
      return null;
    }
    return this.isTeamFundingProrated(this.selectedTeam) ? 'fund_now' : 'next_month';
  }

  get canEditSelectedTeamFundingTiming(): boolean {
    if (!this.shouldShowSelectedTeamFundingTiming) {
      return false;
    }
    return !!this.selectedTeamFundingMethod && !this.selectedTeamFundingMethod?.bankInfo?.isPending;
  }

  get shouldShowSelectedTeamFundingTimingControls(): boolean {
    return this.canEditSelectedTeamFundingTiming;
  }

  get selectedTeamFundingCandidateCount(): number {
    return this.selectedTeamFundingCandidates.length;
  }

  get shouldShowSelectedTeamFundingPrompt(): boolean {
    return this.shouldShowSelectedTeamFundingTiming && this.selectedTeamFundingCandidateCount > 0;
  }

  get canFundSelectedTeamAllowancesNow(): boolean {
    return this.shouldShowSelectedTeamFundingPrompt
      && this.canEditSelectedTeamFundingTiming
      && !this.isFundingMembersLoading
      && !this.isFundingCandidatesLoading;
  }

  get selectedTeamFundingMethodLabel(): string {
    const paymentMethod = this.selectedTeamFundingMethod;
    if (!paymentMethod) {
      return 'None';
    }

    if (paymentMethod?.bankInfo?.type === 'invoice') {
      return 'Invoice';
    }
    if (`${paymentMethod?.bankInfo?.bankName || ''}`.trim().toLowerCase() === 'invoice') {
      return 'Invoice';
    }

    const bankName = `${paymentMethod?.bankInfo?.bankName || 'Funding method'}`.trim();
    const last4 = `${paymentMethod?.stripeLast4 || ''}`.trim();
    return last4 ? `${bankName} ****${last4}` : bankName;
  }

  get selectedTeamFundingTimingDisabledReason(): string {
    if (!this.shouldShowSelectedTeamFundingTiming) {
      return '';
    }
    if (!this.selectedTeamFundingMethod) {
      return 'No verified funding method attached yet.';
    }
    if (this.selectedTeamFundingMethod?.bankInfo?.isPending) {
      return 'Funding method pending verification.';
    }
    return '';
  }

  get selectedTeamFundingPromptMessage(): string {
    const count = this.selectedTeamFundingCandidateCount;
    if (!count) {
      return '';
    }
    return `${count} team member${count === 1 ? '' : 's'} need funding before they can start receiving the monthly allowance.`;
  }

  get selectedTeamFundingPromptHelper(): string {
    if (!this.shouldShowSelectedTeamFundingPrompt) {
      return '';
    }
    if (!this.selectedTeamFundingMethod) {
      return 'Add a verified funding method before member allowances can be funded.';
    }
    if (this.selectedTeamFundingMethod?.bankInfo?.isPending) {
      return 'Verify the current funding method before member allowances can be funded.';
    }
    return '';
  }

  get selectedTeamFundingTimingSummary(): string {
    if (!this.selectedTeamFundingMethod) {
      return 'No funding method';
    }
    return this.selectedTeamFundingTimingValue === 'next_month' ? 'Starts next cycle' : 'Funds right away';
  }

  get canUseCompanyFundingMethod(): boolean {
    return !this.selectedTeamFundingMethod && !!this.selectedTeamAttachableFundingMethod;
  }

  get selectedTeamFundingMethodCtaLabel(): string {
    if (this.canUseCompanyFundingMethod) {
      return 'Use company funding method';
    }
    if (!this.selectedTeamFundingMethod && !this.isCompanyScopedWorkspace) {
      return 'Connect funding method';
    }
    return '';
  }

  get selectedTeamAvailableProductsCount(): number {
    const team = this.selectedTeam;
    if (!team) {
      return 0;
    }

    const selectedProductIds = this.payrollBenefitsAssignmentService.getSelectedMappedBenefitProductIds(
      {
        ...(this.currentCompany || {}),
        groupProductOnboardingRows: this.groupProductOnboardingRows,
      },
      team
    );
    if (!this.isCompanyScopedWorkspace) {
      return selectedProductIds.length;
    }

    const availableProductIds = new Set(
      this.activeEmployerCompanyService.state.benefitsReadyProductIds,
    );
    return selectedProductIds.filter((productId) => availableProductIds.has(productId)).length;
  }

  get selectedTeamReimbursedCount(): number {
    const team: any = this.selectedTeam;
    const standardCategoryCount = Array.isArray(team?.categories) ? team.categories.length : 0;
    const customCategoryCount = Array.isArray(team?.metadata?.customReimbursementCategories)
      ? team.metadata.customReimbursementCategories.filter((category: any) => {
        const name = `${category?.name || ''}`.trim();
        const maxAmountCents = Number(category?.maxAmountCents);
        return !!name && Number.isFinite(maxAmountCents) && maxAmountCents > 0 && !category?.archived;
      }).length
      : 0;

    return standardCategoryCount + customCategoryCount;
  }

  get selectedTeamSnackSelectedIds(): string[] {
    if (!this.selectedTeam?.id) {
      return [];
    }
    return this.payrollSnacksAssignmentService.getSelectedSnackRewardIds(
      this.selectedTeamSnackRewardIdsInOrder,
      this.selectedTeam,
    );
  }

  get selectedTeamSnackPreviewRewards(): PayrollSnackRewardItem[] {
    const selectedIds = new Set(this.selectedTeamSnackSelectedIds);
    return this.selectedTeamSnackRewards
      .filter((reward) => selectedIds.has(reward.id))
      .slice(0, 3);
  }

  get selectedTeamSnackSummaryTitle(): string {
    if (this.isSelectedTeamSnacksLoading) {
      return 'Loading snacks';
    }
    if (this.selectedTeamSnackLoadError) {
      return 'Unable to load snacks';
    }

    const count = this.selectedTeamSnackSelectedIds.length;
    return count
      ? `${count} snack${count === 1 ? '' : 's'} enabled`
      : 'No snacks enabled';
  }

  get selectedTeamSnackSummarySubtitle(): string {
    if (this.isSelectedTeamSnacksLoading) {
      return 'Checking company-available snack rewards.';
    }
    if (this.selectedTeamSnackLoadError) {
      return this.selectedTeamSnackLoadError;
    }

    if (!this.selectedTeamSnackRewardIdsInOrder.length) {
      return 'No company-available snacks are currently configured.';
    }

    const previewNames = this.selectedTeamSnackPreviewRewards
      .map((reward) => reward.title)
      .filter((title) => !!title)
      .join(', ');
    return previewNames || 'Choose snacks to make them visible to this team.';
  }

  memberDisplayName(member: any): string {
    const first = member?.['First Name'] ?? member?.FirstName ?? member?.firstName ?? '';
    const last = member?.['Last Name'] ?? member?.LastName ?? member?.lastName ?? '';
    const combined = `${first} ${last}`.trim();
    return combined || member?.name || member?.email || member?.['Primary Email'] || member?.PrimaryEmail || '—';
  }

  memberStatus(member: any): string {
    if (this.isMemberDeactivationPendingSettlement(member)) {
      return 'Deactivation pending settlement';
    }
    return (
      member?.message ||
      member?.employeeStatus ||
      member?.EmployeeStatus ||
      member?.employmentStatus ||
      member?.EmploymentStatus ||
      member?.['Employee Status'] ||
      this.getMemberInviteStatus(member) ||
      '—'
    );
  }

  private getMemberInviteStatus(member: any): string {
    if (this.hasCompletedMemberAccount(member) || member?.inviteAcceptedAt) {
      return 'Active';
    }
    if (member?.inviteSentAt) {
      return 'Pending Sign Up';
    }
    return 'Pending Invitation';
  }

  private hasCompletedMemberAccount(member: any): boolean {
    return !!member?.userId
      && !!`${member?.firstName || member?.['First Name'] || ''}`.trim()
      && !!`${member?.lastName || member?.['Last Name'] || ''}`.trim()
      && this.isTruthyFlag(member?.userIsActivatedUser ?? member?.isActivatedUser)
      && this.isTruthyFlag(member?.userIsCompleted ?? member?.isCompleted)
      && !this.isTruthyFlag(member?.userIsPendingInvite ?? member?.isPendingInvite);
  }

  private isTruthyFlag(value: any): boolean {
    return value === true || value === 1 || value === '1' || value === 'true';
  }

  memberSummary(member: any): string {
    const values = [
      member?.location || member?.Location || member?.WorkLocation || member?.['Work Location'] || member?.['Default Location Full Path'] || member?.['Location'] || '',
      member?.department || member?.['Default Department Full Path'] || member?.['Department'] || member?.dept || '',
      member?.division || member?.['Default Division Full Path'] || member?.['Division'] || '',
      member?.jobTitle || member?.JobTitle || member?.['Job Title'] || member?.['Payroll Job Title'] || '',
      member?.manager || member?.Manager || '',
      member?.payGroup || member?.PayGroup || member?.['Pay Group'] || '',
      member?.workerType || member?.WorkerType || member?.['Worker Type'] || '',
      member?.payType || member?.['Pay Type'] || '',
      member?.employeeType || member?.['Employee Type'] || member?.employmentType || member?.['Employment Type'] || member?.employment_type || '',
      member?.state || member?.['State'] || '',
    ]
      .map((value) => `${value || ''}`.trim())
      .filter((value) => !!value && value !== '—');

    return Array.from(new Set(values)).join(' • ');
  }

  memberEmployeeNumber(member: any): string {
    return member?.employeeNumber || member?.employeeId || member?.['Employee Id'] || '—';
  }

  memberBirthDate(member: any): string | null {
    return member?.teamuserMetadata?.birthDate ?? member?.metadata?.birthDate ?? null;
  }

  memberEmploymentDate(member: any): string | null {
    return member?.employmentDate || null;
  }

  memberJoinDate(member: any): string | null {
    return member?.memberCreatedAt || null;
  }

  memberDepartment(member: any): string {
    return (
      member?.department ||
      member?.Department ||
      member?.['Default Department Full Path'] ||
      member?.['Department'] ||
      member?.dept ||
      '—'
    );
  }

  memberLocation(member: any): string {
    return member?.location || member?.Location || member?.WorkLocation || member?.['Work Location'] || member?.['Default Location Full Path'] || member?.['Location'] || '—';
  }

  memberEmploymentType(member: any): string {
    return member?.workerType || member?.WorkerType || member?.['Worker Type'] || member?.employeeType || member?.['Employee Type'] || member?.employmentType || member?.['Employment Type'] || member?.employment_type || '—';
  }

  memberPayType(member: any): string {
    return member?.payType || member?.['Pay Type'] || '—';
  }

  trackByMember = (_: number, m: any): string => {
    return m?.id || m?.employeeId || m?.['Employee Id'] || m?.['Primary Email'] || m?.email || `${_}`;
  };

  private extractFilterKey(key: string): string {
    return `${key}`.replace('Default ', '').replace(' Full Path', '').trim();
  }

  private normalizeFilterLabel(label: string): string {
    const normalized = (label || '').trim().toLowerCase();
    if (
      normalized === 'employee type' ||
      normalized === 'employeetype' ||
      normalized === 'employment type' ||
      normalized === 'employmenttype'
    ) {
      return '';
    }
    if (normalized === 'department') {
      return 'Dept';
    }
    return label;
  }

  private normalizeEmail(email: string | undefined | null): string {
    return `${email || ''}`.trim().toLowerCase();
  }

  private normalizeNameKey(firstName: string | undefined | null, lastName: string | undefined | null): string {
    const first = `${firstName || ''}`.trim().toLowerCase();
    const last = `${lastName || ''}`.trim().toLowerCase();
    if (!first && !last) {
      return '';
    }
    return `${first}|${last}`;
  }

  private refreshCensusEmployeeIndexes(): void {
    if (this.censusEmployeesIndexSource === this.availableEmployees) {
      return;
    }

    this.censusEmployeesIndexSource = this.availableEmployees;
    this.censusEmployeeByEmail = new Map<string, any>();
    this.censusEmployeeByName = new Map<string, any | null>();

    for (const employee of this.availableEmployees || []) {
      const email = this.normalizeEmail(
        employee?.['Primary Email'] ||
        employee?.PrimaryEmail ||
        employee?.primaryEmail ||
        employee?.email ||
        employee?.userEmail
      );
      if (email && !this.censusEmployeeByEmail.has(email)) {
        this.censusEmployeeByEmail.set(email, employee);
      }

      const first = employee?.['First Name'] ?? employee?.FirstName ?? employee?.firstName;
      const last = employee?.['Last Name'] ?? employee?.LastName ?? employee?.lastName;
      const nameKey = this.normalizeNameKey(first, last);
      if (!nameKey) {
        continue;
      }

      if (!this.censusEmployeeByName.has(nameKey)) {
        this.censusEmployeeByName.set(nameKey, employee);
      } else {
        this.censusEmployeeByName.set(nameKey, null);
      }
    }
  }

  private findMatchingCensusEmployee(member: any): any | null {
    this.refreshCensusEmployeeIndexes();

    const email = this.normalizeEmail(this.memberEmail(member));
    if (email && this.censusEmployeeByEmail.has(email)) {
      return this.censusEmployeeByEmail.get(email) || null;
    }

    const nameKey = this.normalizeNameKey(
      member?.firstName || member?.['First Name'],
      member?.lastName || member?.['Last Name'],
    );
    if (nameKey && this.censusEmployeeByName.has(nameKey)) {
      return this.censusEmployeeByName.get(nameKey) || null;
    }

    return null;
  }

  private enrichSelectedTeamMember(member: any): any {
    const censusEmployee = this.findMatchingCensusEmployee(member);
    if (!censusEmployee) {
      return member;
    }

    return {
      ...censusEmployee,
      ...member,
    };
  }

  isMemberDeactivationPendingSettlement(member: any): boolean {
    return this.teamMemberRemovalCoordinator.isDeactivationPendingSettlement(member);
  }

  private normalizeFilterKey(filterKey: string): string {
    return `${filterKey || ''}`.toLowerCase().replace(/[^a-z0-9]/g, '');
  }

  private extractEmployeeFilterValue(source: any, rawFilterKey: string): string {
    if (!source) {
      return '';
    }

    const normalizedFilterKey = this.normalizeFilterKey(rawFilterKey);
    const commonFilterValueKeys: Record<string, string[]> = {
      location: ['location', 'Default Location Full Path', 'Location'],
      division: ['division', 'Default Division Full Path', 'Division'],
      department: ['department', 'Default Department Full Path', 'Department', 'dept'],
      state: ['state', 'State'],
      paytype: ['payType', 'Pay Type'],
      employeetype: ['employeeType', 'Employee Type', 'employmentType', 'Employment Type', 'employment_type'],
      employmenttype: ['employmentType', 'Employment Type', 'employeeType', 'Employee Type', 'employment_type'],
      employeestatus: ['Employee Status', 'EmployeeStatus', 'employeeStatus', 'Employment Status', 'EmploymentStatus', 'employmentStatus', 'Source Status', 'SourceStatus', 'sourceStatus', 'status'],
      employmentstatus: ['Employment Status', 'EmploymentStatus', 'employmentStatus', 'Employee Status', 'EmployeeStatus', 'employeeStatus', 'Source Status', 'SourceStatus', 'sourceStatus', 'status'],
      sourcestatus: ['Source Status', 'SourceStatus', 'sourceStatus', 'Employee Status', 'EmployeeStatus', 'employeeStatus', 'Employment Status', 'EmploymentStatus', 'employmentStatus', 'status'],
      status: ['Employee Status', 'EmployeeStatus', 'employeeStatus', 'Employment Status', 'EmploymentStatus', 'employmentStatus', 'Source Status', 'SourceStatus', 'sourceStatus', 'status'],
      manager: ['manager', 'Manager'],
      paygroup: ['payGroup', 'PayGroup', 'Pay Group'],
      flsa: ['flsa', 'FLSA', 'Flsa', 'FLSA Type', 'flsaType'],
      statustype: ['statusType', 'StatusType', 'Status Type', 'employmentType', 'EmploymentType', 'Employment Type'],
      workertype: ['workerType', 'WorkerType', 'Worker Type', 'employeeType', 'Employee Type', 'employmentType', 'Employment Type', 'employment_type'],
      jobtitle: ['jobTitle', 'JobTitle', 'Job Title', 'Payroll Job Title'],
    };

    const titleCaseKey = rawFilterKey
      .split(' ')
      .map((part) => (part ? `${part[0].toUpperCase()}${part.slice(1)}` : ''))
      .join(' ');
    const camelCaseKey = rawFilterKey
      .split(' ')
      .map((part, index) => {
        if (!part) {
          return '';
        }
        const lower = part.toLowerCase();
        return index === 0 ? lower : `${lower[0].toUpperCase()}${lower.slice(1)}`;
      })
      .join('');
    const snakeCaseKey = rawFilterKey
      .split(' ')
      .filter(Boolean)
      .map((part) => part.toLowerCase())
      .join('_');

    const candidates = [
      ...(commonFilterValueKeys[normalizedFilterKey] || []),
      rawFilterKey,
      titleCaseKey,
      camelCaseKey,
      snakeCaseKey,
      `Default ${titleCaseKey} Full Path`,
    ];

    for (const key of candidates) {
      const value = `${source?.[key] ?? ''}`.trim();
      if (value) {
        return value;
      }
    }

    return '';
  }

  private resolveInviteFilterValue(rawFilterKey: string, member: any, censusEmployee: any): string {
    const memberValue = this.extractEmployeeFilterValue(member, rawFilterKey);
    if (memberValue) {
      return memberValue;
    }
    return this.extractEmployeeFilterValue(censusEmployee, rawFilterKey);
  }

  private getValueFromFilterMapByNormalizedKey(
    filterValues: Record<string, string>,
    normalizedKey: string,
  ): string {
    for (const [key, value] of Object.entries(filterValues || {})) {
      if (this.normalizeFilterKey(key) === normalizedKey) {
        const trimmed = `${value || ''}`.trim();
        if (trimmed) {
          return trimmed;
        }
      }
    }
    return '';
  }

  private isEmployeeActive(employee: any): boolean {
    const status = `${employee?.['Employee Status']
      ?? employee?.EmployeeStatus
      ?? employee?.['Employment Status']
      ?? employee?.EmploymentStatus
      ?? employee?.['Source Status']
      ?? employee?.SourceStatus
      ?? employee?.employeeStatus
      ?? employee?.employmentStatus
      ?? employee?.sourceStatus
      ?? employee?.status
      ?? ''}`.trim().toLowerCase();
    return status === 'active' || status === '';
  }

  private getTeamMemberEmailSet(team: any): Set<string> {
    const teamMembers = Array.isArray(team?.teamMembers) ? team.teamMembers : [];
    const emails = teamMembers
      .map((member: any) => this.normalizeEmail(member?.email || member?.userEmail || member?.['Primary Email']))
      .filter((email: string) => !!email);
    return new Set(emails);
  }

  private getFilteredEmployeeEmailSet(filteredCensus: any[]): Set<string> {
    const emails = (filteredCensus || [])
      .filter((employee) => this.isEmployeeActive(employee))
      .map((employee) => this.normalizeEmail(
        employee?.['Primary Email'] ||
        employee?.PrimaryEmail ||
        employee?.primaryEmail ||
        employee?.email ||
        employee?.userEmail
      ))
      .filter((email) => !!email);
    return new Set(emails);
  }

  private getSavedCriteriaEmailSet(criteriaSnapshot: any): Set<string> {
    const emails = (this.availableEmployees || [])
      .filter((employee) => this.isEmployeeActive(employee))
      .filter((employee) => this.employeeMatchesSavedCriteria(employee, criteriaSnapshot))
      .map((employee) => this.normalizeEmail(this.employeeEmailValue(employee)))
      .filter((email) => !!email);
    return new Set(emails);
  }

  private employeeMatchesSavedCriteria(employee: any, criteriaSnapshot: any): boolean {
    const criteria = Array.isArray(criteriaSnapshot?.criteria) ? criteriaSnapshot.criteria : [];
    if (!criteria.length) {
      return false;
    }

    const matchMode = `${criteriaSnapshot?.match || 'all'}`.toLowerCase() === 'any' ? 'any' : 'all';
    const matches = criteria.map((criterion: any) => this.savedCriterionMatchesEmployee(employee, criterion));
    return matchMode === 'any'
      ? matches.some((matchesCriterion: boolean) => matchesCriterion)
      : matches.every((matchesCriterion: boolean) => matchesCriterion);
  }

  private savedCriterionMatchesEmployee(employee: any, criterion: any): boolean {
    const employeeValue = this.extractEmployeeFilterValue(employee, `${criterion?.field || criterion?.label || ''}`)
      .toLowerCase();
    if (!employeeValue) {
      return false;
    }

    const rawValues = Array.isArray(criterion?.value) ? criterion.value : [criterion?.value];
    const criterionValues = rawValues
      .map((value: any) => `${value ?? ''}`.trim().toLowerCase())
      .filter((value: string) => !!value);
    return criterionValues.includes(employeeValue);
  }

  private areEmailSetsEqual(a: Set<string>, b: Set<string>): boolean {
    if (a.size !== b.size) {
      return false;
    }
    for (const email of a) {
      if (!b.has(email)) {
        return false;
      }
    }
    return true;
  }

  private isAutoAddEnabledForTeam(team: any, metadata: Record<string, any> = {}): boolean {
    return team?.autoAddNewMembers === true ||
      team?.metadata?.autoAddNewMembers === true ||
      metadata.autoAddNewMembers === true;
  }

  private hasSavedProviderFilters(filters: any): boolean {
    const fieldsContain = filters?.fieldsContain;
    const fieldsExclude = filters?.fieldsExclude;
    return !!(
      (fieldsContain && Object.keys(fieldsContain).length > 0) ||
      (fieldsExclude && Object.keys(fieldsExclude).length > 0)
    );
  }

  private async removedMemberMatchesProviderFilters(member: any, filters: any): Promise<boolean> {
    const removedEmail = this.normalizeEmail(this.memberEmail(member));
    if (!removedEmail || !this.currentCompany?.id) {
      return false;
    }

    const filteredCensus = await this.payrollService.getFilteredCensusForPayrollProviderCompany(
      this.currentCompany.id,
      {
        fieldsContain: filters?.fieldsContain,
        fieldsExclude: filters?.fieldsExclude,
      },
      this.payrollProviderConnection?.providerSlug || this.payrollProviderConnection?.connection?.providerSlug,
    );
    const filteredEmailSet = this.getFilteredEmployeeEmailSet(filteredCensus);
    return !filteredEmailSet.size || filteredEmailSet.has(removedEmail);
  }

  private async removedMemberMatchesSavedCriteria(member: any, criteriaSnapshot: any): Promise<boolean> {
    const removedEmail = this.normalizeEmail(this.memberEmail(member));
    if (!removedEmail) {
      return false;
    }

    await this.ensureAllEmployeesLoaded();
    const filteredEmailSet = this.getSavedCriteriaEmailSet(criteriaSnapshot);
    return !filteredEmailSet.size || filteredEmailSet.has(removedEmail);
  }

  private async shouldDisableAutoAddForMemberRemoval(team: any, member: any): Promise<boolean> {
    const metadata = this.getTeamSourceMetadata(team);
    const criteriaSnapshot = metadata.criteriaSnapshot || {};
    if (!team?.id || !this.isAutoAddEnabledForTeam(team, metadata)) {
      return false;
    }

    if (Array.isArray(criteriaSnapshot.criteria) && criteriaSnapshot.criteria.length > 0) {
      return this.removedMemberMatchesSavedCriteria(member, criteriaSnapshot);
    }

    const originalFilters = metadata.originalFilters || {};
    if (!this.hasSavedProviderFilters(originalFilters)) {
      return true;
    }

    try {
      return await this.removedMemberMatchesProviderFilters(member, originalFilters);
    } catch (_) {
      return true;
    }
  }

  private applySelectedTeamAutoAddState(team: any, autoAddNewMembers: boolean): void {
    team.autoAddNewMembers = autoAddNewMembers;
    team.metadata = {
      ...(team.metadata || {}),
      autoAddNewMembers,
    };

    if (team?.id && this.teamsMap?.[team.id]) {
      this.teamsMap[team.id] = {
        ...(this.teamsMap[team.id] || {}),
        autoAddNewMembers,
      };
    }
  }

  private async disableAutoAddForMemberRemoval(team: any): Promise<void> {
    const nextMetadata = {
      ...(team.metadata || {}),
      autoAddNewMembers: false,
    };
    await this.teamsService.modifyTeam(team.id, { metadata: nextMetadata });
    this.applySelectedTeamAutoAddState(team, false);
  }

  private getCurrentBrand(): string {
    const brand = this.usersService.me()?.brand || {};
    return this.normalizeBrandValue(
      brand.redirectUrl
      || brand.shortName
      || brand.brand
      || brand.name
      || brand.partnerId
      || ''
    );
  }

  private normalizeBrandValue(value: any): string {
    const normalized = `${value || ''}`.trim().toLowerCase();
    if (!normalized) {
      return '';
    }

    const withoutProtocol = normalized.replace(/^https?:\/\//, '');
    const shortName = withoutProtocol.includes('.') ? withoutProtocol.split('.')[0] : withoutProtocol;
    return shortName.replace(/[^a-z0-9_-]/g, '');
  }

  private async loadCompanySnackRewardSelectionFeatureFlag(): Promise<void> {
    if (this.isCompanyScopedWorkspace) {
      const state = await this.activeEmployerCompanyService.initialize();
      this.isCompanySnackRewardSelectionEnabled = state.hasSnacks;
      if (!state.hasSnacks) {
        this.resetSelectedTeamSnackRewards();
      }
      return;
    }

    if (this.companySnackRewardSelectionFeatureFlagRequest) {
      await this.companySnackRewardSelectionFeatureFlagRequest;
      return;
    }

    this.companySnackRewardSelectionFeatureFlagRequest = this.managedFeatureFlagsService.isEnabled(
      PAYROLL_ADMIN_COMPANY_SNACK_REWARD_SELECTION_FLAG,
      'team-admin',
      {
        userId: this.sessionService.getUserId() || undefined,
      },
    )
      .then((enabled) => {
        this.isCompanySnackRewardSelectionEnabled = enabled;
        if (!enabled) {
          this.resetSelectedTeamSnackRewards();
        }
      })
      .catch(() => {
        this.isCompanySnackRewardSelectionEnabled = false;
        this.resetSelectedTeamSnackRewards();
      })
      .finally(() => {
        this.companySnackRewardSelectionFeatureFlagRequest = undefined;
      });

    await this.companySnackRewardSelectionFeatureFlagRequest;
  }

  private async refreshSelectedTeamSnackRewards(force = false): Promise<void> {
    const companyId = `${this.currentCompany?.id || ''}`.trim();
    const teamId = `${this.selectedTeam?.id || ''}`.trim();
    if (!this.isCompanySnackRewardSelectionEnabled || !companyId || !teamId) {
      this.resetSelectedTeamSnackRewards();
      return;
    }

    if (!force && this.selectedTeamSnackRewardsLoadedTeamId === teamId) {
      return;
    }

    if (this.selectedTeamSnackRewardsLoadPromise) {
      await this.selectedTeamSnackRewardsLoadPromise;
      if (!force && this.selectedTeamSnackRewardsLoadedTeamId === teamId) {
        return;
      }
    }

    this.selectedTeamSnackRewardsLoadPromise = this.loadSelectedTeamSnackRewards(companyId, teamId)
      .finally(() => {
        this.selectedTeamSnackRewardsLoadPromise = undefined;
      });

    await this.selectedTeamSnackRewardsLoadPromise;
  }

  private async loadSelectedTeamSnackRewards(companyId: string, teamId: string): Promise<void> {
    const settingsRead = this.captureTeamSettingsRead();
    this.isSelectedTeamSnacksLoading = true;
    this.selectedTeamSnackLoadError = '';

    try {
      const snackLoad = await this.payrollSnacksAssignmentService.loadSnackRewardsForCompany(
        companyId,
        teamId,
        this.getCurrentBrand(),
        this.isCompanyScopedWorkspace ? this.currentCompany : undefined,
      );
      if (!this.isCurrentTeamSettingsRead(settingsRead) || `${this.currentCompany?.id || ''}` !== companyId || `${this.selectedTeam?.id || ''}` !== teamId) {
        return;
      }

      this.currentCompany = {
        ...(this.currentCompany || {}),
        ...(this.preserveAcceptedCompanySettings(snackLoad.company, settingsRead) || {}),
      };
      this.payrollConfig = this.currentCompany?.payrollConfig;
      this.teamsMap = this.payrollConfig?.teamsMap || {};
      this.selectedTeamSnackRewards = snackLoad.snackRewards;
      this.selectedTeamSnackRewardIdsInOrder = [...snackLoad.companySnackRewardIdsInOrder];
      if (snackLoad.team?.id) {
        this.mergeTeamFromSnackLoad(this.preserveAcceptedTeamSettings(snackLoad.team, settingsRead));
      }
      this.selectedTeamSnackRewardsLoadedTeamId = teamId;
    } catch (_) {
      if (!this.isCurrentTeamSettingsRead(settingsRead) || `${this.selectedTeam?.id || ''}` !== teamId) {
        return;
      }
      this.selectedTeamSnackRewards = [];
      this.selectedTeamSnackRewardIdsInOrder = [];
      this.selectedTeamSnackRewardsLoadedTeamId = '';
      this.selectedTeamSnackLoadError = 'Snack availability could not be loaded right now.';
    } finally {
      if (this.isCurrentTeamSettingsRead(settingsRead) && `${this.selectedTeam?.id || ''}` === teamId) {
        this.isSelectedTeamSnacksLoading = false;
      }
    }
  }

  private resetSelectedTeamSnackRewards(): void {
    this.isSelectedTeamSnacksLoading = false;
    this.selectedTeamSnackRewards = [];
    this.selectedTeamSnackRewardIdsInOrder = [];
    this.selectedTeamSnackRewardsLoadedTeamId = '';
    this.selectedTeamSnackLoadError = '';
  }

  private mergeTeamFromSnackLoad(team: any): void {
    if (!team?.id) {
      return;
    }

    const index = this.teams.findIndex((existingTeam) => existingTeam?.id === team.id);
    if (index < 0) {
      return;
    }

    this.teams[index] = {
      ...this.teams[index],
      ...team,
      teamMembers: this.teams[index]?.teamMembers,
      membersCount: this.teams[index]?.membersCount,
      autoAddNewMembers: team?.metadata?.autoAddNewMembers ?? this.teams[index]?.autoAddNewMembers,
    };
  }

  private applySelectedTeamSnackMetadata(teamId: string, metadata: any): void {
    const index = this.teams.findIndex((team) => team?.id === teamId);
    if (index < 0) {
      return;
    }

    this.teams[index] = {
      ...this.teams[index],
      metadata: { ...(metadata || {}) },
    };
  }

  private async refreshSelectedTeamAutoAddEligibility(): Promise<void> {
    const teamId = `${this.selectedTeam?.id || ''}`.trim();
    const requestVersion = this.selectedTeamDetailsRequestVersion;
    if (!this.canUseSelectedTeamAutoAdd) {
      const team: any = this.selectedTeam;
      this.selectedTeamAutoAddEligible = false;
      this.selectedTeamAutoAddReason = this.canShowSelectedTeamAddOnlyAutoAddControls
        && this.isWallitManagedProviderCensusTeam(team)
        && !this.isWallitManagedProviderCensusSnapshotTeam(team)
        ? AUTO_ADD_REQUIRES_FILTER_TEAM_REASON
        : this.isReadOnlyProviderCensus
        ? `${this.getPayrollProviderDisplayName()} auto-add requires a saved-filter Wallit team.`
        : this.payrollConnectionUnsupportedReason
        || (this.isPayrollConnectionErrored
          ? `Auto-add is unavailable until the ${this.getPayrollProviderDisplayName('payroll provider')} connection is healthy again.`
          : 'Auto-add requires an active payroll provider connection.');
      return;
    }

    const team: any = this.selectedTeam;
    if (!team?.id || !this.currentCompany?.id) {
      this.selectedTeamAutoAddEligible = false;
      this.selectedTeamAutoAddReason = 'Select a team to configure auto-add.';
      return;
    }

    if (this.canUseSelectedTeamAddOnlyAutoAdd && !this.canUseProviderAutoAdd) {
      await this.refreshSelectedPaycorTeamAutoAddEligibility(team, requestVersion);
      return;
    }

    const originalFilters = this.teamsMap?.[team.id]?.originalFilters || {};
    const fieldsContain = originalFilters?.fieldsContain;
    const fieldsExclude = originalFilters?.fieldsExclude;
    const hasFilters = !!(
      (fieldsContain && Object.keys(fieldsContain).length > 0) ||
      (fieldsExclude && Object.keys(fieldsExclude).length > 0)
    );

    if (!hasFilters) {
      this.selectedTeamAutoAddEligible = false;
      this.selectedTeamAutoAddReason = AUTO_ADD_REQUIRES_FILTER_TEAM_REASON;
      return;
    }

    this.isAutoAddEligibilityLoading = true;
    try {
      const filteredCensus = await this.payrollService.getFilteredCensusForPayrollProviderCompany(
        this.currentCompany.id,
        { fieldsContain, fieldsExclude },
        this.payrollProviderConnection?.providerSlug || this.payrollProviderConnection?.connection?.providerSlug,
      );
      if (!this.isCurrentSelectedTeamRequest(teamId, requestVersion)) {
        return;
      }
      const filteredEmailSet = this.getFilteredEmployeeEmailSet(filteredCensus);
      if (!filteredEmailSet.size) {
        this.selectedTeamAutoAddEligible = false;
        this.selectedTeamAutoAddReason = 'No active employees match the saved filters.';
        return;
      }

      const teamEmailSet = this.getTeamMemberEmailSet(team);
      this.selectedTeamAutoAddEligible = this.areEmailSetsEqual(filteredEmailSet, teamEmailSet);
      this.selectedTeamAutoAddReason = this.selectedTeamAutoAddEligible
        ? ''
        : 'Select all employees matching this team’s filters to enable auto-add.';
    } catch (error: any) {
      if (!this.isCurrentSelectedTeamRequest(teamId, requestVersion)) {
        return;
      }
      this.selectedTeamAutoAddEligible = false;
      this.selectedTeamAutoAddReason = 'Unable to validate auto-add eligibility right now.';
    } finally {
      if (this.isCurrentSelectedTeamRequest(teamId, requestVersion)) {
        this.isAutoAddEligibilityLoading = false;
      }
    }
  }

  private async refreshSelectedPaycorTeamAutoAddEligibility(
    team: any,
    requestVersion = this.selectedTeamDetailsRequestVersion,
  ): Promise<void> {
    const teamId = `${team?.id || ''}`.trim();
    const metadata = this.getTeamSourceMetadata(team);
    const criteriaSnapshot = metadata.criteriaSnapshot || {};
    if (!Array.isArray(criteriaSnapshot.criteria) || !criteriaSnapshot.criteria.length) {
      this.selectedTeamAutoAddEligible = false;
      this.selectedTeamAutoAddReason = AUTO_ADD_REQUIRES_FILTER_TEAM_REASON;
      return;
    }

    this.isAutoAddEligibilityLoading = true;
    try {
      await this.ensureAllEmployeesLoaded();
      if (!this.isCurrentSelectedTeamRequest(teamId, requestVersion)) {
        return;
      }
      const filteredEmailSet = this.getSavedCriteriaEmailSet(criteriaSnapshot);
      if (!filteredEmailSet.size) {
        this.selectedTeamAutoAddEligible = false;
        this.selectedTeamAutoAddReason = 'No active employees with email addresses match the saved filters.';
        return;
      }

      const teamEmailSet = this.getTeamMemberEmailSet(team);
      this.selectedTeamAutoAddEligible = this.areEmailSetsEqual(filteredEmailSet, teamEmailSet);
      this.selectedTeamAutoAddReason = this.selectedTeamAutoAddEligible
        ? ''
        : 'Auto-add is only available when this team has every active employee with an email address matching the saved filters.';
    } catch (error: any) {
      if (!this.isCurrentSelectedTeamRequest(teamId, requestVersion)) {
        return;
      }
      this.selectedTeamAutoAddEligible = false;
      this.selectedTeamAutoAddReason = 'Unable to validate auto-add eligibility right now.';
    } finally {
      if (this.isCurrentSelectedTeamRequest(teamId, requestVersion)) {
        this.isAutoAddEligibilityLoading = false;
      }
    }
  }

  async onSelectedTeamChange(teamId: string): Promise<boolean> {
    const authorizedTeamId = this.getAuthorizedTeamId(teamId);
    if (!authorizedTeamId) {
      return false;
    }
    if (
      this.isCompanyScopedWorkspace
      && !await this.selectNativeGlobalTeam(authorizedTeamId)
    ) {
      return false;
    }

    this.invalidateSelectedTeamDetails();
    this.selectedTeamId = authorizedTeamId;
    if (this.selectedTabIndex === 1) {
      await this.ensureSelectedTeamDetailsLoaded(true);
    }
    this.isFundingTimingExpanded = true;
    return true;
  }

  async onSelectedTeamFundingTimingChanged(value: 'fund_now' | 'next_month'): Promise<void> {
    const team = this.selectedTeam;
    if (!team?.id || !this.canEditSelectedTeamFundingTiming || this.isFundingTimingSaving) {
      return;
    }

    const nextProRate = value === 'fund_now';
    if (this.isTeamFundingProrated(team) === nextProRate) {
      return;
    }

    const previousProRate = this.isTeamFundingProrated(team);
    team.proRate = nextProRate;
    this.isFundingTimingSaving = true;

    try {
      await this.teamsService.updateTeamProRate(team.id, nextProRate);
      this.snackBar.openFromComponent(SnackBarComponent, {
        data: `Funding timing updated to ${nextProRate ? 'Fund right away' : 'Start at next scheduled top-up'}.`,
      });
    } catch (error: any) {
      team.proRate = previousProRate;
      const errorMessage = error?.data?.error || error?.message || 'Unable to update funding timing.';
      this.snackBar.openFromComponent(SnackBarComponent, { data: errorMessage });
    } finally {
      this.isFundingTimingSaving = false;
    }
  }

  toggleFundingTimingExpanded(): void {
    this.isFundingTimingExpanded = !this.isFundingTimingExpanded;
  }

  async onSelectedTeamFundingMethodCtaClicked(): Promise<void> {
    if (this.isFundingMethodActionLoading) {
      return;
    }

    if (this.canUseCompanyFundingMethod) {
      await this.attachCompanyFundingMethodToSelectedTeam();
      return;
    }

    this.openCompanyConnectionsForFundingMethod();
  }

  toggleRewardsActivityExpanded(): void {
    this.isRewardsActivityExpanded = !this.isRewardsActivityExpanded;
  }

  async fundSelectedTeamAllowances(): Promise<void> {
    if (this.monthlyTopupSchedule?.fundingBlocked) return;
    const team = this.selectedTeam;
    if (!team?.id || !this.canFundSelectedTeamAllowancesNow) {
      return;
    }
    const teamId = team.id;

    const fundingMode = await this.promptForInitialFundingMode(team);
    if (fundingMode === null) {
      return;
    }

    this.isFundingMembersLoading = true;
    this.spinnerService.show();
    let preview: PreviewMonthlyAllowanceFundingResponseData;
    try {
      const previewResponse = await this.teamsService.previewMonthlyAllowanceFunding(teamId, {
        fundingMode,
      });
      if (!previewResponse.success) {
        throw new Error(previewResponse.message || 'Unable to preview monthly allowance funding.');
      }
      preview = previewResponse.data;
    } catch (error: any) {
      await this.handleMonthlyAllowanceFundingError(error, teamId);
      return;
    } finally {
      this.isFundingMembersLoading = false;
      this.spinnerService.hide();
    }

    const confirm = await this.uiAlertService.presentAlertConfirm(
      this.buildFundAllowanceAlertMessage(team, preview),
    );

    if (!confirm) {
      return;
    }

    this.isFundingMembersLoading = true;
    this.spinnerService.show();
    try {
      await this.teamsService.fundMonthlyAllowanceCandidates(teamId, {
        fundingMode,
        quoteToken: preview.quoteToken,
      });
      this.snackBar.openFromComponent(SnackBarComponent, {
        data: 'Payment initiated. Subscription will be activated when the payment completes. Bank payments may take a few days.',
      });
      window.setTimeout(() => {
        void this.onSelectedTeamChange(teamId);
      }, 3000);
    } catch (error: any) {
      await this.handleMonthlyAllowanceFundingError(error, teamId);
    } finally {
      this.isFundingMembersLoading = false;
      this.spinnerService.hide();
    }
  }

  async onSelectedTeamAutoAddChanged(autoAddNewMembers: boolean): Promise<void> {
    if (!this.canUseSelectedTeamAutoAdd) {
      return;
    }

    const team: any = this.selectedTeam;
    if (!team?.id || !this.currentCompany?.id) {
      return;
    }

    const previousValue = !!team?.autoAddNewMembers;
    if (previousValue === autoAddNewMembers || this.isAutoAddSaving) {
      return;
    }

    if (autoAddNewMembers && !this.selectedTeamAutoAddEligible) {
      team.autoAddNewMembers = previousValue;
      await this.uiAlertService.presentMessage(this.selectedTeamAutoAddReason || 'This team is not eligible for auto-add.');
      return;
    }

    this.isAutoAddSaving = true;
    try {
      const response = await this.payrollService.updateTeamAutoAddNewMembers(
        this.getCurrentBrand(),
        this.currentCompany.id,
        team.id,
        autoAddNewMembers,
      );
      if (autoAddNewMembers && response?.eligible === false) {
        team.autoAddNewMembers = false;
        team.metadata = {
          ...(team.metadata || {}),
          autoAddNewMembers: false,
        };
        await this.uiAlertService.presentMessage(
          response?.reason || 'This team is not eligible for auto-add.',
        );
        return;
      }
      const effectiveValue = !!response?.autoAddNewMembers;
      team.autoAddNewMembers = effectiveValue;
      team.metadata = {
        ...(team.metadata || {}),
        autoAddNewMembers: effectiveValue,
      };
    } catch (error: any) {
      team.autoAddNewMembers = previousValue;
      const errorMessage = error?.data?.error || error?.message || 'Unable to update auto-add setting.';
      await this.uiAlertService.presentMessage(errorMessage);
    } finally {
      this.isAutoAddSaving = false;
      await this.refreshSelectedTeamAutoAddEligibility();
    }
  }

  // -------------------------
  // All Employees - Filters (delegated to census session)
  // -------------------------
  async handleFilterSelect(filterKey: string, filterValue: string | string[]): Promise<void> {
    if (!this.canUseAllEmployeesFilterControls) {
      return;
    }
    try {
      await this.census.handleFilterSelect(filterKey, filterValue);
      this.clearAllEmployeesConnectionError();
    } catch (error: any) {
      this.setAllEmployeesConnectionError(error);
    }
  }

  async clearFilter(filterKey: string, filterValue: string): Promise<void> {
    if (!this.canUseAllEmployeesFilterControls) {
      return;
    }
    try {
      await this.census.clearFilter(filterKey, filterValue);
      this.clearAllEmployeesConnectionError();
    } catch (error: any) {
      this.setAllEmployeesConnectionError(error);
    }
  }

  async clearAllFilters(): Promise<void> {
    if (!this.canUseAllEmployeesFilterControls) {
      return;
    }
    try {
      await this.census.clearAllFilters();
      this.clearAllEmployeesConnectionError();
    } catch (error: any) {
      this.setAllEmployeesConnectionError(error);
    }
  }

  async refreshAllEmployeesFromUkg(): Promise<void> {
    if (!this.canRefreshAllEmployeesFromUkg || this.isRefreshingAllEmployeesFromUkg) {
      return;
    }

    const contextKey = this.getAllEmployeesContextKey();
    this.activateAllEmployeesContext(contextKey);
    const request = this.captureAllEmployeesLoadRequest(contextKey);
    this.allEmployeesRefreshError = '';
    this.isRefreshingAllEmployeesFromUkg = true;

    try {
      await this.census.refreshUkgCensus();
      if (!this.isCurrentAllEmployeesLoad(request)) {
        return;
      }
      this.allEmployeesLoadMode = 'provider';
      this.allEmployeesLoadedContextKey = request.contextKey;
      this.allEmployeesLoaded = true;
      await this.loadTerminationReviewForCurrentCompany({
        preserveOpenState: true,
        reopenResolved: true,
      });
    } catch (_) {
      if (!this.isCurrentAllEmployeesLoad(request)) {
        return;
      }
      this.allEmployeesRefreshError = 'Unable to refresh from UKG. Existing employees and filters are still shown. Try again.';
    } finally {
      if (this.isCurrentAllEmployeesLoad(request)) {
        this.isRefreshingAllEmployeesFromUkg = false;
      }
    }
  }

  get filteredEmployees(): any[] {
    const search = (this.searchAllEmployees || '').trim().toLowerCase();
    const source = this.census.getFilterMatchedEmployees();
    const cacheKey = JSON.stringify({
      activeFilters: this.activeFiltersMap,
      search,
      sortActive: this.allEmployeesSort?.active || '',
      sortDirection: this.allEmployeesSort?.direction || '',
    });
    if (
      this.filteredEmployeesCacheSource === source
      && this.filteredEmployeesCacheKey === cacheKey
    ) {
      return this.filteredEmployeesCache;
    }

    const employees = !search
      ? source
      : source.filter((employee) => this.employeeMatchesSearch(employee, search));

    this.filteredEmployeesCacheSource = source;
    this.filteredEmployeesCacheKey = cacheKey;
    this.filteredEmployeesCache = this.sortAllEmployees(employees || []);
    return this.filteredEmployeesCache;
  }

  onAllEmployeesSortChange(sort: Sort): void {
    if (!this.isPaycorProviderCensus) {
      this.allEmployeesSort = { active: '', direction: '' };
      return;
    }
    this.allEmployeesSort = sort || { active: '', direction: '' };
  }

  private employeeMatchesSearch(employee: any, search: string): boolean {
    return this.getEmployeeSearchValues(employee).some((value) => value.toLowerCase().includes(search));
  }

  private getEmployeeSearchValues(employee: any): string[] {
    const baseValues = [
      this.employeeName(employee),
      this.employeeEmailValue(employee),
    ];

    const paycorValues = this.isPaycorProviderCensus
      ? [
        this.employeeStatus(employee),
        this.employeeJobTitle(employee),
        this.employeeDepartment(employee),
        this.employeeManager(employee),
        this.employeePayGroup(employee),
        this.employeeWorkerType(employee),
        this.employeeLocation(employee),
      ]
      : [];

    return baseValues.concat(paycorValues)
      .map((value) => `${value || ''}`.trim())
      .filter((value) => !!value && value !== '—');
  }

  private sortAllEmployees(employees: any[]): any[] {
    const active = `${this.allEmployeesSort?.active || ''}`.trim();
    const direction = this.allEmployeesSort?.direction;
    if (!this.isPaycorProviderCensus || !active || !direction) {
      return employees;
    }

    const directionMultiplier = direction === 'desc' ? -1 : 1;
    return employees.slice().sort((left, right) => {
      const leftValue = this.getAllEmployeesSortValue(left, active);
      const rightValue = this.getAllEmployeesSortValue(right, active);
      const leftMissing = !leftValue || leftValue === '—';
      const rightMissing = !rightValue || rightValue === '—';

      if (leftMissing && rightMissing) {
        return 0;
      }
      if (leftMissing) {
        return 1;
      }
      if (rightMissing) {
        return -1;
      }

      return leftValue.localeCompare(rightValue, undefined, {
        numeric: true,
        sensitivity: 'base',
      }) * directionMultiplier;
    });
  }

  private getAllEmployeesSortValue(employee: any, active: string): string {
    const values: Record<string, () => string> = {
      department: () => this.employeeDepartment(employee),
      division: () => this.employeeDivision(employee),
      employee: () => this.employeeName(employee),
      employmentType: () => this.employeeEmploymentType(employee),
      jobTitle: () => this.employeeJobTitle(employee),
      location: () => this.employeeLocation(employee),
      manager: () => this.employeeManager(employee),
      payGroup: () => this.employeePayGroup(employee),
      payType: () => this.employeePayType(employee),
      state: () => this.employeeState(employee),
      status: () => this.employeeStatus(employee),
      workerType: () => this.employeeWorkerType(employee),
    };

    return `${values[active]?.() || ''}`.trim();
  }

  activeFilterCount(filterKey: string): number {
    const values = this.allEmployeesActiveFiltersMap?.[filterKey];
    return Array.isArray(values) ? values.length : 0;
  }

  displayFilterValue(filterKey: string, value: string): string {
    const rawValue = `${value ?? ''}`.trim();
    if (!rawValue) {
      return '';
    }

    const normalizedFilterKey = `${filterKey || ''}`.toLowerCase().replace(/[^a-z0-9]/g, '');
    if (!['location', 'department', 'division'].includes(normalizedFilterKey)) {
      return rawValue;
    }

    const escapedFilterKey = `${filterKey || ''}`.trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    if (!escapedFilterKey) {
      return rawValue;
    }

    return rawValue.replace(new RegExp(`^${escapedFilterKey}\\s*\\/\\s*`, 'i'), '').trim();
  }

  get allEmployeesTotalCount(): number {
    return this.availableEmployees?.length ?? 0;
  }

  get allEmployeesVisibleCount(): number {
    return this.filteredEmployees?.length ?? 0;
  }

  get selectedCount(): number {
    return this.selected?.size ?? 0;
  }

  get canCreateTeamFromSelection(): boolean {
    return this.canCreateInternalTeamFromCensusSelection;
  }

  get isUsingCompanyMemberFallbackForAllEmployees(): boolean {
    return !this.canUseProviderCensus;
  }

  toggleEmployee(emailOrId: string, checked: boolean): void {
    this.census.toggleEmployee(emailOrId, checked);
  }

  isAllVisibleSelected(): boolean {
    const visible = (this.filteredEmployees || []).filter((e) => this.hasEmployeeEmail(e));
    if (!visible.length) {
      return false;
    }
    return visible.every((e) => {
      const key = this.employeeKey(e);
      return !!this.selectedEmails[key];
    });
  }

  isSomeVisibleSelected(): boolean {
    const visible = (this.filteredEmployees || []).filter((e) => this.hasEmployeeEmail(e));
    if (!visible.length) {
      return false;
    }
    const some = visible.some((e) => {
      const key = this.employeeKey(e);
      return !!this.selectedEmails[key];
    });
    return some && !this.isAllVisibleSelected();
  }

  toggleSelectAllVisible(checked: boolean): void {
    const visible = this.filteredEmployees || [];
    if (checked) {
      this.census.selectAllVisible(visible);
    } else {
      this.census.deselectAllVisible(visible);
    }
  }

  employeeKey(e: any): string {
    return (
      e?.['Primary Email'] ||
      e?.PrimaryEmail ||
      e?.primaryEmail ||
      e?.email ||
      e?.userEmail ||
      e?.['Employee Id'] ||
      e?.EmployeeId ||
      e?.employeeId ||
      e?.id ||
      ''
    );
  }

  employeeName(e: any): string {
    const first = e?.['First Name'] ?? e?.FirstName ?? e?.firstName ?? '';
    const last = e?.['Last Name'] ?? e?.LastName ?? e?.lastName ?? '';
    const combined = `${first} ${last}`.trim();
    return combined || e?.name || this.employeeEmailValue(e) || '—';
  }

  employeeEmail(e: any): string {
    return this.employeeEmailValue(e) || '—';
  }

  employeeEmailValue(e: any): string {
    return `${e?.['Primary Email'] || e?.PrimaryEmail || e?.primaryEmail || e?.email || e?.userEmail || ''}`.trim();
  }

  hasEmployeeEmail(e: any): boolean {
    return !!this.employeeEmailValue(e);
  }

  missingEmployeeEmailTooltip(e: any): string {
    if (this.hasEmployeeEmail(e)) {
      return '';
    }
    return 'This payroll employee does not have an email address, so they cannot be imported to Wallit yet.';
  }

  employeeLocation(e: any): string {
    return this.displayEmployeeFieldValue(
      'Location',
      e,
      ['location', 'Location', 'WorkLocation', 'Work Location', 'Default Location Full Path'],
    );
  }

  employeeDivision(e: any): string {
    return this.displayEmployeeFieldValue(
      'Division',
      e,
      ['division', 'Division', 'Default Division Full Path'],
    );
  }

  employeeDepartment(e: any): string {
    return this.displayEmployeeFieldValue(
      'Department',
      e,
      ['department', 'Department', 'Default Department Full Path', 'dept'],
    );
  }

  employeeState(e: any): string {
    return e?.state || e?.['State'] || '—';
  }

  employeeEmploymentType(e: any): string {
    return this.displayEmployeeFieldValue(
      '',
      e,
      ['employeeType', 'Employee Type', 'employmentType', 'Employment Type', 'employment_type'],
    );
  }

  employeePayType(e: any): string {
    return this.displayEmployeeFieldValue('', e, ['payType', 'Pay Type']);
  }

  employeeStatus(e: any): string {
    return this.displayEmployeeFieldValue(
      '',
      e,
      ['employeeStatus', 'EmployeeStatus', 'Employee Status', 'employmentStatus', 'EmploymentStatus', 'Employment Status', 'sourceStatus', 'SourceStatus', 'Source Status', 'status'],
    );
  }

  employeeJobTitle(e: any): string {
    return this.displayEmployeeFieldValue(
      '',
      e,
      ['jobTitle', 'JobTitle', 'Job Title', 'Payroll Job Title'],
    );
  }

  employeeManager(e: any): string {
    return this.displayEmployeeFieldValue('', e, ['manager', 'Manager']);
  }

  employeePayGroup(e: any): string {
    return this.displayEmployeeFieldValue('', e, ['payGroup', 'PayGroup', 'Pay Group']);
  }

  employeeWorkerType(e: any): string {
    return this.displayEmployeeFieldValue(
      '',
      e,
      ['workerType', 'WorkerType', 'Worker Type', 'employeeType', 'Employee Type', 'employmentType', 'Employment Type', 'employment_type'],
    );
  }

  private displayEmployeeFieldValue(filterKey: string, employee: any, keys: string[]): string {
    const value = this.getFirstEmployeeFieldValue(employee, keys);
    const displayValue = filterKey ? this.displayFilterValue(filterKey, value) : value;
    return displayValue || '—';
  }

  private getFirstEmployeeFieldValue(employee: any, keys: string[]): string {
    for (const key of keys) {
      const value = `${employee?.[key] ?? ''}`.trim();
      if (value) {
        return value;
      }
    }
    return '';
  }

  onEmployeeDetails(e: any): void {
    this.openEmployeeDetailsDialog({
      employee: e,
      allowEditing: false,
    });
  }

  async onSelectedTeamMemberDetails(member: any): Promise<void> {
    const team = this.selectedTeam as any;
    const memberId = this.getSelectedTeamMemberId(member);
    if (!team?.id || !memberId) {
      return;
    }

    const dialogRef = this.openEmployeeDetailsDialog({
      teamId: team.id,
      employee: this.cloneEmployeeDetails(member),
      allowEditing: true,
    });
    const result = await dialogRef.afterClosed().toPromise() as EmployeeDetailsDialogResult | undefined;
    if (!result?.saved) {
      return;
    }

    this.updateSelectedTeamMemberInPlace(team.id, result.employee);
    await this.reloadSelectedTeamMembers(team.id);
  }

  onCreateSnapshotTeam(): void {
    const matching = this.matchingSelection;
    const snapshotId = matching?.snapshotId || this.workforce.selectionSnapshotId;
    if (!snapshotId || (!matching && !this.workforce.selection.size) || this.teamCreation.busy) { return; }
    const employeeKeys = [...this.workforce.selection.keys()];
    const companyId = this.currentCompany.id;
    this.dialog.open<EditTeamDialogComponent, EditTeamDialogData, EditTeamDialogResult>(EditTeamDialogComponent, {
      width: '900px', maxWidth: '95vw', maxHeight: '90vh', autoFocus: false,
      panelClass: 'edit-team-dialog-panel', data: {
        isEdit: false, skipMemberSelectionStep: true, team: {name: '', amount: 0},
        employees: [], selectedEmployeeIds: [], company: this.currentCompany,
        snapshotSelectionCount: matching ? matching.eligibleRows : employeeKeys.length,
        snapshotSubmit: async (teamName, amount) => {
          if (this.currentCompany.id !== companyId || this.workforce.context?.snapshot?.id !== snapshotId) { throw new Error('Source changed'); }
          if (matching !== this.matchingSelection) throw new Error('Criteria changed. Review the preview again.');
          await this.teamCreation.submit({snapshotId, teamName, amount, ...(matching ? {selection: {mode: 'all_matching' as const, criteria: matching.criteria}} : {employeeKeys})});
          this.workforce.selection.clear();
        },
      },
    });
  }

  onCreateTeamFromSelection(): void {
    if (!this.canCreateTeamFromSelection) {
      return;
    }

    const existingTeamIds = new Set((this.teams || []).map((team) => team?.id).filter((id) => !!id));
    const selectedEmployees = (this.availableEmployees || []).filter((e) => {
      const key = this.employeeKey(e);
      return !!this.selectedEmails[key];
    });

    const data: EditTeamDialogData = {
      isEdit: false,
      skipMemberSelectionStep: true,
      team: { name: '' },
      employees: selectedEmployees,
      selectedEmployeeIds: selectedEmployees,
      initialFieldsContain: this.census.getFieldsContainForRequestSnapshot(),
      criteriaSnapshot: this.census.getActiveCriteriaSnapshot(),
      company: this.currentCompany,
      providerConnection: this.payrollProviderConnection,
      providerDisplayName: this.getPayrollProviderDisplayName(),
    };

    const ref = this.dialog.open<EditTeamDialogComponent, EditTeamDialogData, EditTeamDialogResult>(
      EditTeamDialogComponent,
      {
        data,
        width: '900px',
        maxWidth: '95vw',
        maxHeight: '90vh',
        disableClose: true,
        autoFocus: false,
        panelClass: 'edit-team-dialog-panel',
      }
    );

    ref.afterClosed().subscribe(async (result) => {
      if (!result?.saved) {
        return;
      }

      if (this.isCompanyScopedWorkspace) {
        await this.refreshAndSelectNativeCreatedTeam(existingTeamIds, result, true);
        return;
      }
      const createdTeamId = await this.refreshSavedTeamAfterDialog(existingTeamIds, result);
      if (createdTeamId) {
        await this.onSelectedTeamChange(createdTeamId);
      }
      this.selectedTabIndex = 1;
    });
  }

  private async onSnapshotOperationComplete(teamId: string): Promise<void> {
    if (this.teamCreation.progress?.purpose !== 'add_members') { await this.showCreatedSnapshotTeam(teamId); return; }
    const companyId = this.workforce.companyId;
    const team = this.teams.find(candidate => candidate.id === teamId);
    if (!team) return;
    try {
      const members = await this.teamsService.getTeamMembersWithDailyLoginInfo(teamId);
      if (this.isDestroyed || companyId !== this.workforce.companyId || this.teams.find(candidate => candidate.id === teamId) !== team) return;
      this.mergeTeamIntoList({...team, teamMembers: members, membersCount: members.length});
    } catch (_) {
      if (!this.isDestroyed && companyId === this.workforce.companyId) this.teamCreation.error = 'Employee additions completed. Reload the team to update its displayed members.';
    }
  }

  private async showCreatedSnapshotTeam(teamId: string): Promise<void> {
    const result: EditTeamDialogResult = {saved: true, teamId, teamName: '', wellnessAmount: 0,
      selectedEmployeeIds: [], autoAddNewMembers: false};
    if (this.isCompanyScopedWorkspace) {
      await this.refreshAndSelectNativeCreatedTeam(new Set<string>(), result, true);
      return;
    }
    const created = await this.refreshSavedTeamAfterDialog(new Set<string>(), result);
    if (created && !this.isDestroyed) { await this.onSelectedTeamChange(created); this.selectedTabIndex = 1; }
  }

  private resolveCreatedTeamId(existingTeamIds: Set<string>, result: EditTeamDialogResult): string {
    if (!result || !('saved' in result) || !result.saved) {
      return '';
    }

    if (result.teamId && this.teams.some((team) => team.id === result.teamId)) {
      return result.teamId;
    }

    const newlyAddedTeam = this.teams.find((team) => !!team?.id && !existingTeamIds.has(team.id));
    if (newlyAddedTeam?.id) {
      return newlyAddedTeam.id;
    }

    const normalizedTeamName = `${result.teamName || ''}`.trim().toLowerCase();
    if (!normalizedTeamName) {
      return '';
    }

    const matchingTeams = this.teams.filter(
      (team) => `${team?.name || ''}`.trim().toLowerCase() === normalizedTeamName
    );
    if (!matchingTeams.length) {
      return '';
    }
    if (matchingTeams.length === 1) {
      return matchingTeams[0].id;
    }

    return matchingTeams
      .slice()
      .sort((a, b) => this.parseCreatedAtMs(b?.createdAt) - this.parseCreatedAtMs(a?.createdAt))[0]
      ?.id || '';
  }

  private buildHydratedTeamState(team: any, teamMembers: any[]): any {
    const normalizedTeamMembers = Array.isArray(teamMembers) ? teamMembers : [];
    return {
      ...team,
      teamMembers: normalizedTeamMembers,
      membersCount: normalizedTeamMembers.length,
      autoAddNewMembers: team?.autoAddNewMembers ?? team?.metadata?.autoAddNewMembers,
    };
  }

  private mergeTeamIntoList(team: any): void {
    if (!team?.id) {
      return;
    }

    const nextTeam = { ...team };
    const existingTeamIndex = this.teams.findIndex((candidate) => candidate?.id === nextTeam.id);
    if (existingTeamIndex === -1) {
      this.teams = [...this.teams, nextTeam];
      return;
    }

    const nextTeams = [...this.teams];
    nextTeams[existingTeamIndex] = {
      ...nextTeams[existingTeamIndex],
      ...nextTeam,
    };
    this.teams = nextTeams;
  }

  private async refreshSavedTeamState(teamId: string, settingsRead = this.captureTeamSettingsRead()): Promise<boolean> {
    const companyId = `${this.currentCompany?.id || ''}`.trim();
    const normalizedTeamId = `${teamId || ''}`.trim();
    if (!companyId || !normalizedTeamId) {
      return false;
    }

    try {
      const companyPromise = this.isCompanyScopedWorkspace
        ? this.teamCompaniesService
          .getPayrollCompanyWorkspaceContext(companyId)
          .then((context) => context.company)
        : this.teamCompaniesService.getCompany(companyId);
      const [company, team, teamMembers] = await Promise.all([
        companyPromise,
        this.teamsService.getTeamInfo(normalizedTeamId),
        this.teamsService.getTeamMembersWithDailyLoginInfo(normalizedTeamId),
      ]);
      if (!this.isCurrentTeamSettingsRead(settingsRead) || `${team?.id || ''}`.trim() !== normalizedTeamId
        || (company && company.id !== companyId)) {
        return false;
      }

      if (company) {
        this.currentCompany = this.preserveAcceptedCompanySettings(company, settingsRead);
        this.payrollConfig = this.currentCompany?.payrollConfig;
        this.teamsMap = this.payrollConfig?.teamsMap || {};
      }

      this.mergeTeamIntoList(this.buildHydratedTeamState(this.preserveAcceptedTeamSettings(team, settingsRead), teamMembers));
      if (this.selectedTeamId === normalizedTeamId) {
        this.invalidateSelectedTeamDetails();
      }
      return true;
    } catch (error) {
      console.error('Unable to refresh saved payroll team state', { teamId: normalizedTeamId, error });
      return false;
    }
  }

  private queueBackgroundTeamsReload(): void {
    void this.loadPageData(false).catch((error) => {
      console.error('Unable to reconcile payroll teams page in background', error);
    });
  }

  private async refreshSavedTeamAfterDialog(
    existingTeamIds: Set<string>,
    result: EditTeamDialogResult,
  ): Promise<string> {
    if (!result?.saved) {
      return '';
    }

    const savedTeamId = `${result.teamId || ''}`.trim();
    if (!savedTeamId) {
      await this.loadPageData();
      return this.resolveCreatedTeamId(existingTeamIds, result);
    }

    const didRefreshSavedTeam = await this.refreshSavedTeamState(savedTeamId);
    if (!didRefreshSavedTeam) {
      await this.loadPageData();
      return this.resolveCreatedTeamId(existingTeamIds, result);
    }

    const resolvedTeamId = this.resolveCreatedTeamId(existingTeamIds, result) || savedTeamId;
    if (this.selectedTabIndex === 1 && this.selectedTeamId === resolvedTeamId) {
      await this.ensureSelectedTeamDetailsLoaded(true);
    }
    this.queueBackgroundTeamsReload();
    return resolvedTeamId;
  }

  private async refreshAndSelectNativeCreatedTeam(
    existingTeamIds: Set<string>,
    result: EditTeamDialogResult,
    openTeamDetails: boolean,
  ): Promise<boolean> {
    const settingsRead = this.captureTeamSettingsRead();
    this.selectedTabIndex = 0;
    this.selectedTeamId = '';
    this.invalidateSelectedTeamDetails();

    try {
      const nativeState = await this.nativeTeamContextService.initialize(true);
      if (!this.isCurrentTeamSettingsRead(settingsRead)) { return false; }
      const currentCompanyId = `${this.currentCompany?.id || ''}`.trim();
      if (
        !nativeState?.initialized
        || !nativeState.active
        || !!nativeState.error
        || !currentCompanyId
        || nativeState.companyId !== currentCompanyId
      ) {
        throw new Error('The canonical native Team context could not be refreshed.');
      }

      const previousTeamsById = new Map(
        (this.teams || [])
          .filter((team) => !!team?.id)
          .map((team) => [team.id, team]),
      );
      this.teams = (nativeState.teams || []).map((team) => ({
        ...(previousTeamsById.get(team.id) || {}),
        ...this.preserveAcceptedTeamSettings(team, settingsRead),
      }));

      // A user selection made while context was loading takes precedence.
      if (this.selectedTeamId) { return false; }

      const createdTeamId = this.resolveCreatedTeamId(existingTeamIds, result);
      if (!createdTeamId || !this.getAuthorizedTeamId(createdTeamId)) {
        throw new Error('The created Team was absent from the refreshed native Team context.');
      }

      const didHydrateCreatedTeam = await this.refreshSavedTeamState(createdTeamId, settingsRead);
      if (!this.isCurrentTeamSettingsRead(settingsRead) || this.selectedTeamId) { return false; }
      if (!didHydrateCreatedTeam || !this.getAuthorizedTeamId(createdTeamId)) {
        throw new Error('The created Team could not be hydrated.');
      }

      const selectedTeam = await this.nativeTeamContextService.selectTeam(createdTeamId);
      if (!this.isCurrentTeamSettingsRead(settingsRead) || this.selectedTeamId) { return false; }
      if (`${selectedTeam?.id || ''}`.trim() !== createdTeamId) {
        throw new Error('The created Team could not be selected.');
      }

      this.invalidateSelectedTeamDetails();
      this.selectedTeamId = createdTeamId;
      this.isFundingTimingExpanded = true;
      if (openTeamDetails) {
        this.selectedTabIndex = 1;
        await this.ensureSelectedTeamDetailsLoaded(true);
      }
      return true;
    } catch (error) {
      if (!this.isCurrentTeamSettingsRead(settingsRead) || this.selectedTeamId) { return false; }
      console.error('Unable to select newly created native Team', error);
      this.selectedTabIndex = 0;
      this.selectedTeamId = '';
      this.invalidateSelectedTeamDetails();
      await this.uiAlertService.presentMessage(
        'The Team was created, but it could not be selected. Please try selecting it again from the Teams list.',
      );
      return false;
    }
  }

  private parseCreatedAtMs(value: any): number {
    const timestamp = new Date(value || 0).getTime();
    return Number.isFinite(timestamp) ? timestamp : 0;
  }

  // -------------------------
  // Teams + Team details
  // -------------------------
  get totalMembers(): number {
    return this.teams.reduce((sum, t) => sum + t.membersCount, 0);
  }

  private getCurrentCompanyPayrollAdminTeamsConfig(): any {
    const payrollConfig = this.currentCompany?.payrollConfig && typeof this.currentCompany.payrollConfig === 'object'
      ? this.currentCompany.payrollConfig
      : {};
    const nestedPayrollAdminTeamsConfig =
      payrollConfig?.payrollAdminTeams && typeof payrollConfig.payrollAdminTeams === 'object'
        ? payrollConfig.payrollAdminTeams
        : {};
    const payrollAdminTeamsConfig: any = {};

    if (Object.prototype.hasOwnProperty.call(nestedPayrollAdminTeamsConfig, 'defaultPlan')) {
      payrollAdminTeamsConfig.defaultPlan = nestedPayrollAdminTeamsConfig.defaultPlan;
    }

    if (Object.prototype.hasOwnProperty.call(nestedPayrollAdminTeamsConfig, 'plusPlanIncluded')) {
      payrollAdminTeamsConfig.plusPlanIncluded = nestedPayrollAdminTeamsConfig.plusPlanIncluded;
    } else if (Object.prototype.hasOwnProperty.call(payrollConfig, 'plusPlanIncluded')) {
      payrollAdminTeamsConfig.plusPlanIncluded = payrollConfig.plusPlanIncluded;
    }

    return payrollAdminTeamsConfig;
  }

  get currentCompanyIncludesPlusPlan(): boolean {
    return this.getCurrentCompanyPayrollAdminTeamsConfig()?.plusPlanIncluded === true;
  }

  private async loadPageData(showSpinner = true): Promise<void> {
    let settingsRead = this.captureTeamSettingsRead();
    const pageLoadVersion = ++this.pageLoadVersion;
    this.resetTerminationReviewState();
    this.teamCreation.destroy();
    this.workforce.clear();
    this.workforce.mode = 'resolving';
    this.invalidateAllEmployeesCensusCache();
    this.isTeamsDataLoaded = false;
    const shouldShowSpinner = showSpinner && !this.spinnerService.visibility.value;
    if (shouldShowSpinner) {
      this.spinnerService.show('Loading...');
    }

    try {
      const companies = this.isCompanyScopedWorkspace
        ? this.toActiveCompanyList(await this.activeEmployerCompanyService.initialize())
        : await this.usersService.getTeamCompanies();
      if (pageLoadVersion !== this.pageLoadVersion || this.isDestroyed) return;
      this.updateCompanyState(companies.map(company => this.preserveAcceptedCompanySettings(company, settingsRead)));
      if (!this.isCurrentTeamSettingsRead(settingsRead)) { settingsRead = this.captureTeamSettingsRead(); }

      if (!this.hasCompany) {
        this.teams = [];
        this.payrollConfig = undefined;
        this.teamsMap = {};
        this.groupProductOnboardingRows = [];
        this.selectedTeamId = '';
        this.selectedTabIndex = 0;
        this.initializedNativeTeamCompanyId = '';
        this.payrollConnectionHealth = 'none';
        this.census.availableEmployees = [];
        this.census.availableFilters = [];
        return;
      }

      if (!this.isCompanyScopedWorkspace) {
        const company = this.currentCompany;
        const enrichedCompany = await this.teamCompaniesService.getCompany(company.id).catch(() => company);
        if (pageLoadVersion !== this.pageLoadVersion || this.isDestroyed) return;
        this.currentCompany = this.preserveAcceptedCompanySettings(enrichedCompany, settingsRead);
      }

      await this.workforce.open(this.currentCompany.id);
      if (this.workforce.context?.mode === 'snapshot') { await this.teamCreation.open(this.currentCompany.id); }
      if (pageLoadVersion !== this.pageLoadVersion || this.isDestroyed) return;
      void this.loadTerminationReviewForCurrentCompany();
      const [payrollConnectionHealth, groupProductOnboardingRows, teams] = await Promise.all([
        this.resolvePayrollConnectionHealth(),
        this.teamCompaniesService.getGroupProductOnboardingStatuses(this.currentCompany.id).catch(() => []),
        this.teamsService.getTeamsForCompany(this.currentCompany.id),
      ]);
      if (pageLoadVersion !== this.pageLoadVersion || this.isDestroyed) return;
      this.payrollConnectionHealth = payrollConnectionHealth;
      this.payrollConfig = this.currentCompany?.payrollConfig;
      this.teamsMap = this.payrollConfig?.teamsMap || {};
      this.activateAllEmployeesContext(this.getAllEmployeesContextKey());
      this.groupProductOnboardingRows = Array.isArray(groupProductOnboardingRows) ? groupProductOnboardingRows : [];
      if (!this.isHealthyPayrollConnection) {
        this.allEmployeesLoaded = false;
        this.allEmployeesLoadedContextKey = '';
        this.allEmployeesLoadMode = '';
        this.allEmployeesConnectionError = '';
        this.census.availableEmployees = [];
        this.census.availableFilters = [];
        this.census.activeFilters = [];
        this.census.activeFiltersMap = {};
      }

      const loadedTeams = (Array.isArray(teams) ? teams : []).filter((team: any) => {
        const teamCompanyId = `${team?.companyId || team?.company?.id || ''}`.trim();
        return !teamCompanyId || teamCompanyId === `${this.currentCompany?.id || ''}`;
      });

      if (this.canUseLegacyCensus) await Promise.all(
        loadedTeams.map(async (team) => {
          const teamMembers = await this.teamsService.getTeamMembersWithDailyLoginInfo(team.id);
          team.teamMembers = teamMembers;
          team.membersCount = teamMembers?.length ?? 0;
          team.autoAddNewMembers = team?.metadata?.autoAddNewMembers;
        })
      );
      if (pageLoadVersion !== this.pageLoadVersion || this.isDestroyed) return;
      // Publish hydrated teams together so concurrent details loads cannot replace
      // a visible team while its members are still being assigned to the old object.
      this.teams = loadedTeams.map(team => this.preserveAcceptedTeamSettings(team, settingsRead));

      const currentCompanyId = `${this.currentCompany?.id || ''}`;
      if (
        this.isCompanyScopedWorkspace
        && this.initializedNativeTeamCompanyId !== currentCompanyId
      ) {
        await this.applyInitialNativeTeamSelection();
        this.initializedNativeTeamCompanyId = currentCompanyId;
      } else {
        const selectedTeamStillExists = this.teams.some((team) => team.id === this.selectedTeamId);
        this.selectedTeamId = selectedTeamStillExists ? this.selectedTeamId : (this.teams[0]?.id ?? '');
      }
      this.invalidateSelectedTeamDetails();
      if (this.selectedTabIndex === 1) {
        await this.ensureSelectedTeamDetailsLoaded(true);
      }
      this.loadedCompanyId = currentCompanyId;
      await this.workforce.setVisible(this.selectedTabIndex === 2);
    } finally {
      this.isTeamsDataLoaded = true;
      if (shouldShowSpinner) {
        this.spinnerService.hide();
      }
    }
  }

  formatCreatedAt(dateStringToFormat: string): string {
    const dateToFormat = new Date(dateStringToFormat);
    const mm = dateToFormat.getMonth() + 1;
    const dd = dateToFormat.getDate();
    const yyyy = dateToFormat.getFullYear();
    return `Created ${mm}/${dd}/${yyyy}`;
  }

  onCreateTeam(redirectToConnectionsBank = false): void {
    if (!this.canUseLegacyCensus) return;
    if (!this.currentCompany?.id) {
      return;
    }

    const existingTeamIds = new Set((this.teams || []).map((team) => team?.id).filter((id) => !!id));

    const data: EditTeamDialogData = {
      isEdit: false,
      manualMode: !this.canUseProviderTeamCreationFlow,
      team: { name: '' },
      employees: [],
      selectedEmployeeIds: [],
      company: this.currentCompany,
      providerConnection: this.payrollProviderConnection,
      providerDisplayName: this.getPayrollProviderDisplayName(),
    };

    const ref = this.dialog.open<EditTeamDialogComponent, EditTeamDialogData, EditTeamDialogResult>(
      EditTeamDialogComponent,
      {
        data,
        width: '900px',
        maxWidth: '95vw',
        maxHeight: '90vh',
        disableClose: true,
        autoFocus: false,
        panelClass: 'edit-team-dialog-panel',
      }
    );

    ref.afterClosed().subscribe(async (result) => {
      if (!result?.saved) {
        return;
      }
      if (this.isCompanyScopedWorkspace) {
        await this.refreshAndSelectNativeCreatedTeam(existingTeamIds, result, result.wellnessAmount > 0);
        return;
      }
      const createdTeamId = await this.refreshSavedTeamAfterDialog(existingTeamIds, result);
      if (result.wellnessAmount > 0) {
        if (createdTeamId) {
          await this.onSelectedTeamChange(createdTeamId);
        }
        this.selectedTabIndex = 1;
      }
      if (!this.isCompanyScopedWorkspace && redirectToConnectionsBank && this.isHealthyPayrollConnection) {
        void this.router.navigate(['payrolladmin', 'company', 'connections'], {
          queryParams: { openBankConnectionDialog: '1' },
        });
      }
    });
  }

  async onDeleteTeam(team: Team): Promise<void> {
    return this.uiAlertService.presentAlertConfirm(`Do you really want to delete team ${team.name}?`).then(async (ok) => {
      if (ok && team?.id) {
        await this.teamsService.deleteTeam(team.id, true);
        if (this.isCompanyScopedWorkspace) {
          await this.nativeTeamContextService.initialize(true);
        }
        await this.loadPageData();
      }
    });
  }

  get snapshotSettingsCompanyId(): string {
    return this.workforce.mode === 'snapshot' && this.workforce.companyId === this.currentCompany?.id ? this.workforce.companyId : '';
  }

  private captureTeamSettingsRead(): {companyId: string; companyVersion: number; saveVersion: number} {
    return {companyId: this.currentCompany?.id || '', companyVersion: this.settingsCompanyVersion, saveVersion: this.settingsSaveVersion};
  }

  private isCurrentTeamSettingsRead(read: ReturnType<PayrollTeamsComponent['captureTeamSettingsRead']>): boolean {
    return !this.isDestroyed && read.companyId === this.currentCompany?.id && read.companyVersion === this.settingsCompanyVersion;
  }

  private preserveAcceptedTeamSettings(team: any, read: ReturnType<PayrollTeamsComponent['captureTeamSettingsRead']>): any {
    const accepted = this.acceptedTeamSettings.get(team?.id);
    if (!this.isCurrentTeamSettingsRead(read) || !accepted || accepted.version <= read.saveVersion) { return team; }
    const current = this.teams.find(candidate => candidate.id === team.id) || accepted;
    return {...team, name: current.name, amount: current.amount};
  }

  private preserveAcceptedCompanySettings(company: any, read: ReturnType<PayrollTeamsComponent['captureTeamSettingsRead']>): any {
    if (!this.isCurrentTeamSettingsRead(read) || company?.id !== read.companyId || !company?.payrollConfig?.teamsMap) { return company; }
    const teamsMap = {...company.payrollConfig.teamsMap};
    this.acceptedTeamSettings.forEach((accepted, teamId) => {
      if (accepted.version > read.saveVersion && teamsMap[teamId]) {
        const name = this.currentCompany?.payrollConfig?.teamsMap?.[teamId]?.name ?? accepted.name;
        teamsMap[teamId] = {...teamsMap[teamId], name};
      }
    });
    return {...company, payrollConfig: {...company.payrollConfig, teamsMap}};
  }

  onTeamSettingsSaved(value: SavedTeamSettings): void {
    if (this.isDestroyed || value.companyId !== this.snapshotSettingsCompanyId || !this.teams.some(team => team.id === value.teamId)) { return; }
    // Only reads already in flight need protection. Later authoritative reads
    // remain free to replace these fields; unrelated response fields always merge.
    this.acceptedTeamSettings.set(value.teamId, {version: ++this.settingsSaveVersion, name: value.name, amount: value.amount});
    this.mergeTeamIntoList({id: value.teamId, name: value.name, amount: value.amount});
    const config = this.currentCompany.payrollConfig || {};
    const teamsMap = {...config.teamsMap, [value.teamId]: {...config.teamsMap?.[value.teamId], name: value.name}};
    this.currentCompany = {...this.currentCompany, payrollConfig: {...config, teamsMap}};
    this.payrollConfig = this.currentCompany.payrollConfig;
    this.teamsMap = teamsMap;
  }

  onEditTeam(team: Team, startAtMembersStep = false): void {
    if (!this.canUseLegacyCensus) return;
    const teamMembers = (team as any).teamMembers || [];
    const useMemberFallbackMode = !this.canUseProviderTeamWrites
      && !this.canCreateInternalTeamFromCensusSelection;
    const data: EditTeamDialogData = {
      isEdit: true,
      startAtMembersStep,
      memberFallbackMode: useMemberFallbackMode,
      manualMode: false,
      team: { ...team },
      employees: teamMembers,
      selectedEmployeeIds: teamMembers,
      company: this.currentCompany,
      providerConnection: this.payrollProviderConnection,
      providerDisplayName: this.getPayrollProviderDisplayName(),
    };

    const ref = this.dialog.open<EditTeamDialogComponent, EditTeamDialogData, EditTeamDialogResult>(
      EditTeamDialogComponent,
      {
        data,
        width: '900px',
        maxWidth: '95vw',
        maxHeight: '90vh',
        disableClose: true,
        autoFocus: false,
        panelClass: 'edit-team-dialog-panel',
      }
    );

    ref.afterClosed().subscribe(async (result) => {
      if (!result?.saved) {
        return;
      }
      await this.refreshSavedTeamAfterDialog(new Set((this.teams || []).map((candidate) => candidate?.id).filter((id) => !!id)), result);
    });
  }

  onDuplicateTeam(team: Team): void {
    if (!this.canUseLegacyCensus) return;
    const teamMembers = (team as any).teamMembers || [];
    const duplicateName = `${team?.name || ''} (Copy)`.trim();
    const data: EditTeamDialogData = {
      isEdit: false,
      manualMode: !this.canUseProviderTeamCreationFlow,
      team: {
        name: duplicateName,
        amount: team.amount,
        // Keep source team id so the dialog can preload the same saved filters.
        id: team.id,
      },
      employees: teamMembers,
      selectedEmployeeIds: teamMembers,
      company: this.currentCompany,
      providerConnection: this.payrollProviderConnection,
      providerDisplayName: this.getPayrollProviderDisplayName(),
    };

    const ref = this.dialog.open<EditTeamDialogComponent, EditTeamDialogData, EditTeamDialogResult>(
      EditTeamDialogComponent,
      {
        data,
        width: '900px',
        maxWidth: '95vw',
        maxHeight: '90vh',
        disableClose: true,
        autoFocus: false,
        panelClass: 'edit-team-dialog-panel',
      }
    );

    ref.afterClosed().subscribe(async (result) => {
      if (!result?.saved) {
        return;
      }
      await this.refreshSavedTeamAfterDialog(new Set((this.teams || []).map((candidate) => candidate?.id).filter((id) => !!id)), result);
    });
  }

  onEditSelectedTeamDetails(): void {
    const team = this.selectedTeam;
    if (!team) {
      return;
    }
    this.onEditTeam(team);
  }

  onEditMembersFromDetails(): void {
    const team = this.selectedTeam;
    if (!team) {
      return;
    }
    this.onEditTeam(team, true);
  }

  onAddEditBenefits(): void {
    const team = this.selectedTeam;
    const companyId = this.currentCompany?.id;
    if (!team?.id || !companyId) {
      return;
    }

    const data: EditBenefitsDialogData = {
      teamId: team.id,
      companyId,
      teamName: team.name,
      workspaceContext: this.isNativeCompanyManager ? 'nativeCompanyManager' : 'fullPayrollAdmin',
      company: this.isCompanyScopedWorkspace ? this.currentCompany : undefined,
      availableBenefitProductIds: this.isCompanyScopedWorkspace
        ? this.activeEmployerCompanyService.state.benefitProductIds
        : undefined,
    };

    const ref = this.dialog.open<EditBenefitsDialogComponent, EditBenefitsDialogData, EditBenefitsDialogResult>(
      EditBenefitsDialogComponent,
      {
        data,
        width: '1100px',
        maxWidth: '96vw',
        maxHeight: '90vh',
        disableClose: false,
        autoFocus: false,
        panelClass: 'edit-benefits-dialog-panel',
      }
    );

    ref.afterClosed().subscribe(async (result) => {
      if (!result?.saved) {
        return;
      }
      await this.loadPageData();
    });
  }

  async onEditSelectedTeamSnacks(): Promise<void> {
    const team = this.selectedTeam;
    if (!team?.id || !this.currentCompany?.id || !this.canShowNativeSnacksActions) {
      return;
    }

    const teamId = team.id;
    await this.refreshSelectedTeamSnackRewards();

    const data: EditTeamSnacksDialogData = {
      teamName: team.name || 'Selected team',
      rewards: this.selectedTeamSnackRewards,
      selectedSnackRewardIds: this.selectedTeamSnackSelectedIds,
    };

    const ref = this.dialog.open<EditTeamSnacksDialogComponent, EditTeamSnacksDialogData, EditTeamSnacksDialogResult>(
      EditTeamSnacksDialogComponent,
      {
        data,
        width: '1120px',
        maxWidth: '96vw',
        maxHeight: '90vh',
        disableClose: false,
        autoFocus: false,
        panelClass: 'edit-team-snacks-dialog-panel',
      }
    );

    ref.afterClosed().subscribe(async (result) => {
      if (!result) {
        return;
      }

      try {
        const persisted = await this.payrollSnacksAssignmentService.persistTeamSnackRewards(
          teamId,
          this.selectedTeam || team,
          result.snackRewardIds,
          this.selectedTeamSnackRewardIdsInOrder,
        );
        this.applySelectedTeamSnackMetadata(teamId, persisted.metadata);

        this.snackBar.openFromComponent(SnackBarComponent, {
          data: 'Snack availability updated.',
        });
        await this.refreshSelectedTeamSnackRewards(true);
      } catch (error: any) {
        const message = error?.data?.error || error?.message || 'Unable to update snack availability.';
        await this.uiAlertService.presentMessage(message);
      }
    });
  }

  async onTeamClick(team: Team): Promise<void> {
    const wasTeamDetailsOpen = this.selectedTabIndex === 1;
    if (!await this.onSelectedTeamChange(team?.id || '')) {
      return;
    }

    this.selectedTabIndex = 1;
    if (!wasTeamDetailsOpen) {
      await this.ensureSelectedTeamDetailsLoaded(true);
    }
  }

  // ----- Invites card (Dashboard) -----
  get invitesSentCount(): number {
    const members = (this.selectedTeam as any)?.teamMembers ?? [];
    return members.reduce((sum: number, m: any) => {
      const sent = m?.inviteSentAt ? 1 : 0;
      return sum + sent;
    }, 0);
  }

  get loggedInCount(): number {
    const members = (this.selectedTeam as any)?.teamMembers ?? [];
    return members.reduce((sum: number, m: any) => {
      const opened = m?.dayOfLogin ? 1 : 0;
      return sum + opened;
    }, 0);
  }

  get invitesNotInvitedCount(): number {
    const members = (this.selectedTeam as any)?.teamMembers ?? [];
    return members.reduce((sum: number, m: any) => {
      const notSentOrOpened = m?.inviteSentAt || m?.inviteAcceptedAt ? 0 : 1;
      return sum + notSentOrOpened;
    }, 0);
  }

  get invitesPendingCount(): number {
    const members = (this.selectedTeam as any)?.teamMembers ?? [];
    return members.reduce((sum: number, m: any) => {
      const pending = m?.inviteAcceptedAt ? 0 : 1;
      return sum + pending;
    }, 0);
  }

  get inviteAutomationDashboardSummary(): string {
    const automation = (this.selectedTeam as any)?.metadata?.inviteAutomation || {};
    if (automation?.enabled !== true) {
      return 'Automated invite sending is off.';
    }
    if (automation.initialInviteSendAt && !automation.initialInviteSentAt) {
      return `Initial send on ${this.formatInviteAutomationDate(automation.initialInviteSendAt)}.`;
    }
    if (automation.nextRetryAt) {
      return `Retry on ${this.formatInviteAutomationDate(automation.nextRetryAt)} for ${this.invitesPendingCount} pending signup.`;
    }
    return 'Automated invite sending is on.';
  }

  async onInvitesSent(): Promise<void> {
    const team = this.selectedTeam;
    if (!team?.id) {
      return;
    }

    try {
      const teamMembers = await this.teamsService.getTeamMembersWithDailyLoginInfo(team.id);
      team.teamMembers = teamMembers;
      team.membersCount = teamMembers?.length ?? 0;
    } catch {
      await this.loadPageData(false);
    }
  }

  async onSendEditInvites(): Promise<void> {
    const team: any = this.selectedTeam;
    if (!team?.id) {
      return;
    }
    if (this.hasPendingGroupProductOnboarding) {
      this.snackBar.openFromComponent(SnackBarComponent, {
        data: this.pendingGroupProductInviteBlockMessage,
      });
      return;
    }

    let availableInviteFilters: Array<[string, string[]]> = [];
    const censusEmployeeByEmail = new Map<string, any>();
    const censusEmployeeByName = new Map<string, any | null>();
    try {
      await this.ensureAllEmployeesLoaded();
      for (const employee of this.availableEmployees || []) {
        const email = this.normalizeEmail(
          employee?.['Primary Email'] ||
          employee?.PrimaryEmail ||
          employee?.primaryEmail ||
          employee?.email ||
          employee?.userEmail
        );
        if (email && !censusEmployeeByEmail.has(email)) {
          censusEmployeeByEmail.set(email, employee);
        }

        const first = employee?.['First Name'] ?? employee?.FirstName ?? employee?.firstName;
        const last = employee?.['Last Name'] ?? employee?.LastName ?? employee?.lastName;
        const nameKey = this.normalizeNameKey(first, last);
        if (nameKey) {
          if (!censusEmployeeByName.has(nameKey)) {
            censusEmployeeByName.set(nameKey, employee);
          } else {
            // Mark ambiguous first/last matches so we do not apply incorrect metadata.
            censusEmployeeByName.set(nameKey, null);
          }
        }
      }
    } catch {
      // Keep dialog functional even when census enrichment is unavailable.
    }

    availableInviteFilters = (this.availableFilters || [])
      .map((filter: any) => {
        const key = `${filter?.[0] || ''}`.trim();
        const values = Array.isArray(filter?.[1]) ? filter[1] : [];
        return [key, values] as [string, string[]];
      })
      .filter(([key]) => !!key);

    const invitees = (team.teamMembers ?? [])
      .map((member: any) => {
        const email = member?.email || member?.userEmail || member?.['Primary Email'] || '';
        const firstName = member?.firstName || member?.['First Name'] || '';
        const lastName = member?.lastName || member?.['Last Name'] || '';
        const nameKey = this.normalizeNameKey(firstName, lastName);
        const censusEmployee =
          censusEmployeeByEmail.get(this.normalizeEmail(email)) ||
          (nameKey ? censusEmployeeByName.get(nameKey) : undefined) ||
          null;
        const filterValues: Record<string, string> = {};

        for (const [filterKey] of availableInviteFilters) {
          const value = this.resolveInviteFilterValue(filterKey, member, censusEmployee);
          if (value) {
            filterValues[filterKey] = value;
          }
        }

        const department =
          this.getValueFromFilterMapByNormalizedKey(filterValues, 'department') ||
          this.resolveInviteFilterValue('Department', member, censusEmployee) ||
          '—';
        const location =
          this.getValueFromFilterMapByNormalizedKey(filterValues, 'location') ||
          this.resolveInviteFilterValue('Location', member, censusEmployee) ||
          '—';
        const division = this.getValueFromFilterMapByNormalizedKey(filterValues, 'division') || '—';
        const state = this.getValueFromFilterMapByNormalizedKey(filterValues, 'state') || '—';
        const payType = this.getValueFromFilterMapByNormalizedKey(filterValues, 'paytype') || '—';
        const employmentType =
          this.getValueFromFilterMapByNormalizedKey(filterValues, 'employmenttype') ||
          this.getValueFromFilterMapByNormalizedKey(filterValues, 'employeetype') ||
          '—';

        return {
          memberId: member?.memberId || member?.id || '',
          firstName,
          lastName,
          department,
          location,
          division,
          state,
          payType,
          employmentType,
          filterValues,
          email,
          inviteSentAt: member?.inviteSentAt ?? null,
          inviteAcceptedAt: member?.inviteAcceptedAt ?? null,
          dayOfLogin: member?.dayOfLogin ?? null,
        };
      })
      .filter((invitee: any) => !!invitee.memberId && !!invitee.email);

    const data: SendInvitesDialogData = {
      teamId: team.id,
      teamName: team.name,
      invitees,
      availableFilters: availableInviteFilters,
    };

    const ref = this.dialog.open<
      SendInvitesDialogComponent,
      SendInvitesDialogData,
      SendInvitesDialogResult
    >(SendInvitesDialogComponent, {
      data,
      width: '1200px',
      maxWidth: '96vw',
      maxHeight: '90vh',
      disableClose: true,
      autoFocus: false,
      panelClass: 'send-invites-dialog-panel',
    });

    ref.afterClosed().subscribe((result) => {
      if (result?.invitesUpdated) {
        void this.refreshSavedTeamState(team.id);
      }
    });
  }

  private formatInviteAutomationDate(value: string | Date): string {
    if (!value) {
      return '';
    }
    return new Date(value).toLocaleDateString();
  }

  private maybeOpenCreateTeamFlowFromQuery(): void {
    if (this.handledOpenCreateTeam) {
      return;
    }

    const shouldOpen = this.route.snapshot.queryParamMap.get('openCreateTeam') === '1';
    if (!shouldOpen) {
      return;
    }

    const returnFlow = this.route.snapshot.queryParamMap.get('returnFlow');
    this.handledOpenCreateTeam = true;
    this.onCreateTeam(!this.isCompanyScopedWorkspace && returnFlow === 'connections-bank');

    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { openCreateTeam: null, returnFlow: null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  get hasAllEmployeesConnectionError(): boolean {
    return !!this.allEmployeesConnectionError;
  }

  onGoToCompanyConnections(): void {
    if (this.isCompanyScopedWorkspace) {
      return;
    }
    void this.router.navigate(['payrolladmin', 'company', 'connections']);
  }

  onGoToCompanyTab(): void {
    if (this.isCompanyScopedWorkspace) {
      return;
    }
    void this.router.navigate(['payrolladmin', 'company', 'company']);
  }

  private clearAllEmployeesConnectionError(): void {
    this.allEmployeesConnectionError = '';
    this.allEmployeesCensusErrorCode = '';
  }

  private setAllEmployeesConnectionError(error: any): void {
    const errorCode = this.extractPayrollErrorCode(error);
    const isConnectionError = this.isPayrollAuthenticationOrConfigurationError(error, errorCode);
    if (isConnectionError) {
      this.payrollConnectionHealth = 'error';
      this.allEmployeesCensusErrorCode = '';
    } else {
      this.allEmployeesCensusErrorCode = errorCode || 'PAYROLL_CENSUS_UNAVAILABLE';
    }
    this.allEmployeesLoaded = false;
    this.allEmployeesLoadedContextKey = '';
    this.allEmployeesLoadMode = '';
    this.allEmployeesConnectionError = isConnectionError
      ? this.buildPayrollConnectionErrorMessage(error)
      : this.buildPayrollCensusErrorMessage(this.allEmployeesCensusErrorCode);
  }

  private buildPayrollCensusErrorMessage(errorCode: string): string {
    const providerName = this.getPayrollProviderDisplayName('payroll provider');
    if ([
      'PAYCOR_CENSUS_CAPACITY_EXCEEDED',
      'PAYCOR_PAGINATION_CAPACITY_EXCEEDED',
      'PAYCOR_PAGINATION_LIMIT_EXCEEDED',
    ].includes(errorCode)) {
      return `The ${providerName} connection remains active, but this company exceeds the synchronous census capacity. No partial employee list was shown. A shared background workforce sync is required for this population size.`;
    }
    if (errorCode === 'PAYCOR_ENDPOINT_UNSUPPORTED') {
      return `The ${providerName} connection remains active, but its primary employee endpoint cannot provide the authoritative census required here. No identifying-data-only population was shown.`;
    }
    return `The ${providerName} connection remains active, but census enumeration did not finish. No partial employee list was shown. Try loading the census again.`;
  }

  private isPayrollAuthenticationOrConfigurationError(error: any, errorCode: string): boolean {
    const status = error?.status ?? error?.originalError?.status;
    const normalizedCode = `${errorCode || ''}`.toUpperCase();
    const normalizedMessage = `${this.extractPayrollErrorMessage(error) || error?.message || ''}`
      .trim()
      .toLowerCase();
    if (status === 401 || status === 403) {
      return true;
    }
    if (
      normalizedCode.includes('AUTHENTICATION')
      || normalizedCode.includes('CREDENTIAL')
      || normalizedCode.includes('CONFIGURATION')
      || normalizedCode === 'PAYCOR_LEGAL_ENTITY_ID_REQUIRED'
    ) {
      return true;
    }
    if (
      normalizedMessage.includes('authenticate with ukg')
      || normalizedMessage.includes('unable to fetch data from ukg')
      || normalizedMessage.includes('current configuration')
    ) {
      return true;
    }
    return status === 422 && !this.isPaycorProviderCensus;
  }

  private extractPayrollErrorCode(error: any): string {
    const candidates = [
      error?.data?.code,
      error?.error?.code,
      error?.originalError?.data?.code,
      error?.originalError?.error?.code,
      error?.code,
    ];
    for (const candidate of candidates) {
      const normalizedCode = `${candidate || ''}`.trim().toUpperCase();
      if (normalizedCode) {
        return normalizedCode;
      }
    }
    return '';
  }

  private buildPayrollConnectionErrorMessage(error: any): string {
    const status = error?.status ?? error?.originalError?.status;
    const apiErrorMessage = this.extractPayrollErrorMessage(error);
    const rawMessage = `${apiErrorMessage || error?.message || ''}`.trim();
    const normalizedMessage = rawMessage.toLowerCase();

    if (
      status === 401 ||
      status === 403 ||
      status === 422 ||
      normalizedMessage.includes('authenticate with ukg') ||
      normalizedMessage.includes('unable to fetch data from ukg') ||
      normalizedMessage.includes('current configuration')
    ) {
      const providerName = normalizedMessage.includes('ukg')
        ? 'UKG'
        : this.getPayrollProviderDisplayName('payroll provider');
      return this.isCompanyScopedWorkspace
        ? `The ${providerName} payroll connection is unavailable. Company and team management remain available, but census import and filter-based tools are disabled.`
        : `Payroll connection is in an Error state. Go to Company Connections to update your ${providerName} credentials and try again.`;
    }

    if (status === 502 || normalizedMessage.includes('unable to reach ukg')) {
      const providerName = normalizedMessage.includes('ukg')
        ? 'UKG'
        : this.getPayrollProviderDisplayName('payroll provider');
      return this.isCompanyScopedWorkspace
        ? `Unable to reach ${providerName} right now. Company and team management remain available, but census import and filter-based tools are disabled.`
        : `Unable to reach ${providerName} right now. Check Company Connections and try again.`;
    }

    return this.isCompanyScopedWorkspace
      ? `Unable to load employee data from ${this.getPayrollProviderDisplayName('payroll provider')}. Company and team management remain available.`
      : `Unable to load employee data from ${this.getPayrollProviderDisplayName('payroll provider')}. Check Company Connections and try again.`;
  }

  private extractPayrollErrorMessage(error: any): string {
    const candidates = [
      error?.data?.error,
      error?.data?.message,
      error?.error?.error,
      error?.error?.message,
      error?.message,
    ];

    for (const candidate of candidates) {
      const parsed = this.parsePayrollErrorMessage(candidate);
      if (parsed) {
        return parsed;
      }
    }

    return '';
  }

  private parsePayrollErrorMessage(value: any): string {
    const rawValue = `${value ?? ''}`.trim();
    if (!rawValue) {
      return '';
    }

    if (!rawValue.startsWith('{') && !rawValue.startsWith('[')) {
      return rawValue;
    }

    try {
      const parsed = JSON.parse(rawValue);
      return `${parsed?.error || parsed?.message || rawValue}`.trim();
    } catch (_) {
      return rawValue;
    }
  }

  private async resolvePayrollConnectionHealth(): Promise<PayrollConnectionHealth> {
    if (!this.canUseLegacyCensus) {
      this.payrollProviderConnection = null;
      this.payrollConnectionUnsupportedReason = '';
      return this.workforce.mode === 'snapshot' ? 'healthy' : 'error';
    }
    this.payrollProviderConnection = this.currentCompany?.id
      ? await this.payrollService.getCompanyPayrollProviderConnection(this.currentCompany.id).catch(() => null)
      : null;
    this.payrollConnectionUnsupportedReason = '';

    const hasProviderConnection = this.hasProviderConnection(this.payrollProviderConnection);
    if (!hasProviderConnection) {
      return 'none';
    }

    this.payrollConnectionUnsupportedReason = this.payrollService.providerCapabilityUnsupportedReason(
      this.payrollProviderConnection,
      'supportsCensus',
      'payroll census',
    );
    if (this.payrollConnectionUnsupportedReason) {
      return 'error';
    }

    return 'healthy';
  }

  private hasProviderConnection(connection: PayrollProviderConnectionResponse | null | undefined): boolean {
    return this.payrollService.hasActivePayrollProviderConnection(connection);
  }

  private providerConnectionSupportsCapability(capability: keyof PayrollProviderCapabilities): boolean {
    if (!this.hasProviderConnection(this.payrollProviderConnection)) {
      return false;
    }

    const capabilities = this.payrollProviderConnection?.capabilities
      || this.payrollProviderConnection?.provider?.capabilities
      || {};
    return capabilities[capability] === true;
  }

  private getPayrollProviderSlug(
    providerConnection: PayrollProviderConnectionResponse | null | undefined = this.payrollProviderConnection,
  ): string {
    return `${providerConnection?.providerSlug || providerConnection?.connection?.providerSlug || ''}`
      .trim()
      .toLowerCase();
  }

  private getPayrollProviderDisplayName(fallback = 'Payroll provider'): string {
    const providerName = `${this.payrollProviderConnection?.provider?.name || ''}`.trim();
    if (providerName) {
      return providerName;
    }

    const displayName = `${this.payrollProviderConnection?.connection?.displayName || ''}`.trim();
    if (displayName) {
      return displayName;
    }

    const providerSlug = this.getPayrollProviderSlug();
    return PAYROLL_PROVIDER_DISPLAY_NAMES[providerSlug] || fallback;
  }

  private getTeamSourceMetadata(team: any): Record<string, any> {
    if (!team?.id) {
      return {};
    }
    const teamMapMetadata = this.teamsMap?.[team.id] || {};
    const teamMetadata = team?.metadata || {};
    const mergedMetadata = {
      ...teamMetadata,
      ...teamMapMetadata,
    };
    const hasSavedCriteria = Array.isArray(mergedMetadata?.criteriaSnapshot?.criteria)
      && mergedMetadata.criteriaSnapshot.criteria.length > 0;

    if (hasSavedCriteria) {
      return {
        ...mergedMetadata,
        membershipManagedBy: 'wallit',
        payrollProviderSlug: mergedMetadata.payrollProviderSlug || teamMetadata.payrollProviderSlug,
        payrollTeamSource: 'provider_census_attribute_snapshot',
      };
    }

    return mergedMetadata;
  }

  private isWallitManagedProviderCensusTeam(team: any): boolean {
    const metadata = this.getTeamSourceMetadata(team);
    return metadata.membershipManagedBy === 'wallit'
      && [
        'provider_census_selection',
        'provider_census_attribute_snapshot',
      ].includes(metadata.payrollTeamSource);
  }

  private isWallitManagedProviderCensusSnapshotTeam(team: any): boolean {
    const metadata = this.getTeamSourceMetadata(team);
    return metadata.membershipManagedBy === 'wallit'
      && metadata.payrollTeamSource === 'provider_census_attribute_snapshot';
  }

  private getTeamSourceProviderSlug(team: any): string {
    const metadata = this.getTeamSourceMetadata(team);
    return `${metadata.payrollProviderSlug || ''}`.trim().toLowerCase();
  }

  private getAllEmployeesContextKey(): string {
    const providerConnection = this.payrollProviderConnection;
    const capabilities = providerConnection?.capabilities
      || providerConnection?.provider?.capabilities
      || {};
    return JSON.stringify({
      companyId: `${this.currentCompany?.id || ''}`.trim(),
      connectionExternalId: `${providerConnection?.connection?.externalConnectionId || ''}`.trim(),
      connectionId: `${providerConnection?.connection?.id || ''}`.trim(),
      health: this.payrollConnectionHealth,
      providerSlug: this.getPayrollProviderSlug(providerConnection),
      supportsCensus: capabilities.supportsCensus === true,
    });
  }

  private activateAllEmployeesContext(contextKey: string): void {
    if (this.allEmployeesContextKey === contextKey) {
      return;
    }
    this.invalidateAllEmployeesCensusCache();
    this.allEmployeesContextKey = contextKey;
  }

  private captureAllEmployeesLoadRequest(contextKey: string): AllEmployeesLoadRequest {
    const providerConnection = this.payrollProviderConnection
      ? {
        ...this.payrollProviderConnection,
        capabilities: {...(this.payrollProviderConnection.capabilities || {})},
        connection: this.payrollProviderConnection.connection
          ? {...this.payrollProviderConnection.connection}
          : undefined,
        provider: this.payrollProviderConnection.provider
          ? {
            ...this.payrollProviderConnection.provider,
            capabilities: {...(this.payrollProviderConnection.provider.capabilities || {})},
          }
          : undefined,
      }
      : null;
    return {
      canUseProviderCensus: this.canUseProviderCensus,
      company: {...this.currentCompany},
      contextKey,
      generation: ++this.allEmployeesLoadGeneration,
      providerConnection,
      teamsMap: {...(this.teamsMap || {})},
    };
  }

  private isCurrentAllEmployeesLoad(request: AllEmployeesLoadRequest): boolean {
    return !this.isDestroyed
      && request.contextKey === this.allEmployeesContextKey
      && request.generation === this.allEmployeesLoadGeneration;
  }

  private invalidateAllEmployeesCensusCache(): void {
    this.allEmployeesLoadGeneration++;
    this.allEmployeesContextKey = '';
    this.allEmployeesLoaded = false;
    this.allEmployeesLoadedContextKey = '';
    this.allEmployeesLoadMode = '';
    this.allEmployeesLoadContextKey = '';
    this.allEmployeesLoadPromise = undefined;
    this.allEmployeesRefreshError = '';
    this.isRefreshingAllEmployeesFromUkg = false;
    this.census.invalidate();
    this.censusEmployeesIndexSource = undefined;
    this.censusEmployeeByEmail.clear();
    this.censusEmployeeByName.clear();
    this.filteredEmployeesCacheSource = undefined;
    this.filteredEmployeesCacheKey = '';
    this.filteredEmployeesCache = [];
  }

  private updateCompanyState(companies: any[]): void {
    const previousCompanyId = this.currentCompany?.id || '';
    this.hasCompany = companies.length > 0;
    this.currentCompany = this.hasCompany ? companies[0] : undefined;
    this.payrollProviderConnection = null;
    this.payrollConnectionUnsupportedReason = '';
    this.payrollConnectionHealth = 'none';

    const nextCompanyId = this.currentCompany?.id || '';
    if (nextCompanyId !== previousCompanyId) {
      ++this.settingsCompanyVersion;
      this.acceptedTeamSettings.clear();
      this.resetTerminationReviewState();
      this.invalidateAllEmployeesCensusCache();
      this.census.activeFilters = [];
      this.census.activeFiltersMap = {};
      this.clearAllEmployeesConnectionError();
      this.teamBonusAmounts = {};
      this.teamRewardPaymentMethods = {};
      this.rewardsActivityRequestId += 1;
      this.automatedBonusHistory = [];
      this.oneTimeRewardsHistory = [];
      this.rewardsActivityError = '';
      this.isRewardsActivityLoading = false;
      this.resetSelectedTeamSnackRewards();
    }
  }

  private subscribeToActiveEmployerCompany(): void {
    if (!this.isCompanyScopedWorkspace || this.activeEmployerCompanySubscription) {
      return;
    }

    this.activeEmployerCompanySubscription = this.activeEmployerCompanyService.stateChanges().subscribe((state) => {
      this.isCompanySnackRewardSelectionEnabled = state.hasSnacks;
      const nextCompanyId = `${state.activeCompany?.id || ''}`.trim();
      if (
        !state.initialized
        || state.loading
        || !nextCompanyId
        || state.singleCompanyWorkspaceStatus !== 'active'
      ) {
        this.workforce.destroy(); this.teamCreation.destroy();
        this.invalidateAllEmployeesCensusCache();
        this.resetTerminationReviewState();
        this.terminationResetForUnavailableCompanyState = true;
        return;
      }
      if (nextCompanyId === this.loadedCompanyId) {
        const shouldReloadTerminationReview = this.terminationResetForUnavailableCompanyState;
        this.terminationResetForUnavailableCompanyState = false;
        if (shouldReloadTerminationReview) {
          void this.workforce.open(nextCompanyId).then(async () => {
            if (this.workforce.context?.mode === 'snapshot') { await this.teamCreation.open(nextCompanyId); }
            if (this.isDestroyed || this.currentCompany?.id !== nextCompanyId) {
              return;
            }
            await this.workforce.setVisible(this.selectedTabIndex === 2);
            await this.loadTerminationReviewForCurrentCompany();
          });
        }
        return;
      }

      this.resetTerminationReviewState();
      this.terminationResetForUnavailableCompanyState = false;
      if (this.isActiveCompanyReloadPending) {
        return;
      }
      this.isActiveCompanyReloadPending = true;
      void this.loadPageData().finally(() => {
        this.isActiveCompanyReloadPending = false;
      });
    });
  }

  private toActiveCompanyList(state: { activeCompany?: any }): any[] {
    return state?.activeCompany ? [state.activeCompany] : [];
  }

  private async applyInitialNativeTeamSelection(): Promise<void> {
    const requestedTeamId = `${this.route.snapshot.queryParamMap.get('teamId') || ''}`.trim();
    const requestedView = `${this.route.snapshot.queryParamMap.get('view') || ''}`.trim();
    const requestedTeam = requestedTeamId
      ? this.teams.find((team) => `${team?.id || ''}` === requestedTeamId)
      : undefined;
    const globalTeamId = `${this.teamsService.getSelectedWellspaceCandidateId() || ''}`.trim();
    const globalTeam = globalTeamId
      ? this.teams.find((team) => `${team?.id || ''}` === globalTeamId)
      : undefined;
    const requestedTeamSelectedGlobally = requestedTeam
      ? await this.selectNativeGlobalTeam(requestedTeam.id)
      : false;

    this.selectedTeamId = (requestedTeamSelectedGlobally ? requestedTeam?.id : undefined)
      ?? globalTeam?.id
      ?? this.teams[0]?.id
      ?? '';

    const shouldOpenRequestedTeamDetails =
      requestedTeamSelectedGlobally
      && requestedView === 'details';
    if (shouldOpenRequestedTeamDetails) {
      this.selectedTabIndex = 1;
      return;
    }

    this.selectedTabIndex = 0;
    if (
      (requestedTeamId && !requestedTeamSelectedGlobally)
      || (requestedView === 'details' && !requestedTeamId)
    ) {
      void this.router.navigate([], {
        relativeTo: this.route,
        queryParams: {
          teamId: null,
          view: null,
        },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
    }
  }

  private getAuthorizedTeamId(teamId: string | undefined): string {
    const normalizedTeamId = `${teamId || ''}`.trim();
    if (!normalizedTeamId) {
      return '';
    }
    const authorizedTeam = this.teams.find(
      (team) => `${team?.id || ''}` === normalizedTeamId,
    );
    return `${authorizedTeam?.id || ''}`;
  }

  private async selectNativeGlobalTeam(teamId: string): Promise<boolean> {
    try {
      const selectedTeam = await this.nativeTeamContextService.selectTeam(teamId);
      return `${selectedTeam?.id || ''}` === teamId;
    } catch (error) {
      console.error('Unable to select native Team', { teamId, error });
      return false;
    }
  }

  get selectedTeamCanUseManualMemberTools(): boolean {
    if (!this.canUseLegacyCensus) return false;
    return !!this.selectedTeam?.id && this.isManualTeamFlow;
  }

  get selectedTeamCanUseIndividualMemberRemoval(): boolean {
    if (!this.canUseLegacyCensus) return false;
    return !!this.selectedTeam?.id && (
      this.selectedTeamCanUseManualMemberTools
      || this.getPayrollProviderSlug() === UKG_READY_PROVIDER_SLUG
    );
  }

  private getSelectedTeamMemberId(member: any): string {
    return member?.memberId || member?.id || '';
  }

  isSelectedTeamOwnerMember(member: any): boolean {
    return !!member?.userId && member.userId === (this.selectedTeam as any)?.ownerUserId;
  }

  selectedTeamMemberRemovalLabel(member: any): string {
    return this.isMemberDeactivationPendingSettlement(member) ? 'Finalize Removal' : 'Remove';
  }

  memberEmail(member: any): string {
    return member?.email || member?.userEmail || member?.['Primary Email'] || '';
  }

  canSendOneTimeRewardToSelectedTeamMember(member: any): boolean {
    const team: any = this.selectedTeam;
    if (!team?.id || !this.memberEmail(member) || !this.getSelectedTeamMemberId(member)) {
      return false;
    }
    if (this.isMemberDeactivationPendingSettlement(member) || member?.disabled) {
      return false;
    }

    const isTeamApproved = this.isTeamApprovedForRewards(team);
    const hasValidPaymentMethod = this.hasValidSelectedTeamRewardPaymentMethod();
    const hasLinkedUser = !!member?.userId;
    const canUseWallitBalance = hasLinkedUser && member.userId !== team.ownerUserId;

    return isTeamApproved && (canUseWallitBalance || hasValidPaymentMethod);
  }

  async onSendOneTimeRewardsToSelectedTeam(): Promise<void> {
    const team: any = this.selectedTeam;
    if (!team?.id || !this.canSendOneTimeRewardsToSelectedTeam) {
      return;
    }

    const rewardResult = await this.uiAlertService.teamOneTimeReward(false, undefined, team);

    if (rewardResult) {
      await this.refreshSelectedTeamAfterRewardAction(team.id);
    }
  }

  async onUploadRewardsToSelectedTeam(): Promise<void> {
    const team: any = this.selectedTeam;
    if (!team?.id || !this.canUploadRewardsToSelectedTeam) {
      return;
    }

    await this.uiAlertService.uploadRewards(team.id);
    await this.refreshSelectedTeamAfterRewardAction(team.id);
  }

  async onTopUpSelectedTeamP2p(): Promise<void> {
    const team: any = this.selectedTeam;
    if (!team?.id || !this.canTopUpSelectedTeamP2p) {
      return;
    }

    const rewardResult = await this.uiAlertService.teamOneTimeReward(true, undefined, team);

    if (rewardResult) {
      await this.refreshSelectedTeamAfterRewardAction(team.id);
    }
  }

  async onSendOneTimeRewardToSelectedTeamMember(member: any): Promise<void> {
    const team: any = this.selectedTeam;
    if (!team?.id || !this.canSendOneTimeRewardToSelectedTeamMember(member)) {
      return;
    }

    const rewardResult = await this.uiAlertService.memberOneTimeReward(
      {
        ...member,
        email: this.memberEmail(member),
      },
      false,
      team,
    );

    if (rewardResult) {
      await this.refreshSelectedTeamRewardsActivity();
    }
  }

  private isTeamApprovedForRewards(team: any): boolean {
    return team?.approved === undefined || team?.approved === null ? true : !!team.approved;
  }

  private hasValidSelectedTeamRewardPaymentMethod(): boolean {
    const team: any = this.selectedTeam;
    if (!team?.id) {
      return false;
    }
    const paymentMethod = this.normalizeSelectedTeamFundingMethod(
      this.teamRewardPaymentMethods[team.id],
      team.defaultPaymentMethod,
    );
    return !!paymentMethod && !paymentMethod?.bankInfo?.isPending;
  }

  private getSelectedTeamRewardBaseDisabledReason(): string {
    const team: any = this.selectedTeam;
    if (!team?.id) {
      return 'Select a team first.';
    }
    if (!this.isTeamApprovedForRewards(team)) {
      return 'This team is not approved yet.';
    }
    // team.disabled pauses reimbursements only, not rewards.
    return '';
  }

  private async refreshSelectedTeamAfterRewardAction(teamId: string): Promise<void> {
    await Promise.all([
      this.refreshSelectedTeamRewardsActivity(),
      this.reloadSelectedTeamMembers(teamId),
    ]);
  }

  async onRemoveSelectedTeamMember(member: any): Promise<void> {
    const team = this.selectedTeam as any;
    const memberId = this.getSelectedTeamMemberId(member);
    if (
      !this.selectedTeamCanUseIndividualMemberRemoval
      || !team?.id
      || !memberId
      || this.isSelectedTeamOwnerMember(member)
    ) {
      return;
    }

    const shouldDisableAutoAdd = !this.isMemberDeactivationPendingSettlement(member)
      && await this.shouldDisableAutoAddForMemberRemoval(team, member);
    const result = await this.teamMemberRemovalCoordinator.removeMember({
      team,
      member: {
        ...member,
        memberId,
        teamId: team.id,
      },
      ...(shouldDisableAutoAdd
        ? {
          confirmationWarning: AUTO_ADD_REMOVAL_WARNING,
          beforeMutation: async () => this.disableAutoAddForMemberRemoval(team),
        }
        : {}),
    });

    if (result.status === 'succeeded') {
      const shouldRefreshGuidedReadiness = member?.userId === this.guidedTerminationUserId
        && this.guidedTerminationTeamIds.has(team.id);
      try {
        await this.reloadSelectedTeamMembers(team.id);
      } catch (_) {
        this.snackBar.openFromComponent(SnackBarComponent, {
          data: 'Member removal completed, but the team list could not be refreshed.',
        });
      }
      if (shouldRefreshGuidedReadiness) {
        await this.refreshGuidedTerminationReadiness();
      }
    } else if (result.preparationApplied) {
      this.snackBar.openFromComponent(SnackBarComponent, {
        data: result.status === 'cancelled'
          ? AUTO_ADD_REMOVAL_CANCELLED_MESSAGE
          : AUTO_ADD_REMOVAL_FAILED_MESSAGE,
      });
    }
  }

  async onResendSelectedTeamMemberInvite(member: any): Promise<void> {
    const team = this.selectedTeam as any;
    const memberId = this.getSelectedTeamMemberId(member);
    if (!team?.id || !memberId) {
      return;
    }

    await this.teamsService.resendEmailToTeamMember(memberId, team.id);
    await this.reloadSelectedTeamMembers(team.id);
  }

  private openEmployeeDetailsDialog(data: EmployeeDetailsDialogData) {
    return this.dialog.open<EmployeeDetailsDialogComponent, EmployeeDetailsDialogData, EmployeeDetailsDialogResult>(
      EmployeeDetailsDialogComponent,
      {
        data,
        width: '820px',
        maxWidth: '95vw',
        autoFocus: false,
        panelClass: 'employee-details-dialog-panel',
      }
    );
  }

  private cloneEmployeeDetails(employee: any): any {
    return {
      ...employee,
      metadata: employee?.metadata && typeof employee.metadata === 'object'
        ? { ...employee.metadata }
        : employee?.metadata,
      teamuserMetadata: employee?.teamuserMetadata && typeof employee.teamuserMetadata === 'object'
        ? { ...employee.teamuserMetadata }
        : employee?.teamuserMetadata,
    };
  }

  private updateSelectedTeamMemberInPlace(teamId: string, updatedEmployee: any): void {
    const team = this.teams.find((candidate) => candidate.id === teamId);
    const members = Array.isArray(team?.teamMembers) ? team.teamMembers : [];
    const updatedMemberId = this.getSelectedTeamMemberId(updatedEmployee);
    const updatedEmail = this.memberEmail(updatedEmployee);
    const memberIndex = members.findIndex((member: any) => {
      const memberId = this.getSelectedTeamMemberId(member);
      return (updatedMemberId && memberId === updatedMemberId)
        || (!!updatedEmail && this.memberEmail(member) === updatedEmail);
    });

    if (memberIndex < 0) {
      return;
    }

    members[memberIndex] = {
      ...members[memberIndex],
      ...updatedEmployee,
      metadata: updatedEmployee?.metadata ?? members[memberIndex]?.metadata,
      teamuserMetadata: updatedEmployee?.teamuserMetadata ?? members[memberIndex]?.teamuserMetadata,
    };
  }

  private async reloadSelectedTeamMembers(teamId: string): Promise<void> {
    const team = this.teams.find((candidate) => candidate.id === teamId);
    if (!team) {
      return;
    }

    const teamMembers = await this.teamsService.getTeamMembersWithDailyLoginInfo(team.id);
    team.teamMembers = teamMembers;
    team.membersCount = teamMembers?.length ?? 0;
    await Promise.all([
      this.refreshSelectedTeamAutoAddEligibility(),
      this.refreshSelectedTeamFundingCandidates(),
    ]);
  }

  private getSelectedTeamBonusAmounts(): { welcome: number; birthday: number; anniversary: number } {
    const teamId = this.selectedTeam?.id;
    if (!teamId) {
      return this.emptyTeamBonusAmounts;
    }
    return this.teamBonusAmounts[teamId] || this.emptyTeamBonusAmounts;
  }

  private async refreshSelectedTeamBonusAmounts(): Promise<void> {
    const teamId = this.selectedTeam?.id;
    if (!teamId) {
      return;
    }

    try {
      const programs = await this.teamProgramsService.getPrograms(teamId);
      const normalizedPrograms = Array.isArray(programs) ? programs : [];
      this.teamBonusAmounts[teamId] = {
        welcome: this.extractBonusAmount(normalizedPrograms, 'welcome'),
        birthday: this.extractBonusAmount(normalizedPrograms, 'birthday'),
        anniversary: this.extractBonusAmount(normalizedPrograms, 'anniversary'),
      };
    } catch (error) {
      console.error('Unable to load team bonus settings', error);
      this.teamBonusAmounts[teamId] = { ...this.emptyTeamBonusAmounts };
    }
  }

  private async refreshSelectedTeamRewardPaymentMethod(): Promise<void> {
    const team: any = this.selectedTeam;
    if (!team?.id) {
      return;
    }

    try {
      const teamPaymentMethod = await this.teamsService.getPaymentMethod(team);
      const paymentMethods = await this.teamsService.getPaymentMethods(team.id);
      const paymentMethodId =
        typeof teamPaymentMethod === 'string'
          ? teamPaymentMethod
          : (teamPaymentMethod as any)?.stripePaymentMethod;

      this.teamRewardPaymentMethods[team.id] =
        paymentMethods.find((method: any) => method?.stripePaymentMethod === paymentMethodId) || null;
    } catch (error) {
      this.teamRewardPaymentMethods[team.id] = null;
    }
  }

  private async refreshSelectedTeamAttachableFundingMethod(): Promise<void> {
    const team = this.selectedTeam;
    if (!team?.id || this.selectedTeamFundingMethod) {
      this.selectedTeamAttachableFundingMethod = null;
      return;
    }
    const teamId = team.id;
    const requestVersion = this.selectedTeamDetailsRequestVersion;

    const sourceTeams = (this.teams || []).filter((candidate) => candidate?.id && candidate.id !== team.id);
    for (const sourceTeam of sourceTeams) {
      try {
        const sourceMethod = await this.resolvePrimaryFundingMethodForTeam(sourceTeam);
        if (!this.isCurrentSelectedTeamRequest(teamId, requestVersion)) {
          return;
        }
        if (!sourceMethod) {
          continue;
        }

        this.selectedTeamAttachableFundingMethod = {
          ...sourceMethod,
          sourceTeamId: sourceTeam.id,
          sourceTeamName: sourceTeam.name,
        };
        return;
      } catch {
        if (!this.isCurrentSelectedTeamRequest(teamId, requestVersion)) {
          return;
        }
        continue;
      }
    }

    if (this.isCurrentSelectedTeamRequest(teamId, requestVersion)) {
      this.selectedTeamAttachableFundingMethod = null;
    }
  }

  private async refreshSelectedTeamRewardsActivity(): Promise<void> {
    const teamId = this.selectedTeam?.id;
    const requestId = ++this.rewardsActivityRequestId;

    if (!teamId) {
      this.automatedBonusHistory = [];
      this.oneTimeRewardsHistory = [];
      this.rewardsActivityError = '';
      this.isRewardsActivityLoading = false;
      return;
    }

    this.isRewardsActivityLoading = true;
    this.rewardsActivityError = '';

    const [automatedBonusHistoryResult, oneTimeRewardsResult] = await Promise.all([
      this.teamProgramsService
        .getProgramLogs(teamId)
        .then((value) => ({ ok: true as const, value }))
        .catch((reason) => ({ ok: false as const, reason })),
      this.transactionsService
        .getTeamPeerToPeerTransactions(teamId, 'OnetimeReward')
        .then((value) => ({ ok: true as const, value }))
        .catch((reason) => ({ ok: false as const, reason })),
    ]);

    if (requestId !== this.rewardsActivityRequestId || teamId !== this.selectedTeam?.id) {
      return;
    }

    const failedLoads: string[] = [];

    if (automatedBonusHistoryResult.ok) {
      this.automatedBonusHistory = this.buildAutomatedBonusHistory(automatedBonusHistoryResult.value);
    } else {
      console.error('Unable to load automated bonus history', automatedBonusHistoryResult.reason);
      this.automatedBonusHistory = [];
      failedLoads.push('automated-bonuses');
    }

    if (oneTimeRewardsResult.ok) {
      this.oneTimeRewardsHistory = this.buildOneTimeRewardsHistory(oneTimeRewardsResult.value);
    } else {
      console.error('Unable to load one-time rewards history', oneTimeRewardsResult.reason);
      this.oneTimeRewardsHistory = [];
      failedLoads.push('one-time');
    }

    this.rewardsActivityError = failedLoads.length
      ? 'Some rewards activity could not be loaded right now.'
      : '';
    this.isRewardsActivityLoading = false;
  }

  private async refreshSelectedTeamFundingCandidates(): Promise<void> {
    const team = this.selectedTeam;
    if (!team?.id || Number(team.amount || 0) <= 0) {
      this.selectedTeamFundingCandidates = [];
      this.isFundingCandidatesLoading = false;
      return;
    }
    const teamId = team.id;
    const requestVersion = this.selectedTeamDetailsRequestVersion;

    this.selectedTeamFundingCandidates = [];
    this.isFundingCandidatesLoading = true;
    try {
      const members = await this.teamsService.getTeamMembersForAllowanceFunding(teamId);
      if (!this.isCurrentSelectedTeamRequest(teamId, requestVersion)) {
        return;
      }
      this.selectedTeamFundingCandidates = members
        .filter((member: any) => member?.monthlyAllowanceFunding?.candidate === true)
        .map((member: any) => `${member?.userEmail ?? ''}`.trim())
        .filter((email: string) => !!email);
    } catch (error) {
      if (!this.isCurrentSelectedTeamRequest(teamId, requestVersion)) {
        return;
      }
      this.selectedTeamFundingCandidates = [];
      this.snackBar.openFromComponent(SnackBarComponent, {
        data: 'Unable to load current member funding candidates.',
      });
    } finally {
      if (this.isCurrentSelectedTeamRequest(teamId, requestVersion)) {
        this.isFundingCandidatesLoading = false;
      }
    }
  }

  private extractBonusAmount(programs: any[], type: 'welcome' | 'birthday' | 'anniversary'): number {
    const program = programs.find((candidate: any) => candidate?.type === type);
    if (!program) {
      return 0;
    }
    return this.toValidAmount(program?.amount);
  }

  private buildAutomatedBonusHistory(historyItems: any[]): AutomatedBonusHistoryRow[] {
    return (Array.isArray(historyItems) ? historyItems : []).map((historyItem: any) => ({
      sentTo: `${historyItem?.firstName || ''} ${historyItem?.lastName || ''}`.trim() || '—',
      type: historyItem?.type || '—',
      amount: this.toValidAmount(historyItem?.amount),
      sentOn: historyItem?.createdAt || null,
    }));
  }

  private buildOneTimeRewardsHistory(rewards: any[]): RewardActivityHistoryRow[] {
    return (Array.isArray(rewards) ? rewards : []).map((reward: any) => ({
      sentOn: reward?.transactionDate || null,
      amount: this.toValidAmount(reward?.amount),
      message: reward?.description ?? '---',
      recipient: reward?.recipient ?? '---',
    }));
  }

  private toValidAmount(value: unknown): number {
    const amount = Number(value);
    if (!Number.isFinite(amount) || amount < 0) {
      return 0;
    }
    return Math.round(amount);
  }

  private normalizeSelectedTeamFundingMethod(paymentMethod: any, defaultPaymentMethod?: string): any | null {
    if (!paymentMethod || typeof paymentMethod !== 'object') {
      return null;
    }

    const recordId = `${paymentMethod?.id || ''}`.trim();
    const stripePaymentMethod = `${paymentMethod?.stripePaymentMethod || ''}`.trim();
    const expectedStripePaymentMethod = `${defaultPaymentMethod || ''}`.trim();
    const bankInfo = paymentMethod?.bankInfo && typeof paymentMethod.bankInfo === 'object'
      ? paymentMethod.bankInfo
      : {};
    const bankType = `${bankInfo?.type || ''}`.trim().toLowerCase();
    const bankName = `${bankInfo?.bankName || ''}`.trim();
    const last4 = `${paymentMethod?.stripeLast4 || ''}`.trim();
    const hasFundingDetails = !!bankType || !!bankName || !!last4 || !!paymentMethod?.microdepositInfo;

    if (!recordId || !stripePaymentMethod || (expectedStripePaymentMethod && stripePaymentMethod !== expectedStripePaymentMethod)) {
      return null;
    }
    if (!hasFundingDetails) {
      return null;
    }
    if (bankType === 'invoice' || bankName.toLowerCase() === 'invoice') {
      return null;
    }

    return paymentMethod;
  }

  private isTeamFundingProrated(team: Team | any): boolean {
    return !(team?.proRate === false || team?.proRate === 0 || team?.proRate === '0');
  }

  private buildFundAllowanceAlertMessage(
    team: Team | any,
    preview: PreviewMonthlyAllowanceFundingResponseData,
  ): string {
    const memberCount = preview.candidateCount;
    const invoiceType = team?.invoiceEmail ? 'invoice' : 'charge';
    const message: string[] = [];
    message.push(`${memberCount} new member${memberCount !== 1 ? 's' : ''}.<br><br>`);
    message.push(
      `This will initiate a monthly allowance recurring ${invoiceType} of ${this.formatCents(
        preview.recurringChargeTotalCents,
      )} to your default payment method on day ${preview.monthlyTopupDay} of each month, with the next full payment on ${new Date(preview.nextFundingDate).toLocaleDateString('en-US', { timeZone: 'UTC' })} (UTC).`,
    );
    if (preview.recurringServiceFeeCents > 0) {
      message.push(
        `This includes a monthly ${this.formatCents(preview.recurringServiceFeeCents)} service fee.`,
      );
    }
    if (this.isTeamFundingProrated(team) && preview.immediateFundingSubtotalCents > 0) {
      message.push(
        `Initial funding of ${this.formatCents(preview.immediateChargeTotalCents)} will be charged immediately. Prorated funding covers today through the day before the next scheduled top-up.`,
      );
      if (preview.immediateServiceFeeCents > 0) {
        message.push(
          `This includes an initial ${this.formatCents(preview.immediateServiceFeeCents)} service fee.`,
        );
      }
    }

    const { premiumMessage } = this.getFundingPremiumDetails(team, memberCount);
    if (premiumMessage) {
      message.push(premiumMessage.trim());
    }
    message.push('Any available credit balance will be applied first before charging the remaining amount.');

    return message.join(' ');
  }

  private getFundingPremiumDetails(team: Team | any, memberCount: number): { premiumAmount: number; premiumMessage: string } {
    switch (team?.plan) {
      case Plan.Plus:
        return this.currentCompanyIncludesPlusPlan
          ? { premiumAmount: 0, premiumMessage: '' }
          : {
            premiumAmount: this.stripeService.PREMIUM_MONTHLY_FEE * memberCount,
            premiumMessage: `A Plus Monthly plan recurring monthly charge of $${(
              this.stripeService.PREMIUM_MONTHLY_FEE * memberCount
            ).toFixed(2)} will also be initiated and pro-rated for the initial period.`,
          };
      case Plan.PlusAnnual:
        return {
          premiumAmount: this.stripeService.PREMIUM_ANNUAL_FEE * memberCount,
          premiumMessage: `A Plus Annual plan recurring annual charge of $${(
            this.stripeService.PREMIUM_ANNUAL_FEE * memberCount
          ).toFixed(2)} will also be initiated and pro-rated for the initial period.`,
        };
      default:
        return { premiumAmount: 0, premiumMessage: '' };
    }
  }

  private async promptForInitialFundingMode(
    team: Team | any,
  ): Promise<MonthlyAllowanceFundingMode | null> {
    if (!this.isTeamFundingProrated(team)) {
      return 'none';
    }

    const dialogRef = this.dialog.open(AllowanceFundingAmountDialogComponent, {
      width: '520px',
      data: {
        memberCount: this.selectedTeamFundingCandidates.length,
        monthlyAmount: Number(team?.amount || 0),
      },
    });
    const result = (await dialogRef.afterClosed().toPromise()) as
      | AllowanceFundingAmountDialogResult
      | null;

    return result?.fundingMode ?? null;
  }

  private isConflictError(error: any): boolean {
    return error?.status === 409
      || error?.statusCode === 409
      || error?.data?.status === 409
      || error?.data?.statusCode === 409;
  }

  private formatCents(cents: number): string {
    return `$${(Number(cents || 0) / 100).toFixed(2)}`;
  }

  private async handleMonthlyAllowanceFundingError(error: any, teamId: string): Promise<void> {
    if (this.isConflictError(error)) {
      this.snackBar.openFromComponent(SnackBarComponent, {
        data: 'The funding details changed. Reloading the team members so you can review a new quote.',
      });
      await this.onSelectedTeamChange(teamId);
      return;
    }

    const errorMessage = error?.data?.message
      || error?.data?.error
      || error?.message
      || 'Unable to fund member allowances.';
    this.snackBar.openFromComponent(SnackBarComponent, { data: errorMessage });
  }

  private async attachCompanyFundingMethodToSelectedTeam(): Promise<void> {
    const team = this.selectedTeam;
    const sourceMethod = this.selectedTeamAttachableFundingMethod;
    if (!team?.id || !sourceMethod?.id || !sourceMethod?.stripePaymentMethod) {
      this.openCompanyConnectionsForFundingMethod();
      return;
    }

    this.isFundingMethodActionLoading = true;
    try {
      const userId = this.usersService.getCurrentUserId();
      await this.teamsService.attachPaymentToTeamForCompany(
        team.id,
        userId,
        sourceMethod.id,
        { stripePaymentMethodId: sourceMethod.stripePaymentMethod },
      );
      await this.teamsService.modifyTeam(team.id, {
        defaultPaymentMethod: sourceMethod.stripePaymentMethod,
      });
      team.defaultPaymentMethod = sourceMethod.stripePaymentMethod;
      await this.refreshSelectedTeamRewardPaymentMethod();
      await this.refreshSelectedTeamAttachableFundingMethod();
      this.snackBar.openFromComponent(SnackBarComponent, {
        data: `Funding method attached from ${sourceMethod.sourceTeamName || 'another company team'}.`,
      });
    } catch (error: any) {
      const errorMessage = error?.data?.error || error?.message || 'Unable to attach the company funding method.';
      this.snackBar.openFromComponent(SnackBarComponent, { data: errorMessage });
    } finally {
      this.isFundingMethodActionLoading = false;
    }
  }

  private openCompanyConnectionsForFundingMethod(): void {
    if (this.isCompanyScopedWorkspace) {
      return;
    }
    void this.router.navigate(['payrolladmin', 'company', 'connections'], {
      queryParams: { openBankConnectionDialog: '1' },
    });
  }

  private async resolvePrimaryFundingMethodForTeam(team: Team | any): Promise<any | null> {
    const teamPaymentMethod = await this.teamsService.getPaymentMethod(team);
    const paymentMethods = await this.teamsService.getPaymentMethods(team.id);
    const paymentMethodId =
      typeof teamPaymentMethod === 'string'
        ? teamPaymentMethod
        : (teamPaymentMethod as any)?.stripePaymentMethod;

    if (!paymentMethodId) {
      return null;
    }

    const paymentMethod = (paymentMethods || []).find(
      (method: any) => method?.stripePaymentMethod === paymentMethodId,
    );
    const bankInfo = paymentMethod?.bankInfo || {};
    if (!paymentMethod?.id || !paymentMethod?.stripePaymentMethod || !paymentMethod?.stripeCustomerId) {
      return null;
    }
    if (bankInfo?.isPending || bankInfo?.userAcceptedPaymentMethod === false) {
      return null;
    }
    if (
      bankInfo?.type === 'invoice' ||
      `${bankInfo?.bankName || ''}`.trim().toLowerCase() === 'invoice'
    ) {
      return null;
    }

    return paymentMethod;
  }
}
